/**
 * Modern Navigation Tree Component
 * 
 * This component creates a modern, expandable/collapsible navigation sidebar
 * with the following features:
 * - Expandable/collapsible menu items
 * - Search functionality
 * - Modern UI design matching Pro Sidebar style
 * - Support for badges, icons, and chevrons
 * - Light/Dark theme support
 * - Responsive collapsed/expanded states
 * 
 * @author Madhuri.k
 * @date 04-11-2025
 */

import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { ProSidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";
import { app } from "@microsoft/teams-js";
// Added By Madhuri.K On 22-11-2025 - Import useSettings hook
import useSettings from "app/hooks/useSettings";

// Import custom icons for navigation items
import EDashboardIcon from "../../../assets/img/e-dashboard.svg";
import InitiativeDashboardIcon from "../../../assets/img/initiative-management-icn.svg";
import BusinessUserTrackingIcon from "../../../assets/img/program.svg";
import Project from "../../../assets/img/project.svg";
import InitiativeTracking from "../../../assets/img/initiative-tracking.svg";
import Reports from "../../../assets/img/reports.svg";
import Favorite from "../../../assets/img/favorite.svg";
import Configuration from "../../../assets/img/configuration.svg";
import Security from "../../../assets/img/reports.svg";
import INIConvertedIcon from "../../../assets/img/INI_Converted.png";
import INIComplectedIcon from "../../../assets/img/INI_Complected.png";
import INIWithdrawnIcon from "../../../assets/img/INI_Withdrawn.png";
import INIActionItemsIcon from "../../../assets/img/INI_Action_Items.png";
import INIWarehouseIcon from "../../../assets/img/INI_Warehouse.png";
import INIExternalAuditIcon from "../../../assets/img/INI_ExternalAudit.png";
import INIReallocationIcon from "../../../assets/img/INI_Reallocation.png";
import INIActivateSnoozeIcon from "../../../assets/img/INI_ActivateSnooze.png";
import INIWatchListIcon from "../../../assets/img/INI_WatchList.png";
import INIConversionIcon from "../../../assets/img/INI_Conversion.png";
import INIManComIcon from "../../../assets/img/INI_ManCom.png";
import INILinkingIcon from "../../../assets/img/INI_Linking.png";
import INIProgramIcon from "../../../assets/img/INI_Program.png";
import INIProjectIcon from "../../../assets/img/INI_Project.png";
import INIApprovalIcon from "../../../assets/img/INI_Approval.png";
// Updated By Madhuri.K On 18-11-2025 - Import report page images
import InitiativeReportIcon from "../../../assets/img/Initiative_Report.png";
import ActionItemsReportIcon from "../../../assets/img/Action_Items_Report.png";
import INIReportIcon from "../../../assets/img/INI_Report.png";
import ProgramTrackingReportIcon from "../../../assets/img/Program_Tracking_Report.png";
import DelayedINIReportIcon from "../../../assets/img/Delayed_INI_Report.png";
import DeliveryCalendarReportIcon from "../../../assets/img/Delivery_Calendar_Report.png";
import AgeingReportIcon from "../../../assets/img/Ageing_Report.png";
// Updated By Madhuri.K On 18-11-2025 - Import dashboard and configuration page images
import IniDashboardIcon from "../../../assets/img/Ini_Dashboard.png";
import InitiativeProgressIcon from "../../../assets/img/INI_Progress.png";
import CompanyInformationIcon from "../../../assets/img/Company_Information.png";
import RiskCategoriesIcon from "../../../assets/img/Risk_Categories.png";
import RiskMatrixDefinitionIcon from "../../../assets/img/Risk_Matrix_Definition.png";
// Updated By Madhuri.K On 18-11-2025 - Import additional configuration page images
import CountryIcon from "../../../assets/img/Country.png";
import DocumentsCategoryIcon from "../../../assets/img/Documents_Category.png";
import AuditTypeDefinitionIcon from "../../../assets/img/Audit_Type_Definition.png";
import SkillsIcon from "../../../assets/img/Skills.png";
import CurrencyIcon from "../../../assets/img/Currency.png";
import DepartmentMasterIcon from "../../../assets/img/Department_Master.png";
// Updated By Madhuri.K On 18-11-2025 - Import organization-related page images
import OrganizationStructureIcon from "../../../assets/img/Organization_Structure.png";
import DeliveryTeamIcon from "../../../assets/img/Delivery_Team.png";
import DeliveryUnitIcon from "../../../assets/img/Delivery_Unit.png";
import OrganizationUnitIcon from "../../../assets/img/Organization_Unit.png";
import BusinessGroupIcon from "../../../assets/img/Business_Group.png";
// Updated By Madhuri.K On 18-11-2025 - Import business masters-related page images
import ProcessInformationIcon from "../../../assets/img/Process_Information.png";
import BusinessValueIcon from "../../../assets/img/Business_Value.png";
import BusinessImpactIcon from "../../../assets/img/Business_Impact.png";
import BusinessFeasibilityIcon from "../../../assets/img/Business_Feasibility.png";
import BusinessStrategyIcon from "../../../assets/img/Business_Strategy.png";
// Updated By Madhuri.K On 18-11-2025 - Import checklist-related page images
import ChecklistMakerIcon from "../../../assets/img/Checklist_Maker.png";
import PrioritizationChecklistIcon from "../../../assets/img/Prioritization_Checklist.png";
import ChecklistItemIcon from "../../../assets/img/Checklist_Item.png";
import ChecklistSectionIcon from "../../../assets/img/Checklist_Section.png";
import ChecklistMakerInboxIcon from "../../../assets/img/ChecklistMaker_Inbox.png";
// Updated By Madhuri.K On 18-11-2025 - Import projects-related page images
import ProjectSizeUnitsIcon from "../../../assets/img/ProjectSize_Units.png";
import ProjectTypeIcon from "../../../assets/img/Project_Type.png";
import ProjectGroupIcon from "../../../assets/img/Project_Group.png";
import ProjectStatusIcon from "../../../assets/img/Project_Status.png";
// Updated By Madhuri.K On 18-11-2025 - Import security-related page images
import PasswordManagementIcon from "../../../assets/img/Password_Management.png";
import UnlockUserIcon from "../../../assets/img/Unlock_User.png";
import UserGroupsIcon from "../../../assets/img/User_Groups.png";
import RoleAccessIcon from "../../../assets/img/Role_Access.png";
import RoleMaintenanceIcon from "../../../assets/img/Role.png";
import UserLoginMaintenanceIcon from "../../../assets/img/User_Login_Maintenance.png";
import VendorLoginMaintenanceIcon from "../../../assets/img/Vendor_Login_Maintenance.png";
import UserMaintenanceIcon from "../../../assets/img/User_Maintenance.png";
// Updated By Madhuri.K On 18-11-2025 - Import initiative masters-related page images
import InitiativeCorporateFieldsIcon from "../../../assets/img/Initiative_Corporate_Fields.png";
import InitiativeRelationTypeMasterIcon from "../../../assets/img/InitiativeRelationType_Master.png";
import ActionPriorityIcon from "../../../assets/img/Action_Priority.png";
import ActionStatusIcon from "../../../assets/img/Action_Status.png";
import InitiativeCRESSRangesIcon from "../../../assets/img/Initiative_CRESS_Ranges.png";
import CostCategoryIcon from "../../../assets/img/Cost_Category.png";
import InitiativeAccessIcon from "../../../assets/img/Initiative_Access.png";
import ResourceApproachIcon from "../../../assets/img/Resource_Approach.png";
import PriorityIcon from "../../../assets/img/Priority.png";
import INIVendorIcon from "../../../assets/img/INI_Vendor.png";
import InitiativeCategoryIcon from "../../../assets/img/Initiative_Category.png";
import InitiativeStageIcon from "../../../assets/img/Initiative_Stage.png";
import NatureOfInitiativeIcon from "../../../assets/img/Nature_of_Initiative.png";
import InitiativeMastersIcon from "../../../assets/img/Initiative_Masters.png";
// Updated By Madhuri.K On 18-11-2025 - Import parent node images
import UINavigationNodeIcon from "../../../assets/img/UI&Navigation.png";
import SecurityNodeIcon from "../../../assets/img/Security.png";
import ProjectsNodeIcon from "../../../assets/img/Projects_Node.png";
import ChecklistNodeIcon from "../../../assets/img/Checklist_Node.png";
import RiskMatrixNodeIcon from "../../../assets/img/Risk_Matrix_Node.png";
import OrganizationNodeIcon from "../../../assets/img/Organization_Node.png";
import GeneralInformationNodeIcon from "../../../assets/img/General_Information.png";

// Material-UI icons for UI elements
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import FolderIcon from "@mui/icons-material/Folder";
import InventoryIcon from "@mui/icons-material/Inventory";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import AuditIcon from "@mui/icons-material/VerifiedUser";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SettingsIcon from "@mui/icons-material/Settings";
import TransformIcon from "@mui/icons-material/Transform";
import StarIcon from "@mui/icons-material/Star";
import LinkIcon from "@mui/icons-material/Link";
import GavelIcon from "@mui/icons-material/Gavel";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DescriptionIcon from "@mui/icons-material/Description";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WarningIcon from "@mui/icons-material/Warning";
import TimelineIcon from "@mui/icons-material/Timeline";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CategoryIcon from "@mui/icons-material/Category";
import SecurityIcon from "@mui/icons-material/Security";
import GroupIcon from "@mui/icons-material/Group";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import BuildIcon from "@mui/icons-material/Build";
import InfoIcon from "@mui/icons-material/Info";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import PublicIcon from "@mui/icons-material/Public";
import MoneyIcon from "@mui/icons-material/Money";
import FlagIcon from "@mui/icons-material/Flag";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TableChartIcon from "@mui/icons-material/TableChart";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InboxIcon from "@mui/icons-material/Inbox";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import BusinessIcon from "@mui/icons-material/Business";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import RefreshIcon from "@mui/icons-material/Refresh";

// Material-UI components for modern UI
import {
  Box,
  Typography,
  IconButton,
  InputBase,
  Paper,
  Chip,
  Tooltip,
  Divider
} from "@mui/material";
import "./SidebarComponent.css";

/**
 * Icon mappings for navigation items
 * Maps navigation item names to their corresponding icon files
 */
const iconMappings = {
  Dashboard: EDashboardIcon,
  "Initiative Management": InitiativeDashboardIcon,
  Program: BusinessUserTrackingIcon,
  Projects: Project,
  "Initiative Tracking": InitiativeTracking,
  Reports: Reports,
  Favorite: Favorite,
  Configuration: Configuration,
  Security: Security
};

/**
 * Get icon for Initiative Management child pages based on page name
 * Added By Madhuri.K On 04-11-2025 - Add icons for pages under Initiative Management module
 * 
 * @param {string} pageName - The page name (tagName or tagDescription)
 * @param {boolean} isActive - Whether the item is currently selected/active
 * @returns {JSX.Element|null} - Icon component or null
 */
const getInitiativeManagementPageIcon = (pageName, isActive = false) => {
  if (!pageName) return null;
  
  const pageNameLower = pageName.toLowerCase();
  
  // Updated By Madhuri.K - Use Initiative Management parent icon for "Initiative" page
  // Updated By Madhuri.K - Ensure icon size matches Material-UI icons (fontSize 16 = ~20px visual size)
  // Updated By Madhuri.K On 21-11-2025 - Apply orange color (#f97316) when inactive, white when active
  // Check if this is the "Initiative" page and return the parent icon (InitiativeDashboardIcon)
  if (pageNameLower === "initiative" || pageNameLower === "initiatives") {
    const initiativesColor = "#f97316"; // Orange
    
    return (
      <img 
        src={InitiativeDashboardIcon} 
        alt="Initiative Management" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply orange color (#f97316) using CSS filter
                // This filter converts the image to orange color
                filter: 'brightness(0) saturate(100%) invert(58%) sepia(96%) saturate(1352%) hue-rotate(5deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("InitiativeDashboardIcon icon failed to load. Path:", InitiativeDashboardIcon);
          // Fallback: show a colored box if image fails to load
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_Converted image for "Converted Initiatives" page
  // Updated By Madhuri.K - Apply emerald green color (#10b981) when inactive, white when active
  if (pageNameLower === "converted initiatives" || pageNameLower === "converted initiative") {
    const convertedColor = "#10b981"; // Emerald Green
    
    return (
      <img 
        src={INIConvertedIcon} 
        alt="Converted Initiatives" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply emerald green color (#10b981) using CSS filter
                // This filter converts the image to emerald green color
                filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(142deg) brightness(95%) contrast(86%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Converted icon failed to load. Path:", INIConvertedIcon);
          // Fallback: show a colored box if image fails to load
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_Complected image for "Completed Initiatives" page
  // Updated By Madhuri.K - Apply blue color (#3b82f6) when inactive, white when active
  if (pageNameLower === "completed initiatives" || pageNameLower === "completed initiative") {
    const completedColor = "#3b82f6"; // Blue
    
    return (
      <img 
        src={INIComplectedIcon} 
        alt="Completed Initiatives" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply blue color (#3b82f6) using CSS filter
                // This filter converts the image to blue color
                filter: 'brightness(0) saturate(100%) invert(39%) sepia(96%) saturate(1352%) hue-rotate(212deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Complected icon failed to load. Path:", INIComplectedIcon);
          // Fallback: show a colored box if image fails to load
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_Withdrawn image for "Withdrawn Initiatives" page
  // Updated By Madhuri.K - Apply red color (#ef4444) when inactive, white when active
  if (pageNameLower === "withdrawn initiatives" || pageNameLower === "withdrawn initiative") {
    const withdrawnColor = "#ef4444"; // Red
    
    return (
      <img 
        src={INIWithdrawnIcon} 
        alt="Withdrawn Initiatives" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply red color (#ef4444) using CSS filter
                // This filter converts the image to red color
                filter: 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(7471%) hue-rotate(347deg) brightness(94%) contrast(92%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Withdrawn icon failed to load. Path:", INIWithdrawnIcon);
          // Fallback: show a colored box if image fails to load
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_Action_Items image for "Action Items" page
  // Updated By Madhuri.K - Apply purple color (#a855f7) when inactive, white when active
  if (pageNameLower === "action items" || pageNameLower === "action item") {
    const actionItemsColor = "#a855f7"; // Purple
    
    return (
      <img 
        src={INIActionItemsIcon} 
        alt="Action Items" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply purple color (#a855f7) using CSS filter
                // This filter converts the image to purple color
                filter: 'brightness(0) saturate(100%) invert(52%) sepia(96%) saturate(1352%) hue-rotate(250deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Action_Items icon failed to load. Path:", INIActionItemsIcon);
          // Fallback: show a colored box if image fails to load
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_Warehouse image for "Warehouse" page
  // Updated By Madhuri.K - Apply amber/orange color (#f59e0b) when inactive, white when active
  if (pageNameLower === "warehouse") {
    const warehouseColor = "#f59e0b"; // Amber/Orange
    
    return (
      <img 
        src={INIWarehouseIcon} 
        alt="Warehouse" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply amber/orange color (#f59e0b) using CSS filter
                // This filter converts the image to amber/orange color
                filter: 'brightness(0) saturate(100%) invert(67%) sepia(96%) saturate(1352%) hue-rotate(10deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Warehouse icon failed to load. Path:", INIWarehouseIcon);
          // Fallback: show a colored box if image fails to load
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_ExternalAudit image for "External Audit" page
  // Updated By Madhuri.K - Apply indigo color (#6366f1) when inactive, white when active
  if (pageNameLower === "external audit") {
    const externalAuditColor = "#6366f1"; // Indigo
    
    return (
      <img 
        src={INIExternalAuditIcon} 
        alt="External Audit" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply indigo color (#6366f1) using CSS filter
                // This filter converts the image to indigo color
                filter: 'brightness(0) saturate(100%) invert(40%) sepia(96%) saturate(1352%) hue-rotate(230deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_ExternalAudit icon failed to load. Path:", INIExternalAuditIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_Reallocation image for "Initiative Reallocation" page
  // Updated By Madhuri.K - Apply cyan color (#06b6d4) when inactive, white when active
  if (pageNameLower === "initiative reallocations" || pageNameLower === "initiative reallocation") {
    const reallocationColor = "#06b6d4"; // Cyan
    
    return (
      <img 
        src={INIReallocationIcon} 
        alt="Initiative Reallocation" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply cyan color (#06b6d4) using CSS filter
                // This filter converts the image to cyan color
                filter: 'brightness(0) saturate(100%) invert(70%) sepia(96%) saturate(1352%) hue-rotate(160deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Reallocation icon failed to load. Path:", INIReallocationIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_ActivateSnooze image for "Initiative Activate Snooze" page
  // Updated By Madhuri.K - Apply orange color (#f97316) when inactive, white when active
  if (pageNameLower === "initiative activate snooze" || pageNameLower === "initiative activate - snooze") {
    const activateSnoozeColor = "#f97316"; // Orange
    
    return (
      <img 
        src={INIActivateSnoozeIcon} 
        alt="Initiative Activate Snooze" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply orange color (#f97316) using CSS filter
                // This filter converts the image to orange color
                filter: 'brightness(0) saturate(100%) invert(60%) sepia(96%) saturate(1352%) hue-rotate(5deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_ActivateSnooze icon failed to load. Path:", INIActivateSnoozeIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_WatchList image for "Watch List Configuration" page
  // Updated By Madhuri.K - Apply pink color (#ec4899) when inactive, white when active
  if (pageNameLower === "watch list configuration" || pageNameLower === "watch list") {
    const watchListColor = "#ec4899"; // Pink
    
    return (
      <img 
        src={INIWatchListIcon} 
        alt="Watch List Configuration" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply pink color (#ec4899) using CSS filter
                // This filter converts the image to pink color
                filter: 'brightness(0) saturate(100%) invert(50%) sepia(96%) saturate(1352%) hue-rotate(300deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_WatchList icon failed to load. Path:", INIWatchListIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_Conversion image for "Initiative Conversion" page
  // Updated By Madhuri.K - Apply blue color (#3b82f6) when inactive, white when active
  if (pageNameLower === "initiative conversion") {
    const conversionColor = "#3b82f6"; // Blue
    
    return (
      <img 
        src={INIConversionIcon} 
        alt="Initiative Conversion" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply blue color (#3b82f6) using CSS filter
                // This filter converts the image to blue color
                filter: 'brightness(0) saturate(100%) invert(39%) sepia(96%) saturate(1352%) hue-rotate(212deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Conversion icon failed to load. Path:", INIConversionIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_Linking image for "Initiative Linking" page
  // Updated By Madhuri.K - Apply purple color (#8b5cf6) when inactive, white when active
  if (pageNameLower === "initiative linking") {
    const linkingColor = "#8b5cf6"; // Purple
    
    return (
      <img 
        src={INILinkingIcon} 
        alt="Initiative Linking" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply purple color (#8b5cf6) using CSS filter
                // This filter converts the image to purple color
                filter: 'brightness(0) saturate(100%) invert(55%) sepia(96%) saturate(1352%) hue-rotate(250deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Linking icon failed to load. Path:", INILinkingIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_ManCom image for "Man-Com Prioritization" page
  // Updated By Madhuri.K - Apply red color (#ef4444) when inactive, white when active
  if (pageNameLower === "man-com prioritization" || pageNameLower === "man com prioritization" || pageNameLower === "mancom prioritization") {
    const manComColor = "#ef4444"; // Red
    
    return (
      <img 
        src={INIManComIcon} 
        alt="Man-Com Prioritization" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply red color (#ef4444) using CSS filter
                // This filter converts the image to red color
                filter: 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(7471%) hue-rotate(347deg) brightness(94%) contrast(92%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_ManCom icon failed to load. Path:", INIManComIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Removed icon color, use default/inherit color
  
  // Map page names to icon components for Initiative Management module
  // Updated By Madhuri.K - Add color to icons
  // Updated By Madhuri.K On 12-11-2025 - Use white (#fff) color when item is active/selected
  const getIconForPage = (iconType, defaultColor) => {
    const iconColor = isActive ? "#ffffff" : (defaultColor || "#3b82f6"); // White when active, otherwise default color
    const iconSx = { fontSize: 16 };
    const iconStyle = { color: iconColor, fill: iconColor }; // Add fill for SVG icons
    switch (iconType) {
      case "folder":
        return <FolderIcon sx={iconSx} style={iconStyle} />;
      case "inventory":
        return <InventoryIcon sx={iconSx} style={iconStyle} />;
      case "check":
        return <CheckCircleIcon sx={iconSx} style={iconStyle} />;
      case "cancel":
        return <CancelIcon sx={iconSx} style={iconStyle} />;
      case "warehouse":
        return <WarehouseIcon sx={iconSx} style={iconStyle} />;
      case "assignment":
        return <AssignmentIcon sx={iconSx} style={iconStyle} />;
      case "watch":
        return <WatchLaterIcon sx={iconSx} style={iconStyle} />;
      case "audit":
        return <AuditIcon sx={iconSx} style={iconStyle} />;
      case "swap":
        return <SwapHorizIcon sx={iconSx} style={iconStyle} />;
      case "schedule":
        return <ScheduleIcon sx={iconSx} style={iconStyle} />;
      case "settings":
        return <SettingsIcon sx={iconSx} style={iconStyle} />;
      default:
        return null;
    }
  };
  
  // Map page names to icon types and default colors
  // Updated By Madhuri.K - Give different vibrant colors to each page icon
  const pageIconConfig = {
    "initiative status management": { type: "settings", color: "#64748b" }, // Slate Gray
    "initiative status": { type: "settings", color: "#64748b" } // Slate Gray
  };
  
  // Check for exact match first
  if (pageIconConfig[pageNameLower]) {
    const config = pageIconConfig[pageNameLower];
    return getIconForPage(config.type, config.color);
  }
  
  // Check for partial match
  for (const [key, config] of Object.entries(pageIconConfig)) {
    if (pageNameLower.includes(key) || key.includes(pageNameLower)) {
      return getIconForPage(config.type, config.color);
    }
  }
  
  return null;
};

/**
 * Get icon for Initiative Tracking child pages based on page name
 * Added By Madhuri.K On 04-11-2025 - Add icons for pages under Initiative Tracking module
 * 
 * @param {string} pageName - The page name (tagName or tagDescription)
 * @param {boolean} isActive - Whether the item is currently selected/active
 * @returns {JSX.Element|null} - Icon component or null
 */
const getInitiativeTrackingPageIcon = (pageName, isActive = false) => {
  if (!pageName) return null;
  
  const pageNameLower = pageName.toLowerCase();
  
  // Updated By Madhuri.K - Use INI_Conversion image for "Initiative Conversion" page
  // Updated By Madhuri.K - Apply blue color (#3b82f6) when inactive, white when active
  if (pageNameLower === "initiative conversion") {
    const conversionColor = "#3b82f6"; // Blue
    
    return (
      <img 
        src={INIConversionIcon} 
        alt="Initiative Conversion" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply blue color (#3b82f6) using CSS filter
                // This filter converts the image to blue color
                filter: 'brightness(0) saturate(100%) invert(39%) sepia(96%) saturate(1352%) hue-rotate(212deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Conversion icon failed to load. Path:", INIConversionIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_Linking image for "Initiative Linking" page
  // Updated By Madhuri.K - Apply purple color (#8b5cf6) when inactive, white when active
  if (pageNameLower === "initiative linking") {
    const linkingColor = "#8b5cf6"; // Purple
    
    return (
      <img 
        src={INILinkingIcon} 
        alt="Initiative Linking" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply purple color (#8b5cf6) using CSS filter
                // This filter converts the image to purple color
                filter: 'brightness(0) saturate(100%) invert(55%) sepia(96%) saturate(1352%) hue-rotate(250deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Linking icon failed to load. Path:", INILinkingIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_ManCom image for "Man-Com Prioritization" page
  // Updated By Madhuri.K - Apply red color (#ef4444) when inactive, white when active
  if (pageNameLower === "man-com prioritization" || pageNameLower === "man com prioritization" || pageNameLower === "mancom prioritization") {
    const manComColor = "#ef4444"; // Red
    
    return (
      <img 
        src={INIManComIcon} 
        alt="Man-Com Prioritization" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply red color (#ef4444) using CSS filter
                // This filter converts the image to red color
                filter: 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(7471%) hue-rotate(347deg) brightness(94%) contrast(92%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_ManCom icon failed to load. Path:", INIManComIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Removed icon color, use default/inherit color
  
  // Map page names to icon components for Initiative Tracking module
  // Updated By Madhuri.K - Add color to icons
  // Updated By Madhuri.K On 12-11-2025 - Use white (#fff) color when item is active/selected
  const getIconForPage = (iconType, defaultColor) => {
    const iconColor = isActive ? "#ffffff" : (defaultColor || "#3b82f6"); // White when active, otherwise default color
    const iconSx = { fontSize: 16 };
    const iconStyle = { color: iconColor, fill: iconColor }; // Add fill for SVG icons
    switch (iconType) {
      case "trending":
        return <TrendingUpIcon sx={iconSx} style={iconStyle} />;
      case "transform":
        return <TransformIcon sx={iconSx} style={iconStyle} />;
      case "star":
        return <StarIcon sx={iconSx} style={iconStyle} />;
      case "link":
        return <LinkIcon sx={iconSx} style={iconStyle} />;
      case "gavel":
        return <GavelIcon sx={iconSx} style={iconStyle} />;
      case "swap":
        return <SwapHorizIcon sx={iconSx} style={iconStyle} />;
      default:
        return null;
    }
  };
  
  // Map page names to icon types and default colors
  // Added By Madhuri.K On 04-11-2025 - Changed icon for Man-Com Prioritization to GavelIcon (committee/management decisions)
  const pageIconConfig = {
    "initiative progress": { type: "trending", color: "#10b981" },
    "initiative prioritization": { type: "star", color: "#f59e0b" }
  };
  
  // Check for exact match first
  if (pageIconConfig[pageNameLower]) {
    const config = pageIconConfig[pageNameLower];
    return getIconForPage(config.type, config.color);
  }
  
  // Check for partial match
  for (const [key, config] of Object.entries(pageIconConfig)) {
    if (pageNameLower.includes(key) || key.includes(pageNameLower)) {
      return getIconForPage(config.type, config.color);
    }
  }
  
  return null;
};

/**
 * Get icon for Program module child pages based on page name
 * Added By Madhuri.K On 04-11-2025 - Add icons for pages under Program module with reduced size and different colors
 * 
 * @param {string} pageName - The page name (tagName or tagDescription)
 * @param {boolean} isActive - Whether the item is currently selected/active
 * @returns {JSX.Element|null} - Icon component or null
 */
const getProgramPageIcon = (pageName, isActive = false) => {
  if (!pageName) return null;
  
  const pageNameLower = pageName.toLowerCase();
  
  // Updated By Madhuri.K - Use INI_Program image for "Program" or "Program List" child pages
  // Updated By Madhuri.K - Apply blue color (#3b82f6) when inactive, white when active
  if (pageNameLower === "program" || pageNameLower === "program list") {
    const programColor = "#3b82f6"; // Blue
    
    return (
      <img 
        src={INIProgramIcon} 
        alt="Program" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply blue color (#3b82f6) using CSS filter
                // This filter converts the image to blue color
                filter: 'brightness(0) saturate(100%) invert(39%) sepia(96%) saturate(1352%) hue-rotate(212deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Program icon failed to load. Path:", INIProgramIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_Project image for "Projects" or "Project List" child pages
  // Updated By Madhuri.K - Apply indigo color (#6366f1) when inactive, white when active
  if (pageNameLower === "projects" || pageNameLower === "project list") {
    const projectColor = "#6366f1"; // Indigo
    
    return (
      <img 
        src={INIProjectIcon} 
        alt="Projects" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply indigo color (#6366f1) using CSS filter
                // This filter converts the image to indigo color
                filter: 'brightness(0) saturate(100%) invert(40%) sepia(96%) saturate(1352%) hue-rotate(230deg) brightness(98%) contrast(96%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Project icon failed to load. Path:", INIProjectIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Use INI_Approval image for "Project Health Sheet Approval" page
  // Updated By Madhuri.K - Apply green color (#10b981) when inactive, white when active
  if (pageNameLower === "project health sheet approval") {
    const approvalColor = "#10b981"; // Green
    
    return (
      <img 
        src={INIApprovalIcon} 
        alt="Project Health Sheet Approval" 
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{
          width: "20px",
          height: "20px",
          maxWidth: "20px",
          maxHeight: "20px",
          minWidth: "20px",
          minHeight: "20px",
          display: "block",
          margin: "0 auto",
          objectFit: "contain",
          flexShrink: 0,
          ...(isActive 
            ? { 
                filter: 'brightness(0) invert(1)', // White when active
                opacity: 1
              }
            : {
                // Apply green color (#10b981) using CSS filter
                // This filter converts the image to green color
                filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(142deg) brightness(95%) contrast(86%)',
                opacity: 1
              }
          )
        }}
        onError={(e) => {
          console.error("INI_Approval icon failed to load. Path:", INIApprovalIcon);
          e.target.style.display = 'none';
        }}
      />
    );
  }
  
  // Updated By Madhuri.K - Removed icon color, use default/inherit color
  
  // Map page names to icon components for Program module
  // Updated By Madhuri.K - Add color to icons
  // Added By Madhuri.K On 04-11-2025 - Standard icon size to 16px for all module pages (same as other modules)
  // Updated By Madhuri.K On 12-11-2025 - Use white (#fff) color when item is active/selected
  const getIconForPage = (iconType, defaultColor) => {
    const iconColor = isActive ? "#ffffff" : (defaultColor || "#3b82f6"); // White when active, otherwise default color
    const iconSx = { fontSize: 16 };
    const iconStyle = { color: iconColor, fill: iconColor }; // Add fill for SVG icons
    switch (iconType) {
      case "list":
        return <ListAltIcon sx={iconSx} style={iconStyle} />;
      case "assignment":
        return <AssignmentIcon sx={iconSx} style={iconStyle} />;
      case "approval":
        return <AssignmentTurnedInIcon sx={iconSx} style={iconStyle} />;
      default:
        return null;
    }
  };
  
  // Map page names to icon types and default colors
  // Added By Madhuri.K On 04-11-2025 - Standard size to 16px for all module pages (consistent with other modules)
  // Added By Madhuri.K On 04-11-2025 - Changed icon for Project Health Sheet Approval from CheckCircleOutlineIcon to AssignmentTurnedInIcon
  const pageIconConfig = {
    "milestone progress": { type: "assignment", color: "#3b82f6" }
  };
  
  // Check for exact match first
  if (pageIconConfig[pageNameLower]) {
    const config = pageIconConfig[pageNameLower];
    return getIconForPage(config.type, config.color);
  }
  
  // Check for partial match
  for (const [key, config] of Object.entries(pageIconConfig)) {
    if (pageNameLower.includes(key) || key.includes(pageNameLower)) {
      return getIconForPage(config.type, config.color);
    }
  }
  
  return null;
};

/**
 * Helper function to get CSS filter for a hex color
 * Updated By Madhuri.K On 18-11-2025 - Convert hex colors to CSS filters for custom images
 * 
 * @param {string} hexColor - Hex color code (e.g., "#3b82f6")
 * @returns {string} - CSS filter string
 */
const getColorFilter = (hexColor) => {
  // Updated By Madhuri.K On 18-11-2025 - Map common colors to their CSS filters
  const colorFilterMap = {
    "#3b82f6": "brightness(0) saturate(100%) invert(39%) sepia(96%) saturate(1352%) hue-rotate(212deg) brightness(98%) contrast(96%)", // Blue
    "#10b981": "brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(142deg) brightness(95%) contrast(86%)", // Green
    "#ef4444": "brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(7471%) hue-rotate(347deg) brightness(94%) contrast(92%)", // Red
    "#8b5cf6": "brightness(0) saturate(100%) invert(55%) sepia(96%) saturate(1352%) hue-rotate(250deg) brightness(98%) contrast(96%)", // Purple
    "#f59e0b": "brightness(0) saturate(100%) invert(67%) sepia(96%) saturate(1352%) hue-rotate(10deg) brightness(98%) contrast(96%)", // Amber/Orange
    "#06b6d4": "brightness(0) saturate(100%) invert(70%) sepia(96%) saturate(1352%) hue-rotate(160deg) brightness(98%) contrast(96%)", // Cyan
    "#6366f1": "brightness(0) saturate(100%) invert(40%) sepia(96%) saturate(1352%) hue-rotate(230deg) brightness(98%) contrast(96%)", // Indigo
    "#ec4899": "brightness(0) saturate(100%) invert(50%) sepia(96%) saturate(1352%) hue-rotate(300deg) brightness(98%) contrast(96%)", // Pink
    "#f97316": "brightness(0) saturate(100%) invert(60%) sepia(96%) saturate(1352%) hue-rotate(5deg) brightness(98%) contrast(96%)", // Orange
    "#a855f7": "brightness(0) saturate(100%) invert(52%) sepia(96%) saturate(1352%) hue-rotate(250deg) brightness(98%) contrast(96%)", // Purple (darker)
    "#14b8a6": "brightness(0) saturate(100%) invert(65%) sepia(96%) saturate(1352%) hue-rotate(150deg) brightness(98%) contrast(96%)", // Teal
    "#22c55e": "brightness(0) saturate(100%) invert(60%) sepia(96%) saturate(1352%) hue-rotate(120deg) brightness(98%) contrast(96%)", // Green (lighter)
    "#64748b": "brightness(0) saturate(100%) invert(45%) sepia(10%) saturate(500%) hue-rotate(180deg) brightness(90%) contrast(85%)", // Slate Gray
    "#6b7280": "brightness(0) saturate(100%) invert(50%) sepia(5%) saturate(500%) hue-rotate(180deg) brightness(90%) contrast(85%)", // Gray
  };
  
  return colorFilterMap[hexColor.toLowerCase()] || "brightness(0) saturate(100%) invert(39%) sepia(96%) saturate(1352%) hue-rotate(212deg) brightness(98%) contrast(96%)"; // Default to blue
};

/**
 * Get icon for Reports module child pages based on page name
 * Added By Madhuri.K On 04-11-2025 - Add icons for pages under Reports module
 * Updated By Madhuri.K On 18-11-2025 - Use custom images instead of Material-UI icons with colors
 * 
 * @param {string} pageName - The page name (tagName or tagDescription)
 * @param {boolean} isActive - Whether the item is currently selected/active
 * @returns {JSX.Element|null} - Icon component or null
 */
const getReportsPageIcon = (pageName, isActive = false) => {
  if (!pageName) return null;
  
  const pageNameLower = pageName.toLowerCase();
  
  // Updated By Madhuri.K On 18-11-2025 - Map page names to custom image icons with colors
  // Map page names to image icons and colors for Reports module
  const pageIconConfig = {
    "initiative report": { icon: InitiativeReportIcon, color: "#3b82f6" }, // Blue
    "action items report": { icon: ActionItemsReportIcon, color: "#ec4899" }, // Pink
    "change nature of initiative report": { icon: INIReportIcon, color: "#06b6d4" }, // Cyan
    "program tracking report": { icon: ProgramTrackingReportIcon, color: "#10b981" }, // Green
    "delayed initiatives": { icon: DelayedINIReportIcon, color: "#ef4444" }, // Red
    "delivery calendar": { icon: DeliveryCalendarReportIcon, color: "#f59e0b" }, // Amber/Orange
    "ageing report": { icon: AgeingReportIcon, color: "#6366f1" } // Indigo
  };
  
  // Check for exact match first
  if (pageIconConfig[pageNameLower]) {
    const { icon: iconImage, color } = pageIconConfig[pageNameLower];
    // Updated By Madhuri.K On 18-11-2025 - Return image icon with color filter
    return (
      <img 
        src={iconImage} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Check for partial match
  for (const [key, config] of Object.entries(pageIconConfig)) {
    if (pageNameLower.includes(key) || key.includes(pageNameLower)) {
      const { icon: iconImage, color } = config;
      // Updated By Madhuri.K On 18-11-2025 - Return image icon with color filter
      return (
        <img 
          src={iconImage} 
          alt={pageName}
          className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
          style={{ 
            width: 16, 
            height: 16,
            ...(isActive 
              ? { filter: 'brightness(0) invert(1)' } // White when active
              : { filter: getColorFilter(color) } // Apply color when inactive
            )
          }}
        />
      );
    }
  }
  
  return null;
};

/**
 * Get icon for Dashboard module child pages based on page name
 * Added By Madhuri.K - Add icons for pages under Dashboard module
 * Updated By Madhuri.K On 18-11-2025 - Use custom images for Initiative Dashboard and Initiative Progress
 * 
 * @param {string} pageName - The page name (tagName or tagDescription)
 * @param {boolean} isActive - Whether the item is currently selected/active
 * @returns {JSX.Element|null} - Icon component or null
 */
const getDashboardPageIcon = (pageName, isActive = false) => {
  if (!pageName) return null;
  
  const pageNameLower = pageName.toLowerCase();
  
  // Updated By Madhuri.K On 18-11-2025 - Map page names to custom image icons with colors
  // Map page names to image icons and colors for Dashboard module
  const pageIconConfig = {
    "initiative dashboard": { icon: IniDashboardIcon, color: "#3b82f6" }, // Blue
    "initiative progress": { icon: InitiativeProgressIcon, color: "#10b981" } // Green
  };
  
  // Check for exact match first
  if (pageIconConfig[pageNameLower]) {
    const { icon: iconImage, color } = pageIconConfig[pageNameLower];
    // Updated By Madhuri.K On 18-11-2025 - Return image icon with color filter
    return (
      <img 
        src={iconImage} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Check for partial match
  for (const [key, config] of Object.entries(pageIconConfig)) {
    if (pageNameLower.includes(key) || key.includes(pageNameLower)) {
      const { icon: iconImage, color } = config;
      // Updated By Madhuri.K On 18-11-2025 - Return image icon with color filter
      return (
        <img 
          src={iconImage} 
          alt={pageName}
          className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
          style={{ 
            width: 16, 
            height: 16,
            ...(isActive 
              ? { filter: 'brightness(0) invert(1)' } // White when active
              : { filter: getColorFilter(color) } // Apply color when inactive
            )
          }}
        />
      );
    }
  }
  
  return null;
};

/**
 * Get icon for Configuration module child pages based on page name
 * Added By Madhuri.K On 04-11-2025 - Add icons for pages under Configuration module based on page names
 * 
 * @param {string} pageName - The page name (tagName or tagDescription)
 * @param {boolean} isActive - Whether the item is currently selected/active
 * @returns {JSX.Element|null} - Icon component or null
 */
const getConfigurationPageIcon = (pageName, isActive = false) => {
  if (!pageName) return null;
  
  // Updated By Madhuri.K On 18-11-2025 - Normalize spaces (replace multiple spaces with single space) before checking
  const normalizedPageName = pageName.replace(/\s+/g, ' ').trim();
  const pageNameLower = normalizedPageName.toLowerCase();
  // Updated By Madhuri.K - Removed icon color, use default/inherit color
  
  // Map page names to icon components for Configuration module
  // Updated By Madhuri.K - Add color to icons
  // Added By Madhuri.K On 04-11-2025 - Standard icon size to 16px for all module pages
  // Updated By Madhuri.K On 12-11-2025 - Use white (#fff) color when item is active/selected
  const getIconForPage = (iconType, defaultColor) => {
    const iconColor = isActive ? "#ffffff" : (defaultColor || "#3b82f6"); // White when active, otherwise default color
    const iconSx = { fontSize: 16 };
    const iconStyle = { color: iconColor, fill: iconColor }; // Add fill for SVG icons
    switch (iconType) {
      case "business":
        return <BusinessCenterIcon sx={iconSx} style={iconStyle} />;
      case "category":
        return <CategoryIcon sx={iconSx} style={iconStyle} />;
      case "security":
        return <SecurityIcon sx={iconSx} style={iconStyle} />;
      case "group":
        return <GroupIcon sx={iconSx} style={iconStyle} />;
      case "email":
        return <EmailIcon sx={iconSx} style={iconStyle} />;
      case "lock":
        return <LockIcon sx={iconSx} style={iconStyle} />;
      case "tree":
        return <AccountTreeIcon sx={iconSx} style={iconStyle} />;
      case "build":
        return <BuildIcon sx={iconSx} style={iconStyle} />;
      case "info":
        return <InfoIcon sx={iconSx} style={iconStyle} />;
      case "checkbox":
        return <CheckBoxIcon sx={iconSx} style={iconStyle} />;
      case "public":
        return <PublicIcon sx={iconSx} style={iconStyle} />;
      case "money":
        return <MoneyIcon sx={iconSx} style={iconStyle} />;
      case "flag":
        return <FlagIcon sx={iconSx} style={iconStyle} />;
      case "trending":
        return <TrendingUpIcon sx={iconSx} style={iconStyle} />;
      case "table":
        return <TableChartIcon sx={iconSx} style={iconStyle} />;
      case "person":
        return <PersonIcon sx={iconSx} style={iconStyle} />;
      case "people":
        return <PeopleIcon sx={iconSx} style={iconStyle} />;
      case "shipping":
        return <LocalShippingIcon sx={iconSx} style={iconStyle} />;
      case "inbox":
        return <InboxIcon sx={iconSx} style={iconStyle} />;
      case "settings":
        return <SettingsIcon sx={iconSx} style={iconStyle} />;
      case "business":
        return <BusinessIcon sx={iconSx} style={iconStyle} />;
      case "groupwork":
        return <GroupWorkIcon sx={iconSx} style={iconStyle} />;
      case "warehouse":
        return <WarehouseIcon sx={iconSx} style={iconStyle} />;
      case "assignment":
        return <AssignmentIcon sx={iconSx} style={iconStyle} />;
      case "folder":
        return <FolderIcon sx={iconSx} style={iconStyle} />;
      case "link":
        return <LinkIcon sx={iconSx} style={iconStyle} />;
      case "warning":
        return <WarningIcon sx={iconSx} style={iconStyle} />;
      case "flash":
        return <FlashOnIcon sx={iconSx} style={iconStyle} />;
      case "assessment":
        return <AssessmentIcon sx={iconSx} style={iconStyle} />;
      default:
        return null;
    }
  };
  
  // Updated By Madhuri.K On 18-11-2025 - Check for pages with custom images first
  // Updated By Madhuri.K On 18-11-2025 - Parent node images
  // UI & Navigation Node
  if (pageNameLower === "ui & navigation" || pageNameLower === "ui navigation") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={UINavigationNodeIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Security Node - Updated By Madhuri.K On 18-11-2025
  if (pageNameLower === "security") {
    const color = "#6366f1"; // Indigo
    return (
      <img 
        src={SecurityNodeIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Projects Node - Updated By Madhuri.K On 18-11-2025
  if (pageNameLower === "projects") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={ProjectsNodeIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Checklist Node - Updated By Madhuri.K On 18-11-2025
  if (pageNameLower === "checklist") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={ChecklistNodeIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Risk Matrix Node - Updated By Madhuri.K On 18-11-2025
  if (pageNameLower === "risk master" || pageNameLower === "risk matrix") {
    const color = "#ef4444"; // Red
    return (
      <img 
        src={RiskMatrixNodeIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Organization Node - Updated By Madhuri.K On 18-11-2025
  if (pageNameLower === "organization") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={OrganizationNodeIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // General Information Node - Updated By Madhuri.K On 18-11-2025
  if (pageNameLower === "general information" || pageNameLower === "general") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={GeneralInformationNodeIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Company Information
  if (pageNameLower === "company information") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={CompanyInformationIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Risk Categories uses custom image
  if (pageNameLower === "risk categories") {
    const color = "#ef4444"; // Red
    return (
      <img 
        src={RiskCategoriesIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Risk Matrix Definition uses custom image
  if (pageNameLower === "risk matrix definition") {
    const color = "#ef4444"; // Red
    return (
      <img 
        src={RiskMatrixDefinitionIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Country uses custom image
  if (pageNameLower === "country" || pageNameLower === "countries") {
    const color = "#06b6d4"; // Cyan
    return (
      <img 
        src={CountryIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Documents Category uses custom image
  if (pageNameLower === "documents category" || pageNameLower === "document category" || pageNameLower === "document categories") {
    const color = "#f59e0b"; // Amber/Orange
    return (
      <img 
        src={DocumentsCategoryIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Audit Type Definition uses custom image
  if (pageNameLower === "audit type definition" || pageNameLower === "audit type") {
    const color = "#6366f1"; // Indigo
    return (
      <img 
        src={AuditTypeDefinitionIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Skills uses custom image
  if (pageNameLower === "skills" || pageNameLower === "skill") {
    const color = "#8b5cf6"; // Purple
    return (
      <img 
        src={SkillsIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Currency uses custom image
  if (pageNameLower === "currency" || pageNameLower === "currencies") {
    const color = "#10b981"; // Green
    return (
      <img 
        src={CurrencyIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Department Master uses custom image
  if (pageNameLower === "department master") {
    const color = "#f59e0b"; // Amber/Orange
    return (
      <img 
        src={DepartmentMasterIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Organization Structure uses custom image
  if (pageNameLower === "organization structure") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={OrganizationStructureIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Delivery Team uses custom image
  if (pageNameLower === "delivery team") {
    const color = "#ec4899"; // Pink
    return (
      <img 
        src={DeliveryTeamIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Delivery Unit uses custom image
  if (pageNameLower === "delivery unit") {
    const color = "#10b981"; // Green
    return (
      <img 
        src={DeliveryUnitIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Organization Unit uses custom image
  if (pageNameLower === "organization unit") {
    const color = "#06b6d4"; // Cyan
    return (
      <img 
        src={OrganizationUnitIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Business Group uses custom image
  if (pageNameLower === "business group") {
    const color = "#8b5cf6"; // Purple
    return (
      <img 
        src={BusinessGroupIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Process Information uses custom image
  if (pageNameLower === "process information") {
    const color = "#8b5cf6"; // Purple
    return (
      <img 
        src={ProcessInformationIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Business Value uses custom image
  if (pageNameLower === "business value") {
    const color = "#10b981"; // Green
    return (
      <img 
        src={BusinessValueIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Business Impact uses custom image
  // Handle variations with single or double spaces
  if (pageNameLower === "business impact" || pageNameLower === "business  impact" || pageNameLower === "impact") {
    const color = "#f97316"; // Orange Red
    return (
      <img 
        src={BusinessImpactIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Business Feasibility uses custom image
  // Handle variations with single or double spaces
  if (pageNameLower === "business feasibility" || pageNameLower === "business  feasibility" || pageNameLower === "feasibility") {
    const color = "#06b6d4"; // Cyan
    return (
      <img 
        src={BusinessFeasibilityIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Business Strategy uses custom image
  if (pageNameLower === "business strategy") {
    const color = "#f59e0b"; // Amber/Orange
    return (
      <img 
        src={BusinessStrategyIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Checklist Item uses custom image
  if (pageNameLower === "checklist item") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={ChecklistItemIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Checklist Section uses custom image
  if (pageNameLower === "checklist section") {
    const color = "#8b5cf6"; // Purple
    return (
      <img 
        src={ChecklistSectionIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Checklist Maker uses custom image
  if (pageNameLower === "checklist maker") {
    const color = "#06b6d4"; // Cyan
    return (
      <img 
        src={ChecklistMakerIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Checklist Maker Inbox uses custom image
  if (pageNameLower === "checklist maker inbox") {
    const color = "#10b981"; // Green
    return (
      <img 
        src={ChecklistMakerInboxIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Prioritization Checklist uses custom image
  if (pageNameLower === "prioritization checklist") {
    const color = "#f59e0b"; // Amber/Orange
    return (
      <img 
        src={PrioritizationChecklistIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Project Status uses custom image
  if (pageNameLower === "project status") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={ProjectStatusIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Project Group uses custom image
  // Normalized page name should handle spacing variations
  if (pageNameLower === "project group") {
    const color = "#8b5cf6"; // Purple
    return (
      <img 
        src={ProjectGroupIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Project Type uses custom image
  if (pageNameLower === "project type") {
    const color = "#06b6d4"; // Cyan
    return (
      <img 
        src={ProjectTypeIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Project Size Units uses custom image
  if (pageNameLower === "project size units") {
    const color = "#10b981"; // Green
    return (
      <img 
        src={ProjectSizeUnitsIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Password Management uses custom image
  if (pageNameLower === "password management") {
    const color = "#ef4444"; // Red
    return (
      <img 
        src={PasswordManagementIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Unlock User uses custom image
  if (pageNameLower === "unlock user") {
    const color = "#10b981"; // Green
    return (
      <img 
        src={UnlockUserIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - User Groups uses custom image
  if (pageNameLower === "user groups") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={UserGroupsIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Role Access uses custom image
  if (pageNameLower === "role access") {
    const color = "#14b8a6"; // Teal
    return (
      <img 
        src={RoleAccessIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Role Maintenance uses custom image
  if (pageNameLower === "role maintenance") {
    const color = "#ec4899"; // Pink
    return (
      <img 
        src={RoleMaintenanceIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - User Login Maintenance uses custom image
  if (pageNameLower === "user login maintenance") {
    const color = "#06b6d4"; // Cyan
    return (
      <img 
        src={UserLoginMaintenanceIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Vendor Login Maintenance uses custom image
  if (pageNameLower === "vendor login maintenance") {
    const color = "#f59e0b"; // Amber/Orange
    return (
      <img 
        src={VendorLoginMaintenanceIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - User Maintenance uses custom image
  if (pageNameLower === "user maintenance") {
    const color = "#6366f1"; // Indigo
    return (
      <img 
        src={UserMaintenanceIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Initiative Corporate Fields uses custom image
  if (pageNameLower === "initiative corporate fields") {
    const color = "#64748b"; // Slate Gray
    return (
      <img 
        src={InitiativeCorporateFieldsIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Initiative Relation Type Master uses custom image
  if (pageNameLower === "initiative relation type master") {
    const color = "#14b8a6"; // Teal
    return (
      <img 
        src={InitiativeRelationTypeMasterIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Action Priority uses custom image
  if (pageNameLower === "action priority") {
    const color = "#f97316"; // Orange Red
    return (
      <img 
        src={ActionPriorityIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Action Status uses custom image
  if (pageNameLower === "action status") {
    const color = "#8b5cf6"; // Purple
    return (
      <img 
        src={ActionStatusIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Initiative CRESS Ranges uses custom image
  if (pageNameLower === "initiative cress ranges") {
    const color = "#06b6d4"; // Cyan
    return (
      <img 
        src={InitiativeCRESSRangesIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Cost Category uses custom image
  if (pageNameLower === "cost category") {
    const color = "#10b981"; // Green
    return (
      <img 
        src={CostCategoryIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Initiative Access uses custom image
  if (pageNameLower === "initiative access") {
    const color = "#ef4444"; // Red
    return (
      <img 
        src={InitiativeAccessIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Resource Approach uses custom image
  if (pageNameLower === "resource approach") {
    const color = "#ec4899"; // Pink
    return (
      <img 
        src={ResourceApproachIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Priority uses custom image
  if (pageNameLower === "priority") {
    const color = "#f59e0b"; // Amber/Orange
    return (
      <img 
        src={PriorityIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Vendor uses custom image
  if (pageNameLower === "vendor") {
    const color = "#a855f7"; // Purple (darker)
    return (
      <img 
        src={INIVendorIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Initiative Category uses custom image
  if (pageNameLower === "initiative category") {
    const color = "#6366f1"; // Indigo
    return (
      <img 
        src={InitiativeCategoryIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Initiative Stage uses custom image
  if (pageNameLower === "initiative stage") {
    const color = "#22c55e"; // Green (lighter)
    return (
      <img 
        src={InitiativeStageIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Nature of Initiative uses custom image
  if (pageNameLower === "nature of initiative") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={NatureOfInitiativeIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Updated By Madhuri.K On 18-11-2025 - Initiative Masters (parent node) uses custom image
  if (pageNameLower === "initiative masters") {
    const color = "#3b82f6"; // Blue
    return (
      <img 
        src={InitiativeMastersIcon} 
        alt={pageName}
        className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
        style={{ 
          width: 16, 
          height: 16,
          ...(isActive 
            ? { filter: 'brightness(0) invert(1)' } // White when active
            : { filter: getColorFilter(color) } // Apply color when inactive
          )
        }}
      />
    );
  }
  
  // Map page names to icon types and default colors
  // Added By Madhuri.K On 04-11-2025 - Icons for Configuration module pages based on page names
  const pageIconConfig = {
    // Configuration Parent Nodes - Updated By Madhuri.K - Give icons based on node names
    // Updated By Madhuri.K On 18-11-2025 - Company Information, UI & Navigation, Security, Projects, Checklist, Risk Matrix, Organization, and General Information now use custom images (handled above)
    "corporate settings": { type: "settings", color: "#6b7280" }, // Gray
    "corporate settings": { type: "settings", color: "#6b7280" }, // Gray
    // Updated By Madhuri.K On 18-11-2025 - UI & Navigation, Security, Projects, Checklist, Risk Matrix, Organization, and General Information now use custom images (removed from here)
    // Updated By Madhuri.K On 18-11-2025 - Initiative Masters now uses custom image (handled above)
    "business masters": { type: "business", color: "#3b82f6" }, // Blue
    
    // Organization Node - Updated By Madhuri.K - Give different colors to each page icon
    // Updated By Madhuri.K On 18-11-2025 - Organization Structure, Business Group, Organization Unit, Delivery Unit, and Delivery Team now use custom images (handled above)
    // Updated By Madhuri.K On 18-11-2025 - Department Master now uses custom image (handled above)
    
    // Initiative Masters Node - Updated By Madhuri.K - Give different colors to each page icon
    // Updated By Madhuri.K On 18-11-2025 - Initiative CRESS Ranges, Nature of Initiative, Priority, Cost Category, Action Status, Action Priority, Initiative Category, Resource Approach, Initiative Relation Type Master, Initiative Stage, Initiative Access, Initiative Corporate Fields, Vendor, and Initiative Masters (parent) now use custom images (handled above)
    
    // Business Masters Node - Updated By Madhuri.K - Give different colors to each page icon
    // Updated By Madhuri.K On 18-11-2025 - Business Feasibility, Business Impact, Business Value, Business Strategy, and Process Information now use custom images (handled above)
    
    // Projects Node - Updated By Madhuri.K - Give different colors to each page icon
    // Updated By Madhuri.K On 18-11-2025 - Project Status, Project Group, Project Type, and Project Size Units now use custom images (handled above)
    "practice": { type: "build", color: "#f59e0b" }, // Orange (changed from purple)
    
    // Checklist Node - Updated By Madhuri.K - Give different colors to each page icon
    // Updated By Madhuri.K On 18-11-2025 - Checklist Item, Checklist Section, Checklist Maker, Checklist Maker Inbox, and Prioritization Checklist now use custom images (handled above)
    
    // UI & Navigation Node - Updated By Madhuri.K - Give different colors to each page icon
    "ui & navigation": { type: "info", color: "#3b82f6" }, // Blue (General node icon)
    "ui navigation": { type: "info", color: "#3b82f6" }, // Blue (General node icon)
    "system email messages master": { type: "email", color: "#3b82f6" }, // Blue
    "system email message master": { type: "email", color: "#3b82f6" }, // Blue
    "framework email messages": { type: "email", color: "#8b5cf6" }, // Purple
    "framework email message": { type: "email", color: "#8b5cf6" }, // Purple
    
    // General Information Node / General Node - Updated By Madhuri.K - Give different colors to each page icon
    "general information": { type: "info", color: "#3b82f6" }, // Blue
    "general": { type: "info", color: "#3b82f6" }, // Blue
    // Updated By Madhuri.K On 18-11-2025 - Skills, Audit Type Definition, Country, Currency, and Documents Category now use custom images (handled above)
    
    // Security Node - Updated By Madhuri.K - Give different colors to each page icon
    // Updated By Madhuri.K On 18-11-2025 - Password Management, Unlock User, User Groups, Role Access, Role Maintenance, User Login Maintenance, Vendor Login Maintenance, and User Maintenance now use custom images (handled above)
    "user group access": { type: "security", color: "#8b5cf6" }, // Purple (changed from blue)
    "node maintenance": { type: "build", color: "#22c55e" }, // Green (changed from purple)
    "active session maintenance": { type: "security", color: "#f97316" }, // Orange Red
    
    // Updated By Madhuri.K On 18-11-2025 - Company Information now uses custom image (handled above)
    "corporate settings": { type: "settings", color: "#6b7280" },
    
    // Risk Master Node - Updated By Madhuri.K - Give different colors to each page icon
    // Updated By Madhuri.K On 18-11-2025 - Risk Categories and Risk Matrix Definition now use custom images (handled above)
  };
  
  // Check for exact match first
  if (pageIconConfig[pageNameLower]) {
    const config = pageIconConfig[pageNameLower];
    return getIconForPage(config.type, config.color);
  }
  
  // Check for partial match
  for (const [key, config] of Object.entries(pageIconConfig)) {
    if (pageNameLower.includes(key) || key.includes(pageNameLower)) {
      return getIconForPage(config.type, config.color);
    }
  }
  
  return null;
};

/**
 * Builds the full navigation path by combining parent and child paths
 * 
 * @param {string} parentPath - The parent navigation path
 * @param {string} tagDescription - The current item's tag description
 * @returns {string} - The combined full path (e.g., "Parent > Child")
 */
const buildFullPath = (parentPath, tagDescription) => {
  // If parentPath exists, combine it with tagDescription
  if (parentPath) {
    // Clean up the parentPath and tagDescription, removing any leading or trailing ">"
    let cleanParentPath = parentPath
      ?.trim()
      ?.replace(/^\s*>\s*/, "")  // Remove leading ">"
      ?.replace(/\s*>\s*$/, "");  // Remove trailing ">"
    let cleanTagDescription = tagDescription
      .trim()
      ?.replace(/^\s*>\s*/, "")
      ?.replace(/\s*>\s*$/, "");

    // Concatenate and ensure no double ">" separators
    return `${cleanParentPath} > ${cleanTagDescription}`?.replace(/\s*>\s*>+\s*/g, " > ");
  }
  // If no parentPath, return tagDescription directly
  return tagDescription.trim();
};

/**
 * Main Navigation Component
 * 
 * @param {Array} items - Array of navigation items to display
 * @param {boolean} isHovered - Whether the sidebar is being hovered
 * @param {string} mode - Sidebar mode: "full", "compact", or "close"
 * @param {string} searchTerm - Search term for filtering navigation items
 * @param {Function} onSearch - Callback function for search input changes
 * @param {Function} onRefresh - Callback function for refresh button
 * @param {boolean} isRefreshing - Whether refresh is in progress
 * @param {boolean} loading - Whether data is loading
 * @param {Array} allNavigations - All navigation items (for filtering)
 * 
 * @author Madhuri.k
 * @date 04-11-2025
 */
const WhizVerticalNav = ({ 
  items, 
  isHovered, 
  mode, 
  searchTerm = "", 
  onSearch,
  onRefresh,
  isRefreshing = false,
  loading = false,
  allNavigations = []
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Added By Madhuri.K On 22-11-2025 - Access settings and update settings
  const { settings, updateSettings } = useSettings();
  
  // State management
  const [selectedItem, setSelectedItem] = useState(location.pathname); // Currently selected navigation item
  // Updated By Madhuri.K On 12-11-2025 - Use ref to track immediate selection (synchronous update)
  // This ensures isSelected works correctly on first selection before state updates
  const selectedItemRef = useRef(location.pathname);
  // Updated By Madhuri.K On 06-11-2025 - Initially show collapsed (icons only), expand on click to show page names
  // Updated By Madhuri.K - Initialize based on screen width: collapsed on desktop, expanded on mobile
  // On mobile (≤1199px), always start expanded to show text labels
  // Updated By Madhuri.K - Start with false (expanded) to ensure mobile view shows correctly, then adjust on mount
  const [isCollapsed, setIsCollapsed] = useState(false); // Start expanded, will be adjusted on mount based on screen width
  const [expandedItems, setExpandedItems] = useState({}); // Track which parent items are expanded
  // Updated By Madhuri.K On 06-11-2025 - Track which parent item is currently expanded to show children on right side
  const [activeExpandedParent, setActiveExpandedParent] = useState(null); // Track the currently expanded parent item ID
  // Updated By Madhuri.K On 06-11-2025 - Store the expanded item directly for easier access
  const [expandedParentItem, setExpandedParentItem] = useState(null); // Store the expanded parent item object
  // Updated By Madhuri.K On 06-11-2025 - Track if children panel is actually visible (has children to show)
  const [hasVisibleChildren, setHasVisibleChildren] = useState(false); // Track if children panel should be visible
  const [isDarkMode, setIsDarkMode] = useState(false); // Dark mode toggle (currently not used, can be enabled)
  const [searchQuery, setSearchQuery] = useState(searchTerm); // Search query state
  const [filteredItems, setFilteredItems] = useState(items); // Filtered navigation items
  // Added By Madhuri.K On 04-11-2025 - Track selected module/page name to display at top
  const [selectedModuleName, setSelectedModuleName] = useState("Modules"); // Selected module/page name to display at top
  // Added By Madhuri.K - Track search term for children panel
  const [childrenPanelSearchTerm, setChildrenPanelSearchTerm] = useState(""); // Search term for filtering children items
  // Added By Madhuri.K - Track parent hierarchy for back navigation
  const [parentHierarchy, setParentHierarchy] = useState([]); // Track parent hierarchy for back navigation
  
  // Ref for children panel to ensure it's positioned at top
  const childrenPanelRef = useRef(null);
  
  // Added By Madhuri.K On 22-11-2025 - Refs to store latest items and allNavigations for event handler
  const itemsRef = useRef(items);
  const allNavigationsRef = useRef(allNavigations);
  
  // Added By Madhuri.K On 22-11-2025 - Update refs when items change
  useEffect(() => {
    itemsRef.current = items;
    allNavigationsRef.current = allNavigations;
  }, [items, allNavigations]);
  
  // Debug: Log state changes for children panel
  useEffect(() => {
    console.log('🔍 State Update - hasVisibleChildren:', hasVisibleChildren, 'expandedParentItem:', expandedParentItem?.tagName, 'activeExpandedParent:', activeExpandedParent);
  }, [hasVisibleChildren, expandedParentItem, activeExpandedParent]);

  // Reset children panel search term when parent item changes
  useEffect(() => {
    setChildrenPanelSearchTerm("");
  }, [expandedParentItem]);
  
  // Updated By Madhuri.K - Ensure hierarchy is set for Configuration nodes when panel is visible
  // This ensures back button always shows when panel is rendered for Configuration nodes
  useEffect(() => {
    if (hasVisibleChildren && expandedParentItem && parentHierarchy.length === 0) {
      const parentName = expandedParentItem.tagName || "";
      const parentNameLower = parentName.toLowerCase();
      const isConfigurationNode = parentNameLower === "security" || 
                                parentNameLower === "ui & navigation" || 
                                parentNameLower === "ui navigation" ||
                                parentNameLower === "company information" ||
                                parentNameLower === "corporate settings" ||
                                parentNameLower === "projects" ||
                                parentNameLower === "initiative masters" ||
                                parentNameLower === "organization" ||
                                parentNameLower === "business masters" ||
                                parentNameLower === "checklist" ||
                                parentNameLower === "general information" ||
                                parentNameLower === "general" ||
                                parentNameLower === "risk master";
      
      if (isConfigurationNode) {
        // Find Configuration in navigation tree
        const findItemInTree = (itemsArray, targetTagName, level = 0) => {
          if (level > 5 || !itemsArray) return null;
          const targetLower = targetTagName.toLowerCase();
          for (const treeItem of itemsArray) {
            const tagNameLower = treeItem.tagName?.toLowerCase() || "";
            const tagDescLower = treeItem.tagDescription?.toLowerCase() || "";
            if (tagNameLower === targetLower || tagDescLower === targetLower) {
              return treeItem;
            }
            if (treeItem.children && Array.isArray(treeItem.children)) {
              const found = findItemInTree(treeItem.children, targetTagName, level + 1);
              if (found) return found;
            }
          }
          return null;
        };
        
        const sourceItems = (allNavigations && allNavigations.length > 0) ? allNavigations : items;
        const configItem = findItemInTree(sourceItems, "Configuration");
        if (configItem) {
          setParentHierarchy([configItem]);
          console.log(`✅ Set hierarchy to [Configuration] for ${parentName} in useEffect - back button will show`);
        } else {
          setParentHierarchy([{
            tagName: "Configuration",
            tagDescription: "Configuration",
            tagId: null,
            children: [],
            isParent: true
          }]);
          console.log(`✅ Created minimal Configuration object for hierarchy in useEffect - back button will show`);
        }
      }
    }
  }, [hasVisibleChildren, expandedParentItem, parentHierarchy.length, allNavigations, items]);

  // Updated By Madhuri.K - Scroll children panel to top when it opens and ensure it's at top position
  useEffect(() => {
    if (hasVisibleChildren && expandedParentItem) {
      // Small delay to ensure panel is rendered
      setTimeout(() => {
        const panelElement = document.getElementById('children-panel-container');
        if (panelElement) {
          // Force panel to be at top of viewport
          panelElement.style.setProperty('top', '0px', 'important');
          panelElement.style.setProperty('position', 'fixed', 'important');
          panelElement.style.setProperty('margin-top', '0px', 'important');
          panelElement.style.setProperty('margin-left', '50px', 'important');
          panelElement.style.setProperty('padding-top', '0px', 'important');
          panelElement.style.setProperty('bottom', 'auto', 'important');
          panelElement.style.setProperty('transform', 'none', 'important');
          
          // Get computed style to verify
          const computedStyle = window.getComputedStyle(panelElement);
          console.log('📍 Panel computed position - top:', computedStyle.top, 'position:', computedStyle.position);
          
          // Scroll panel content to top
          panelElement.scrollTop = 0;
          
          // Also scroll the window to top if needed
          window.scrollTo({ top: 0, behavior: 'instant' });
          
          console.log('✅ Scrolled children panel to top and positioned at top');
        } else {
          console.log('❌ Panel element not found');
        }
      }, 150); // Increased delay to ensure rendering
    }
  }, [hasVisibleChildren, expandedParentItem]);

  // Updated By Madhuri.K On 13-11-2025 - Close children panel when clicking outside of it
  // This allows users to close the panel by clicking anywhere on the UI (not on a page name or sidebar)
  useEffect(() => {
    if (!hasVisibleChildren) {
      return; // Don't add listener if panel is not visible
    }

    const handleClickOutside = (event) => {
      const panelElement = document.getElementById('children-panel-container');
      const sidebarElement = document.querySelector('.pro-sidebar');
      
      // Check if click is inside the children panel (including page names)
      const isClickInsidePanel = panelElement && panelElement.contains(event.target);
      if (isClickInsidePanel) {
        return; // Don't close if clicking inside the panel (including page names)
      }
      
      // Check if click is on the sidebar or navigation menu items
      const isClickOnSidebar = sidebarElement && sidebarElement.contains(event.target);
      const isClickOnMenuItem = event.target.closest('.pro-menu-item') || 
                                event.target.closest('.ps-menu-button-root');
      
      // Updated By Madhuri.K On 13-11-2025 - Close panel when clicking on main content area (UI)
      // Close if click is outside panel and sidebar - this means click is on the main UI/content area
      if (!isClickInsidePanel && !isClickOnSidebar && !isClickOnMenuItem) {
        console.log('🖱️ Click outside children panel (on main UI) - closing panel');
        setHasVisibleChildren(false);
        setExpandedParentItem(null);
        setActiveExpandedParent(null);
        setParentHierarchy([]);
        setChildrenPanelSearchTerm("");
      }
    };

    // Add event listener with a small delay to prevent immediate closure when opening panel
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [hasVisibleChildren]);
  
  /**
   * Theme configuration for light/dark modes
   * Defines colors for different UI elements based on theme
   * Added By Madhuri.K On 04-11-2025 - Set sidebar background to white (#fff) for entire navigation tree section
   */
  const theme = {
    background: "#ffffff", // Main background color - always white - Added By Madhuri.K On 04-11-2025
    color: "#111827", // Primary text color
    sidebarBg: "#ffffff", // Sidebar background - always white (#fff) - Added By Madhuri.K On 04-11-2025
    itemHover: "rgba(59, 130, 246, 0.05)", // Hover background
    itemActive: "#3b82f6", // Active item background (blue)
    textSecondary: "#6b7280", // Secondary text color
    border: "#e5e7eb" // Border color
  };

  // useEffect(() => {
  //   const initializeTeams = async () => {
  //     if (window.microsoftTeams) {
  //       try {
  //         await new Promise((resolve) => {
  //           window?.microsoftTeams?.app?.initialize(() => {
  //             resolve();
  //           });
  //         });
  //         window.microsoftTeams.app.getContext((context) => {
  //           if (context.theme === "dark") {
  //             setTheme({
  //               background: "black",
  //               color: "white" // Text color for dark mode
  //             });
  //           } else {
  //             setTheme({
  //               background: "rgba(241, 241, 241, 255)",
  //               color: "black" // Text color for light mode
  //             });
  //           }
  //         });
  //       } catch (error) {
  //         console.error("Teams SDK initialization failed:", error);
  //       }
  //     } else {
  //       console.error("Teams SDK is not available.");
  //     }
  //   };

  //   initializeTeams();
  // }, []);

  /**
   * Update selected item when route changes
   */
  useEffect(() => {
    // Updated By Madhuri.K On 12-11-2025 - Sync both state and ref with location.pathname
    // This ensures consistency between ref and state when location changes
    setSelectedItem(location.pathname);
    selectedItemRef.current = location.pathname;
    
    // Updated By Madhuri.K - Ensure navigation tree stays expanded on mobile after page selection
    // This prevents the tree from collapsing when a page is selected on mobile view
    // Use setTimeout to ensure this runs after other effects and maintains mobile expanded state
    setTimeout(() => {
      const screenWidth = window.innerWidth;
      if (screenWidth <= 1199) {
        setIsCollapsed(false); // Always keep expanded on mobile
      }
    }, 0);
  }, [location.pathname]);

  /**
   * Handle sidebar collapse/expand based on mode and hover state
   * Updated By Madhuri.K On 06-11-2025 - Remove hover-based expansion, only expand on click
   * Updated By Madhuri.K On 06-11-2025 - Allow manual expansion even in compact mode
   * Updated By Madhuri.K On 06-11-2025 - Don't override isCollapsed when user clicks to expand
   * Updated By Madhuri.K On 14-11-2025 - Keep tree expanded on mobile (≤1199px)
   * Updated By Madhuri.K - Ensure mobile view always stays expanded, regardless of mode
   * Updated By Madhuri.K - Use setTimeout to ensure mobile check runs after initial mount
   */
  useEffect(() => {
    // Updated By Madhuri.K On 14-11-2025 - Check screen width and keep expanded on mobile
    // Updated By Madhuri.K - Always prioritize mobile view - check screen width first
    // Use setTimeout to ensure this runs after initial mount state is set
    setTimeout(() => {
      const screenWidth = window.innerWidth;
      if (screenWidth <= 1199) {
        // On mobile, always keep tree expanded - never collapse
        setIsCollapsed(false);
        return;
      }
      
      // Only apply mode-based logic on desktop (>1199px)
      // Updated By Madhuri.K On 06-11-2025 - Only set to false if mode is "full", but don't force collapse
      // This allows user clicks to control the collapsed state
      if (mode === "full") {
        setIsCollapsed(false);
      }
      // Don't force collapse in compact mode - allow user to expand on click
      // Removed: else if (mode === "compact") setIsCollapsed(true);
      // This was preventing text from showing when user clicked to expand
      // Removed isHovered check - no hover expansion
    }, 0);
  }, [mode]);
  
  // Updated By Madhuri.K On 14-11-2025 - Handle window resize to maintain expanded state on mobile
  // Updated By Madhuri.K - Set initial state IMMEDIATELY on mount with highest priority for mobile
  // This must run FIRST before any other useEffects to ensure mobile view starts expanded
  // Updated By Madhuri.K - Set state synchronously AND asynchronously to ensure it works on first load
  useEffect(() => {
    // Set initial state immediately - check screen width synchronously first
    const screenWidth = window.innerWidth;
    if (screenWidth <= 1199) {
      // On mobile, always keep tree expanded - set immediately
      setIsCollapsed(false);
    } else {
      // On desktop, can be collapsed
      setIsCollapsed(true);
    }
    
    // Also use setTimeout as a safeguard to ensure it runs after render and overrides other effects
    setTimeout(() => {
      const screenWidth = window.innerWidth;
      if (screenWidth <= 1199) {
        // On mobile, always keep tree expanded
        setIsCollapsed(false);
      } else {
        // On desktop, can be collapsed
        setIsCollapsed(true);
      }
    }, 0);
    
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth <= 1199) {
        // On mobile, always keep tree expanded
        setIsCollapsed(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Updated By Madhuri.K - Keep navigation tree expanded when child panel is visible
  // This ensures the tree doesn't collapse when the child panel opens
  // Updated By Madhuri.K On 14-11-2025 - Always keep expanded on mobile
  useEffect(() => {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 1199) {
      // On mobile, always keep tree expanded
      setIsCollapsed(false);
      return;
    }
    
    if (hasVisibleChildren && expandedParentItem) {
      // When child panel is open, ensure tree stays expanded
      setIsCollapsed(false);
      console.log('✅ Child panel open - keeping tree expanded, isCollapsed: false');
    }
  }, [hasVisibleChildren, expandedParentItem]);

  // Added By Madhuri.K On 22-11-2025 - Handle breadcrumb click to open navigation tree and expand any parent
  // This works for all pages with breadcrumb format "Parent > Child" (e.g., Configuration > Corporate Settings)
  useEffect(() => {
    const handleExpandNavigationTree = (event) => {
      console.log('🎯 Event received: expandNavigationTree', event.detail);
      const { parentName } = event.detail || {};
      
      // Added By Madhuri.K On 22-11-2025 - Get breadcrumb context from event detail
      const breadcrumbContext = event.detail.breadcrumbContext;
      
      if (!parentName) {
        console.log('⚠️ No parentName in event detail');
        return;
      }
      
      console.log(`🔍 Processing breadcrumb click for: ${parentName}, context: ${breadcrumbContext}`);
      
      if (parentName) {
        // Added By Madhuri.K On 22-11-2025 - First, ensure sidebar is open/expanded (not in "close" mode)
        const leftSidebar = settings.layout1Settings?.leftSidebar;
        if (leftSidebar?.mode === "close") {
          updateSettings({
            layout1Settings: { 
              leftSidebar: { 
                ...leftSidebar, 
                mode: "full" 
              } 
            }
          });
        }
        
        // Added By Madhuri.K On 22-11-2025 - Find parent item in navigation tree (works for any parent, not just Configuration)
        // Updated By Madhuri.K On 22-11-2025 - Also check tagDescription in case item uses that instead of tagName
        // Updated By Madhuri.K On 22-11-2025 - Added case-insensitive matching as fallback
        const findItemInTree = (itemsArray, targetTagName, level = 0) => {
          if (level > 5) return null;
          
          const targetLower = targetTagName.toLowerCase();
          
          for (const treeItem of itemsArray || []) {
            // Check both tagName and tagDescription for matching (exact match first)
            if (treeItem.tagName === targetTagName || treeItem.tagDescription === targetTagName) {
              return treeItem;
            }
            
            // Added By Madhuri.K On 22-11-2025 - Case-insensitive fallback
            const tagNameLower = treeItem.tagName?.toLowerCase();
            const tagDescLower = treeItem.tagDescription?.toLowerCase();
            if (tagNameLower === targetLower || tagDescLower === targetLower) {
              console.log(`✅ Found ${parentName} via case-insensitive match`);
              return treeItem;
            }
            
            if (treeItem.children && Array.isArray(treeItem.children)) {
              const found = findItemInTree(treeItem.children, targetTagName, level + 1);
              if (found) return found;
            }
          }
          return null;
        };
        
        // Added By Madhuri.K On 22-11-2025 - Find the parent of an item in the tree
        // Updated By Madhuri.K On 22-11-2025 - Also check tagDescription in case item uses that instead of tagName
        const findParentOfItem = (itemsArray, targetTagName, parent = null, level = 0) => {
          if (level > 5) return null;
          
          for (const treeItem of itemsArray || []) {
            // Check both tagName and tagDescription for matching
            if (treeItem.tagName === targetTagName || treeItem.tagDescription === targetTagName) {
              return parent; // Return the parent of the found item
            }
            
            if (treeItem.children && Array.isArray(treeItem.children)) {
              const found = findParentOfItem(treeItem.children, targetTagName, treeItem, level + 1);
              if (found) return found;
            }
          }
          return null;
        };
        
        // Added By Madhuri.K On 22-11-2025 - Use allNavigations if available, otherwise use items
        // Use refs to ensure we have the latest data even if useEffect hasn't re-run
        const currentAllNavigations = allNavigationsRef.current || allNavigations;
        const currentItems = itemsRef.current || items;
        const sourceItems = (currentAllNavigations && currentAllNavigations.length > 0) ? currentAllNavigations : currentItems;
        
        console.log(`🔍 Source items available - allNavigations: ${currentAllNavigations?.length || 0}, items: ${currentItems?.length || 0}`);
        
        // Added By Madhuri.K On 22-11-2025 - Find all matching items (there might be multiple with same name, e.g., Projects under Configuration vs Program)
        const findAllItemsInTree = (itemsArray, targetTagName, level = 0, results = []) => {
          if (level > 5) return results;
          
          for (const treeItem of itemsArray || []) {
            // Check both tagName and tagDescription for matching
            if (treeItem.tagName === targetTagName || treeItem.tagDescription === targetTagName) {
              results.push({ item: treeItem, level });
            }
            
            if (treeItem.children && Array.isArray(treeItem.children)) {
              findAllItemsInTree(treeItem.children, targetTagName, level + 1, results);
            }
          }
          return results;
        };
        
        const allMatchingItems = findAllItemsInTree(sourceItems, parentName);
        console.log(`🔍 Found ${allMatchingItems.length} items matching "${parentName}":`, allMatchingItems.map(r => ({
          tagName: r.item.tagName,
          hasChildren: r.item.children?.length > 0,
          isParent: r.item.isParent,
          parentTagId: r.item.parentTagId,
          level: r.level
        })));
        
        // Added By Madhuri.K On 22-11-2025 - Prefer item with children, or find under Configuration
        // Updated By Madhuri.K On 22-11-2025 - Use breadcrumb context to find correct parent (e.g., Projects under Configuration vs Program)
        let parentItem = null;
        
        // Added By Madhuri.K On 22-11-2025 - PRIORITY 1: If breadcrumb context exists (e.g., "Configuration"), use it to find the correct parent
        // This ensures we get the right Projects (under Configuration) not the wrong one (under Program)
        if (breadcrumbContext) {
          console.log(`🔍 Searching for ${parentName} under ${breadcrumbContext} using breadcrumb context...`);
          const contextItem = findItemInTree(sourceItems, breadcrumbContext);
          console.log(`🔍 Context item found:`, contextItem?.tagName, `has children:`, contextItem?.children?.length);
          
          if (contextItem && contextItem.children) {
            // Look for parentName under the context parent (e.g., Projects under Configuration)
            const itemInContext = contextItem.children.find(child => 
              (child.tagName === parentName || child.tagDescription === parentName) &&
              (child.children && child.children.length > 0 || child.isParent)
            );
            if (itemInContext) {
              parentItem = itemInContext;
              console.log(`✅ Found ${parentName} under ${breadcrumbContext} via breadcrumb context with ${itemInContext.children?.length || 0} children`);
            } else {
              // Try without children check - maybe children are loaded lazily
              const itemInContextAny = contextItem.children.find(child => 
                child.tagName === parentName || child.tagDescription === parentName
              );
              if (itemInContextAny) {
                parentItem = itemInContextAny;
                console.log(`✅ Found ${parentName} under ${breadcrumbContext} via breadcrumb context (may need to load children)`);
              }
            }
          } else {
            console.log(`⚠️ Context item ${breadcrumbContext} not found or has no children`);
          }
        }
        
        // PRIORITY 2: If not found via context, try to find one that has children from all matches
        if (!parentItem && allMatchingItems.length > 0) {
          parentItem = allMatchingItems.find(r => 
            r.item.children && Array.isArray(r.item.children) && r.item.children.length > 0
          )?.item;
          if (parentItem) {
            console.log(`✅ Found ${parentName} with children from all matches`);
          }
        }
        
        // PRIORITY 3: If still not found and parentName is a Configuration node, try to find under Configuration
        if (!parentItem) {
          const parentNameLower = parentName.toLowerCase();
          const isConfigurationNode = parentNameLower === "security" || 
                                    parentNameLower === "ui & navigation" || 
                                    parentNameLower === "ui navigation" ||
                                    parentNameLower === "company information" ||
                                    parentNameLower === "corporate settings" ||
                                    parentNameLower === "projects" ||
                                    parentNameLower === "initiative masters" ||
                                    parentNameLower === "organization" ||
                                    parentNameLower === "business masters" ||
                                    parentNameLower === "checklist" ||
                                    parentNameLower === "general information" ||
                                    parentNameLower === "general" ||
                                    parentNameLower === "risk master";
          
          if (isConfigurationNode) {
            console.log(`🔍 ${parentName} is a Configuration node, searching under Configuration...`);
            const configItem = findItemInTree(sourceItems, "Configuration");
            if (configItem && configItem.children) {
              const itemInConfig = configItem.children.find(child => 
                (child.tagName === parentName || child.tagDescription === parentName) &&
                (child.children && child.children.length > 0 || child.isParent)
              );
              if (itemInConfig) {
                parentItem = itemInConfig;
                console.log(`✅ Found ${parentName} under Configuration with ${itemInConfig.children?.length || 0} children`);
              } else {
                // Try without children check
                const itemInConfigAny = configItem.children.find(child => 
                  child.tagName === parentName || child.tagDescription === parentName
                );
                if (itemInConfigAny) {
                  parentItem = itemInConfigAny;
                  console.log(`✅ Found ${parentName} under Configuration (may need to load children)`);
                }
              }
            }
          }
        }
        
        // PRIORITY 4: If still not found, use the first matching item
        if (!parentItem && allMatchingItems.length > 0) {
          parentItem = allMatchingItems[0].item;
          console.log(`⚠️ Using first matching item as fallback:`, parentItem?.tagName);
        }
        
        console.log(`🔍 Breadcrumb click - parentName: ${parentName}, parentItem found:`, parentItem);
        console.log(`🔍 parentItem children:`, parentItem?.children, `length:`, parentItem?.children?.length);
        console.log(`🔍 parentItem isParent:`, parentItem?.isParent);
        
        // Added By Madhuri.K On 22-11-2025 - Check if item has children or is marked as parent
        // Some items might have isParent=true but children array empty (children loaded lazily)
        // Also check parentTagId - if it exists, the item is likely a child of another parent
        const hasChildren = parentItem && (
          (parentItem.children && Array.isArray(parentItem.children) && parentItem.children.length > 0) ||
          (parentItem.isParent === true) ||
          (parentItem.isParent && parentItem.children && parentItem.children.length > 0)
        );
        
        // Added By Madhuri.K On 22-11-2025 - If item has parentTagId (like Projects under Configuration), 
        // it's likely a parent node even if children array is empty - try to get children from Configuration
        if (parentItem && !hasChildren && parentItem.parentTagId) {
          // Try to find this item in Configuration's children to get the full structure
          const configItem = findItemInTree(sourceItems, "Configuration");
          if (configItem && configItem.children) {
            const itemInConfig = configItem.children.find(child => 
              (child.tagName === parentName || child.tagDescription === parentName) &&
              child.tagId === parentItem.tagId
            );
            if (itemInConfig && itemInConfig.children && itemInConfig.children.length > 0) {
              console.log(`✅ Found ${parentName} with children under Configuration`);
              parentItem = itemInConfig;
            }
          }
        }
        
        // Added By Madhuri.K On 22-11-2025 - Re-check hasChildren after potential update
        const finalHasChildren = parentItem && (
          (parentItem.children && Array.isArray(parentItem.children) && parentItem.children.length > 0) ||
          (parentItem.isParent === true)
        );
        
        if (finalHasChildren || parentItem?.isParent) {
          // Added By Madhuri.K On 22-11-2025 - Find if this parent has a parent (e.g., Security is child of Configuration, General is child of UI & Navigation)
          // Updated By Madhuri.K On 22-11-2025 - For nested nodes like "General", find its immediate parent (UI & Navigation), not top-level (Configuration)
          let grandParent = findParentOfItem(sourceItems, parentName);
          
          // Added By Madhuri.K On 22-11-2025 - Check if this is a nested node (like General under UI & Navigation)
          // For nested nodes, find the immediate parent first, not the top-level Configuration
          const parentNameLower = parentName.toLowerCase();
          const isNestedNode = parentNameLower === "general" || 
                              parentNameLower === "general information";
          
          if (isNestedNode && !grandParent) {
            // For "General", find it under "UI & Navigation"
            const uiNavItem = findItemInTree(sourceItems, "UI & Navigation");
            if (uiNavItem && uiNavItem.children) {
              const itemInUINav = uiNavItem.children.find(child => 
                (child.tagName === parentName || child.tagDescription === parentName) ||
                (child.tagId === parentItem?.tagId)
              );
              if (itemInUINav) {
                grandParent = uiNavItem;
                console.log(`✅ Found grandParent: UI & Navigation for ${parentName} via nested search`);
              }
            }
          }
          
          // Added By Madhuri.K On 22-11-2025 - If Projects is under Configuration, set Configuration as grandParent
          // Check parentTagId first, then try to find in Configuration's children
          if (!grandParent && parentItem?.parentTagId) {
            const configItem = findItemInTree(sourceItems, "Configuration");
            if (configItem && configItem.tagId === parentItem.parentTagId) {
              grandParent = configItem;
              console.log(`✅ Found grandParent: Configuration for ${parentName} via parentTagId`);
            }
          }
          
          // Added By Madhuri.K On 22-11-2025 - If still not found, check if parentItem is directly under Configuration
          if (!grandParent) {
            const configItem = findItemInTree(sourceItems, "Configuration");
            if (configItem && configItem.children) {
              const itemInConfig = configItem.children.find(child => 
                (child.tagName === parentName || child.tagDescription === parentName) ||
                (child.tagId === parentItem?.tagId)
              );
              if (itemInConfig) {
                grandParent = configItem;
                console.log(`✅ Found grandParent: Configuration for ${parentName} via direct search`);
              }
            }
          }
          
          // Added By Madhuri.K On 22-11-2025 - Force navigation tree to be expanded (not collapsed)
          setIsCollapsed(false);
          
          // Added By Madhuri.K On 22-11-2025 - Set up parent hierarchy for back arrow
          // If the clicked parent (e.g., Security, Projects) has a parent (e.g., Configuration), include it in hierarchy
          // Updated By Madhuri.K On 22-11-2025 - When breadcrumbContext is "Configuration", ensure we use Configuration, not Program
          let hierarchy = grandParent ? [grandParent] : [];
          
          // Updated By Madhuri.K On 22-11-2025 - If breadcrumbContext is "Configuration", ensure hierarchy uses Configuration
          // This prevents back button from going to Program when it should go to Configuration
          // BUT: Don't override hierarchy for nested nodes like "General" (they should have "UI & Navigation" in hierarchy)
          if (breadcrumbContext?.toLowerCase() === "configuration") {
            const parentNameLower = parentName.toLowerCase();
            const isNestedNode = parentNameLower === "general" || 
                                parentNameLower === "general information";
            
            // For nested nodes like "General", keep the existing hierarchy (UI & Navigation)
            // Don't override with Configuration
            if (!isNestedNode) {
              const configItem = findItemInTree(sourceItems, "Configuration");
              if (configItem) {
                // Verify that the parentItem is actually under Configuration
                if (configItem.children) {
                  const itemInConfig = configItem.children.find(child => 
                    (child.tagName === parentName || child.tagDescription === parentName) ||
                    (child.tagId === parentItem?.tagId)
                  );
                  if (itemInConfig) {
                    hierarchy = [configItem];
                    console.log(`✅ Set hierarchy to Configuration for ${parentName} (breadcrumbContext is Configuration)`);
                  } else {
                    console.log(`⚠️ ${parentName} not found under Configuration despite breadcrumbContext`);
                  }
                }
              }
            } else {
              console.log(`ℹ️ Keeping existing hierarchy for nested node ${parentName} (will show UI & Navigation >> General)`);
            }
          } else {
            // Added By Madhuri.K On 22-11-2025 - If Projects (or other Configuration nodes) has parentTagId pointing to Configuration, add Configuration to hierarchy
            // Updated By Madhuri.K On 22-11-2025 - Also check if parentName is a Configuration node and add Configuration to hierarchy
            if (hierarchy.length === 0) {
              // First check parentTagId
              if (parentItem?.parentTagId) {
                const configItem = findItemInTree(sourceItems, "Configuration");
                if (configItem && configItem.tagId === parentItem.parentTagId) {
                  hierarchy = [configItem];
                  console.log(`✅ Added Configuration to hierarchy for ${parentName} via parentTagId`);
                }
              }
              
              // If still no hierarchy, check if this is a known Configuration node
              if (hierarchy.length === 0) {
                const parentNameLower = parentName.toLowerCase();
                const isConfigurationNode = parentNameLower === "security" || 
                                          parentNameLower === "ui & navigation" || 
                                          parentNameLower === "ui navigation" ||
                                          parentNameLower === "company information" ||
                                          parentNameLower === "corporate settings" ||
                                          parentNameLower === "projects" ||
                                          parentNameLower === "initiative masters" ||
                                          parentNameLower === "organization" ||
                                          parentNameLower === "business masters" ||
                                          parentNameLower === "checklist" ||
                                          parentNameLower === "general information" ||
                                          parentNameLower === "general" ||
                                          parentNameLower === "risk master";
                
                if (isConfigurationNode) {
                  const configItem = findItemInTree(sourceItems, "Configuration");
                  if (configItem) {
                    hierarchy = [configItem];
                    console.log(`✅ Added Configuration to hierarchy for ${parentName} (Configuration node)`);
                  }
                }
              }
            }
          }
          
          // Updated By Madhuri.K On 22-11-2025 - Keep Configuration in hierarchy for back button functionality
          // The header display logic will prevent showing "Configuration >> Projects" format
          // But we need hierarchy to have Configuration so back button can navigate back
          // Header logic at line ~5574 checks: hierarchy has Configuration -> don't show breadcrumb format
          // So hierarchy should have Configuration, but header will show just node name
          
          // Added By Madhuri.K On 22-11-2025 - Create a new object reference to ensure React detects the change
          const parentItemCopy = { ...parentItem };
          if (parentItem.children && Array.isArray(parentItem.children)) {
            parentItemCopy.children = [...parentItem.children];
          } else if (parentItem.isParent) {
            // Added By Madhuri.K On 22-11-2025 - If isParent is true but children is empty, initialize empty array
            // The panel will handle loading children if needed
            parentItemCopy.children = [];
          }
          
          // Added By Madhuri.K On 22-11-2025 - Generate itemId - format: tagName-index-level
          // Since we don't have index from breadcrumb, use tagId or create unique ID
          const itemId = `${parentName}-${parentItem.tagId || 0}-0`;
          
          console.log(`🔍 Setting up panel for ${parentName}:`, {
            itemId,
            hasChildren: parentItemCopy.children?.length,
            parentItemCopy
          });
          
          // Added By Madhuri.K On 22-11-2025 - Set all states synchronously in correct order
          // Set expandedItems first
          setExpandedItems(prev => {
            const updated = { ...prev, [itemId]: true };
            console.log('🔍 Updated expandedItems:', updated);
            return updated;
          });
          
          // Set expandedParentItem and hasVisibleChildren together (these trigger the panel render)
          setExpandedParentItem(parentItemCopy);
          setHasVisibleChildren(true);
          
          // Set other states
          setActiveExpandedParent(itemId);
          setParentHierarchy(hierarchy);
          setSelectedModuleName(parentItem.tagName || "Modules");
          
          console.log(`✅ ${parentName} expanded from breadcrumb click - navigation tree opened`);
          console.log(`✅ State set - itemId: ${itemId}, hasVisibleChildren: true, expandedParentItem:`, parentItemCopy.tagName);
          console.log(`✅ Hierarchy:`, hierarchy.map(h => h.tagName));
          console.log(`✅ Children count:`, parentItemCopy.children?.length);
        } else {
          console.log(`⚠️ ${parentName} not found or has no children - cannot open child panel`);
          console.log(`⚠️ parentItem:`, parentItem);
        }
      }
    };
    
    // Added By Madhuri.K On 22-11-2025 - Add event listener for breadcrumb clicks
    // Use refs in the handler so we don't need to recreate the listener when items change
    window.addEventListener('expandNavigationTree', handleExpandNavigationTree);
    console.log('✅ Event listener added for expandNavigationTree');
    
    // Cleanup
    return () => {
      window.removeEventListener('expandNavigationTree', handleExpandNavigationTree);
      console.log('🧹 Event listener removed for expandNavigationTree');
    };
  }, [settings, updateSettings]); // Removed items and allNavigations from dependencies - using refs instead

  /**
   * Filter navigation items based on search term
   * Updated By Madhuri.K On 12-11-2025 - Use allNavigations for complete tree search to find pages in nested parent/children
   * Enhanced to search recursively through all nested levels (parent > sub-parent > page)
   * When a page matches inside a sub-node, the parent node is also included
   * 
   * @param {string} term - Search term to filter by
   */
  const handleSearchFilter = (term) => {
    // Updated By Madhuri.K On 18-11-2025 - Trim search term to prevent searching with spaces
    const trimmedTerm = term ? term.trim() : "";
    if (!trimmedTerm || trimmedTerm === "") {
      setFilteredItems(items);
      return;
    }

    const searchTerm = trimmedTerm.toLowerCase();

    // Helper function to check if an item matches the search term
    const itemMatchesSearch = (item) => {
      const nameMatch = item.tagName && item.tagName.toLowerCase().includes(searchTerm);
      const descMatch = item.tagDescription && item.tagDescription.toLowerCase().includes(searchTerm);
      return nameMatch || descMatch;
    };

    // Recursive function to filter items and their children
    // Updated By Madhuri.K - Exclude parent nodes from search results completely, only show actual pages
    // Flatten the results to show only matching child pages, not parent nodes
    const filterItems = (itemsToFilter) => {
      const results = [];
      
      itemsToFilter.forEach((item) => {
        // Check if this item matches
        const itemMatches = itemMatchesSearch(item);

        // Filter children recursively
        let filteredChildren = [];
        if (item.children && item.children.length > 0) {
          filteredChildren = filterItems(item.children);
        }

        // Check if this is a parent node (has children)
        const isParentNode = item.children && item.children.length > 0;

        // Updated By Madhuri.K - Only include actual pages (items without children) that match the search
        if (itemMatches && !isParentNode) {
          // Item matches and is a page (no children) - include it directly
          results.push({ ...item, children: item.children || [] });
        }

        // Updated By Madhuri.K - If children match, add them directly to results (flatten hierarchy)
        // Don't include the parent node itself - only the matching children
        // This prevents parent nodes like "Projects" from appearing in search results
        if (filteredChildren.length > 0) {
          // Add matching children directly to results, not wrapped in parent
          results.push(...filteredChildren);
        }
      });
      
      return results;
    };

    // Updated By Madhuri.K On 12-11-2025 - Use allNavigations if available for complete tree search
    // This allows searching through all nested parent and children nodes, not just displayed items
    // For example, searching for a page inside Configuration > Parent > Child will find it even if collapsed
    const sourceItems = (allNavigations && allNavigations.length > 0) ? allNavigations : items;
    const filtered = filterItems(sourceItems);
    setFilteredItems(filtered);
  };

  /**
   * Sync search query with prop changes and update filtered items
   * Updated By Madhuri.K On 18-11-2025 - Trim search term to prevent searching with spaces
   */
  useEffect(() => {
    // Updated By Madhuri.K On 18-11-2025 - Trim search term to prevent searching with spaces
    const trimmedSearchTerm = searchTerm ? searchTerm.trim() : "";
    setSearchQuery(trimmedSearchTerm);
    // Update filtered items when search term or items change
    if (!trimmedSearchTerm || trimmedSearchTerm === "") {
      setFilteredItems(items);
    } else {
      handleSearchFilter(trimmedSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, items]);
  
  /**
   * Update filtered items when items prop changes
   */
  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  /**
   * Handle search input change
   * 
   * @param {Event} event - Input change event
   */
  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchQuery(term);
    
    // Call parent search handler if provided
    if (onSearch) {
      onSearch(event);
    } else {
      // Otherwise, filter locally (trimming is handled in handleSearchFilter)
      handleSearchFilter(term);
    }
  };

  /**
   * Toggle expand/collapse state for parent menu items
   * 
   * @param {string} itemId - Unique identifier for the menu item
   */
  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId] // Toggle the expanded state
    }));
  };

  /**
   * Handle navigation item click
   * Added By Madhuri.K On 04-11-2025 - Updated to track selected module/page name and prevent hover expansion
   * 
   * @param {string} path - The page path/name
   * @param {string} tagDescription - The tag description for routing
   * @param {string} parentPath - The parent path for breadcrumb
   * @param {string} tagName - The tag name (page/module name) to display at top
   */
  const handleClick = (path, tagDescription, parentPath, tagName = "") => {
    const formattedPath = `/${path?.replace(".aspx", "")}`;

    // Updated By Madhuri.K On 12-11-2025 - Check if the clicked item is a parent with children
    // Only collapse/hide if it's a leaf node (actual page), not a parent item
    // Updated By Madhuri.K On 12-11-2025 - Also search in expandedParentItem.children and allNavigations
    // This ensures we find the clicked item even when clicking from the children panel
    const findItemInTree = (itemsArray, searchTagName, searchTagDescription) => {
      for (const item of itemsArray || []) {
        if ((item.tagName === searchTagName) || (item.tagDescription === searchTagDescription)) {
          return item;
        }
        if (item.children && item.children.length > 0) {
          const found = findItemInTree(item.children, searchTagName, searchTagDescription);
          if (found) return found;
        }
      }
      return null;
    };
    
    // Updated By Madhuri.K On 12-11-2025 - Search in multiple places to find the clicked item
    // Priority: expandedParentItem.children (most likely when clicking from children panel) > items > allNavigations
    let clickedItem = null;
    if (expandedParentItem && expandedParentItem.children) {
      // First search in expandedParentItem.children (children panel items) - most likely location when clicking from panel
      clickedItem = findItemInTree(expandedParentItem.children, tagName, tagDescription);
    }
    if (!clickedItem) {
      // Then search in main items array
      clickedItem = findItemInTree(items, tagName, tagDescription);
    }
    if (!clickedItem && allNavigations && allNavigations.length > 0) {
      // Finally search in allNavigations (complete navigation tree) as fallback
      clickedItem = findItemInTree(allNavigations, tagName, tagDescription);
    }
    
    const isParentWithChildren = clickedItem && clickedItem.isParent && clickedItem.children && clickedItem.children.length > 0;
    const isSearchResultWrapper = clickedItem && clickedItem.isSearchResultWrapper;
    
    // Updated By Madhuri.K - Prevent parent nodes from navigating, only expand to show children
    // Updated By Madhuri.K On 12-11-2025 - If item is a parent with children, don't navigate, just expand
    if (isParentWithChildren && !isSearchResultWrapper) {
      // This is a parent node - don't navigate, just expand to show children
      console.log('⚠️ Parent node clicked - preventing navigation, only expanding:', tagName || tagDescription);
      // Don't call handleClick navigation logic for parent nodes
      return; // Exit early to prevent navigation
    }
    
    // Updated By Madhuri.K On 12-11-2025 - Only hide children panel and collapse navigation tree for leaf nodes (actual pages)
    // Don't collapse/hide if clicking on a parent item that should show its children
    // Updated By Madhuri.K On 12-11-2025 - If item not found, assume it's a leaf node and close the panel
    // Updated By Madhuri.K - Always close children panel when clicking on any page name
    // This ensures children panel closes when clicking on pages from the children panel
    if (!isParentWithChildren) {
      // This is a leaf node (actual page) or item not found, so collapse and hide
      setHasVisibleChildren(false);
      // Updated By Madhuri.K - Keep expandedParentItem so parent shows as active when child is selected
      // The hasActiveChild function uses expandedParentItem to check if a child is selected
      // Don't clear it so the parent node can show as active/selected
      // setExpandedParentItem(null); // Keep this for active state detection
      setActiveExpandedParent(null);
      setParentHierarchy([]);
      setChildrenPanelSearchTerm("");
      
      // Updated By Madhuri.K - Keep parent expanded in navigation tree so it shows as active
      // Find the parent item ID and keep it expanded
      if (expandedParentItem) {
        // Find the parent item in the navigation tree to get its ID
        const findParentItemId = (itemsArray, targetItem, level = 0, currentIndex = 0) => {
          if (level > 5 || !itemsArray) return null;
          for (let i = 0; i < itemsArray.length; i++) {
            const item = itemsArray[i];
            if ((item.tagName === targetItem.tagName || item.tagDescription === targetItem.tagDescription) &&
                item.tagId === targetItem.tagId) {
              return `${item.tagName}-${i}-${level}`;
            }
            if (item.children && Array.isArray(item.children)) {
              const found = findParentItemId(item.children, targetItem, level + 1, i);
              if (found) return found;
            }
          }
          return null;
        };
        
        const sourceItems = (allNavigations && allNavigations.length > 0) ? allNavigations : items;
        const parentItemId = findParentItemId(sourceItems, expandedParentItem);
        if (parentItemId) {
          // Keep parent expanded so it shows as active
          setExpandedItems(prev => ({ ...prev, [parentItemId]: true }));
          console.log(`✅ Keeping parent ${expandedParentItem.tagName} expanded for active state - itemId: ${parentItemId}`);
        }
      }
      // Updated By Madhuri.K On 14-11-2025 - Only collapse navigation tree on screens > 1199px
      // On mobile (≤1199px), keep tree expanded to maintain proper layout
      // Updated By Madhuri.K - Always keep expanded on mobile, never collapse
      const screenWidth = window.innerWidth;
      if (screenWidth > 1199) {
        setIsCollapsed(true);
        console.log('✅ Closing children panel - clicked item is a leaf node or not found:', tagName || tagDescription, 'Screen width:', screenWidth, '- Collapsing tree');
      } else {
        // Always keep tree expanded on mobile - never collapse
        setIsCollapsed(false);
        console.log('✅ Closing children panel - clicked item is a leaf node or not found:', tagName || tagDescription, 'Screen width:', screenWidth, '- Keeping tree expanded on mobile');
      }
      
      // Updated By Madhuri.K - Additional safeguard: Ensure tree stays expanded on mobile after navigation
      // This prevents any race conditions or state updates from collapsing the tree
      // Use multiple timeouts to ensure it overrides any other effects
      setTimeout(() => {
        const currentScreenWidth = window.innerWidth;
        if (currentScreenWidth <= 1199) {
          setIsCollapsed(false);
        }
      }, 50);
      
      // Additional safeguard with longer delay to ensure it runs after all other effects
      setTimeout(() => {
        const currentScreenWidth = window.innerWidth;
        if (currentScreenWidth <= 1199) {
          setIsCollapsed(false);
        }
      }, 200);
    } else {
      console.log('⚠️ Keeping children panel open - clicked item is a parent with children:', tagName || tagDescription);
    }

    // If clicking the same item, refresh the page instead of navigating
    if (selectedItem === formattedPath) {
      window.location.reload();
      return;
    }

    // Added By Madhuri.K On 04-11-2025 - Update selected module name to display at top
    setSelectedModuleName(tagName || tagDescription || "Modules");

    // Build full navigation path and store in sessionStorage for breadcrumb
    const fullPath = buildFullPath(parentPath, tagDescription);
    sessionStorage.setItem("selectedPath", fullPath);
    console.log("tagDescription", tagDescription);
    
    // Updated By Madhuri.K On 12-11-2025 - Determine actual route path first, then update state and ref
    // This ensures selectedItemRef.current matches the actual navigation path for correct isSelected matching
    let actualRoutePath = formattedPath; // Default to formattedPath if no switch case matches
    
    // Navigate to the appropriate route based on tagDescription
    switch (tagDescription) {
      case "Dashboard":
        actualRoutePath = "/e-dashboard";
        // Updated By Madhuri.K - Ensure mobile tree stays expanded when navigating to Dashboard
        const screenWidth = window.innerWidth;
        if (screenWidth <= 1199) {
          setIsCollapsed(false);
        }
        navigate(actualRoutePath);
        // Additional safeguard after navigation
        setTimeout(() => {
          if (window.innerWidth <= 1199) {
            setIsCollapsed(false);
          }
        }, 0);
        break;
        case "Initiatives":
          actualRoutePath = "/InitiativeManagement";
          navigate(actualRoutePath);
          break;
      case "Warehouse":
        actualRoutePath = "/Warehouse";
        navigate(actualRoutePath);
        break;
      case "Completed Initiatives":
        actualRoutePath = "/CompletedInitiativesList";
        navigate(actualRoutePath);
        break;
      case "Withdrawn Initiatives":
        actualRoutePath = "/WithdrawnInitiatives";
        navigate(actualRoutePath);
        break;
      case "Action Items":
        actualRoutePath = "/ActionItems";
        navigate(actualRoutePath);
        break;
      case "Converted Initiatives":
        actualRoutePath = "/ConvertedInitiatives";
        navigate(actualRoutePath);
        break;
      case "Initiative Reallocation":
        actualRoutePath = "/Reallocation";
        navigate(actualRoutePath);
        break;
      case "External Audit":
        actualRoutePath = "/ExternalAudit";
        navigate(actualRoutePath);
        break;
      case "Initiative Status Management":
        actualRoutePath = "/InitiativeStatusManagement";
        navigate(actualRoutePath);
        break;
      case "Watch List Configuration":
        actualRoutePath = "/WatchListConfiguration";
        navigate(actualRoutePath);
        break;
      case "Initiative Prioritization":
        actualRoutePath = "/InitiativePrioritization";
        navigate(actualRoutePath);
        break;
      case "Initiative Progress":
        actualRoutePath = "/InitiativeProgress";
        navigate(actualRoutePath);
        break;
      // Added By Madhuri.K On 10-06-2025
      case "Initiative Conversion":
        actualRoutePath = "/InitiativeConversion";
        navigate(actualRoutePath);
        break;
      case "Initiative Linking":
        actualRoutePath = "/InitiativeLinking";
        navigate(actualRoutePath);
        break;
      case "Man-Com Prioritization":
        actualRoutePath = "/ManComPrioritization";
        navigate(actualRoutePath);
        break;
      case "Currency":
        actualRoutePath = "/currency";
        navigate(actualRoutePath);
        break;
      // Updated By Madhuri.K - Added "Projects" case to handle navigation when clicked from Program module
      // Note: If "Projects" is a parent node with children, the early return above will prevent navigation
      // This case handles when "Projects" is clicked as a page (not a parent) or from Program module
      case "Projects":
        actualRoutePath = "/projectlist";
        navigate(actualRoutePath);
        break;
      case "Program":
        actualRoutePath = "/programlist";
        navigate(actualRoutePath);
        break;
      case "Initiative Report":
        navigate("/InitiativeReport");
        break;
      case "Action Items Report":
        navigate("/ActionItemsReport");
        break;
      case "Change Nature Of Initiative Report":
        navigate("/ChangeNOIReport");
        break;
      case "Delayed Initiatives":
        navigate("/DelayedInitiativeReport");
        break;
      case "Delivery Calendar":
        navigate("/DeliveryCalendarReport");
        break;
      case "Ageing Report":
        navigate("/AgeingReport");
        break;
      case "Program Tracking Report":
        navigate("/ProgramTrackingReport");
        break;
      case "Initiative Activate - Snooze":
        navigate("/Snooze");
        break;
      case "Initiative Dashboard":
        navigate("/InitiativeDashboard");
        break;
      // Added By Madhuri.K On 01-09-2025
      case "Project Status":
        navigate("/ProjectStatus");
        break;
      case "Project Group":
        navigate("/ProjectGroup");
        break;
      case "Project Type":
        navigate("/ProjectType");
        break;
      case "Project Size Units":
        navigate("/ProjectSizeUnits");
        break;
      case "Cost Category":
        navigate("/CostCategory");
        break;
      case "Priority":
        navigate("/Priority");
        break;
      case "Business  Feasibility":
        navigate("/BusinessFeasibility");
        break;
      case "Business  Impact":
        navigate("/BusinessImpact");
        break;
      case "Business Value":
        navigate("/BusinessValue");
        break;
      case "Risk Categories":
        navigate("/RiskCategories");
        break;
      case "Business Strategy":
        navigate("/BusinessStrategy");
        break;
      case "Skills":
        navigate("/Skills");
        break;
      case "Audit Type Definition":
        navigate("/AuditTypeDefinition");
        break;
      case "Action Status":
        navigate("/ActionStatus");
        break;
      case "Action Priority":
        navigate("/ActionPriority");
        break;
      case "Department Master":
        navigate("/DepartmentMaster");
        break;
      case "Initiative Category":
        navigate("/InitiativeCategory");
        break;
      case "Resource Approach":
        navigate("/ResourceApproach");
        break;
      case "Country":
        navigate("/Country");
        break;
      case "Initiative Relation Type Master":
        navigate("/InitiativeRelationTypeMaster");
        break;
      case "Initiative Stage":
        navigate("/InitiativeStage");
        break;
      case "Checklist Item":
        navigate("/ChecklistItem");
        break;
      case "Checklist Section":
        navigate("/ChecklistSection");
        break;
      case "Practice":
        navigate("/Practice");
        break;
      case "Unlock User":
        navigate("/UnlockUser");
        break;
      case "Documents Category":
        navigate("/DocumentsCategory");
        break;
      case "Checklist Maker":
        navigate("/ChecklistMaker");
        break;
      case "Company Information":
        navigate("/CompanyInformation");
        break;
      case "Corporate Settings":
        navigate("/CorporateSettings");
        break;
      case "User Groups":
        navigate("/UserGroup");
        break;
      case "Project Health Sheet Approval":
        navigate("/ProjectHealthSheetApproval");
        break;
      case "Initiative Access":
        navigate("/InitiativeAccess");
        break;
      case "Vendor":
        navigate("/Vendor");
        break;
      case "Delivery Team":
        navigate("/DeliveryTeam");
        break;
      case "User Login Maintenance":
        navigate("/UserLoginMaintenance");
        break;
      case "Vendor Login Maintenance":
        navigate("/VendorLoginMaintenance");
        break;
      case "Process Information":
        navigate("/ProcessInformation");
        break;
      case "Update Actual Cost":
        navigate("/UpdateActualCost");
        break;
      case "Risk Matrix Definition":
        navigate("/RiskMatrixDefinition");
        break;
      case "Initiative Corporate Fields":
        navigate("/InitiativeCorporateFields");
        break;
      case "Role Maintenance":
        navigate("/Role_Maintenance");
        break;
      case "User Maintenance":
        navigate("/UserMaintenance");
        break;
      case "Prioritization Checklist":
        navigate("/PrioritizationChecklist");
        break;
      // Added by Gauri on 01-09-2025
      case "System Email Messages Master":
        navigate("/SystemEmailMessageMaster");
        break;
      case "Email Messages":
        navigate("/FrameworkEmailMessage");
        break;
        // Added by Madhuri.K on 24-11-2025
      case "Analytics Dashboard":
        navigate("/AnalyticsDashboard");
        break;
      case "Node Maintenance":
        navigate("/NodeMaintenance");
        break;
      case "Role Access":
        navigate("/RoleAccess");
        break;
      case "User Group Access":
        navigate("/UserGroupAccess");
        break;
      case "Password Management":
        navigate("/PasswordManagement");
        break;
      case "Nature of Initiative":
        navigate("/NatureOfInitiative");
        break;
      case "Initiative CRESS Ranges":
        navigate("/InitiativeCressRanges");
        break;
      case "Organization Structure":
        navigate("/OrganizationStructure");
        break;
      case "Business Group":
        navigate("/BusinessGroup");
        break;
      case "Organization Unit":
        navigate("/OrganizationUnit");
        break;
      case "Delivery Unit":
        navigate("/DeliveryUnit");
        break;
      case "Checklist Maker Inbox":
        navigate("/ChecklistMakerInbox");
        break;
      // End of Added by Gauri on 01-09-2025
      default:
        actualRoutePath = "/under-construction";
        navigate(actualRoutePath);
        break;
    }
    
    // Updated By Madhuri.K On 12-11-2025 - Update selectedItemRef and state to actual route path
    // This ensures isSelected works correctly by matching the actual navigation path
    // This is critical for cases where tagDescription maps to a different route (e.g., "Initiative" -> "/InitiativeManagement")
    selectedItemRef.current = actualRoutePath;
    setSelectedItem(actualRoutePath);
    console.log(`✅ Updated selectedItemRef to actual route: ${actualRoutePath}`);
    
    // Updated By Madhuri.K - Final safeguard: Ensure mobile tree stays expanded after navigation completes
    // This runs after all state updates to ensure mobile view remains expanded
    setTimeout(() => {
      const currentScreenWidth = window.innerWidth;
      if (currentScreenWidth <= 1199) {
        setIsCollapsed(false);
      }
    }, 300);
  };

  /**
   * Check if a navigation item is currently selected
   * Updated By Madhuri.K On 12-11-2025 - Improved path matching to handle cases where pageName might be missing
   * 
   * @param {Object} item - The navigation item to check
   * @returns {boolean} - True if the item is selected
   */
  // Updated By Madhuri.K On 20-11-2025 - Added parentPath and parentName parameters to distinguish between Projects in Program vs Configuration
  // This fixes the issue where selecting Projects page in Program Module was also selecting Projects Node in Configuration module
  const isSelected = (item, parentPath = "", parentName = "") => {
    if (!item) return false;
    
    // Normalize paths for comparison (remove leading/trailing slashes, remove .aspx, remove spaces and special chars, convert to lowercase)
    // Updated By Madhuri.K - Remove spaces and special characters to handle cases like "Company Information" matching "/CompanyInformation"
    // and "UI & Navigation" matching routes like "/UINavigation"
    const normalizePath = (path) => {
      if (!path) return "";
      return path
        .replace(/\.aspx$/i, "") // Remove .aspx extension (case-insensitive)
        .replace(/^\/+|\/+$/g, "") // Remove leading/trailing slashes
        .replace(/\s+/g, "") // Remove all spaces - Updated By Madhuri.K - Handle "Company Information" -> "CompanyInformation"
        .replace(/&/g, "") // Remove & character - Updated By Madhuri.K - Handle "UI & Navigation" -> "UINavigation"
        .toLowerCase(); // Convert to lowercase for case-insensitive comparison
    };
    
    // Updated By Madhuri.K On 12-11-2025 - Use location.pathname as PRIMARY source (most current/accurate)
    // This ensures only the actual current page is selected, preventing multiple items from showing as selected
    // Priority: location.pathname (most current) > selectedItemRef.current (immediate) > selectedItem (fallback)
    // Updated By Madhuri.K On 12-11-2025 - Always prefer location.pathname to prevent stale state issues
    const currentPath = normalizePath(location.pathname || selectedItemRef.current || selectedItem);
    
    // Updated By Madhuri.K - Prevent "Program" from matching when "Project" is selected and vice versa
    // This is a critical check to ensure exact route matching
    if (!currentPath) return false;
    
    // Updated By Madhuri.K On 20-11-2025 - Check if item is under Configuration module
    // This prevents "Projects" under Configuration from matching "/projectlist" route
    // Only "Projects" under Program module should match "/projectlist", not "Projects" under Configuration
    // Configuration > Projects is a Node (parent with children), Program > Projects is a direct page
    const isUnderConfiguration = parentName === "Configuration" || 
                                 (parentPath && parentPath.toLowerCase().includes("configuration"));
    
    // Updated By Madhuri.K On 12-11-2025 - Define route mappings first to check against
    // This ensures we check route mappings before checking itemPaths to prevent false matches
    const routeMapping = {
      "Dashboard": "/e-dashboard", // Added By Madhuri.K - Handle Dashboard route mapping
      "Initiative": "/InitiativeManagement",
      "Initiatives": "/InitiativeManagement", // Updated By Madhuri.K On 21-11-2025 - Added Initiatives route mapping
      "Program": "/programlist",
      "Projects": "/projectlist",
      "Project": "/projectlist", // Added By Madhuri.K On 12-11-2025 - Handle "Project" parent node
      "Completed Initiatives": "/CompletedInitiativesList",
      "Withdrawn Initiatives": "/WithdrawnInitiatives",
      "Action Items": "/ActionItems",
      "Converted Initiatives": "/ConvertedInitiatives",
      "Initiative Reallocation": "/Reallocation",
      "External Audit": "/ExternalAudit",
      "Initiative Status Management": "/InitiativeStatusManagement",
      "Watch List Configuration": "/WatchListConfiguration",
      "Initiative Prioritization": "/InitiativePrioritization",
      "Initiative Progress": "/InitiativeProgress",
      "Initiative Conversion": "/InitiativeConversion",
      "Initiative Linking": "/InitiativeLinking",
      "Man-Com Prioritization": "/ManComPrioritization",
      "Currency": "/currency",
      "Initiative Report": "/InitiativeReport",
      "Action Items Report": "/ActionItemsReport",
      "Change Nature Of Initiative Report": "/ChangeNOIReport",
      "Delayed Initiatives": "/DelayedInitiativeReport",
      "Delivery Calendar": "/DeliveryCalendarReport",
      "Ageing Report": "/AgeingReport",
      "Program Tracking Report": "/ProgramTrackingReport",
      "Initiative Activate - Snooze": "/Snooze",
      "Initiative Dashboard": "/InitiativeDashboard",
      "Company Information": "/CompanyInformation", // Added By Madhuri.K - Handle "Company Information" parent node page
      "Corporate Settings": "/CorporateSettings", // Added By Madhuri.K - Handle "Corporate Settings" parent node page
      // Configuration Module Pages - Added By Madhuri.K - Ensure all Configuration pages show active state correctly
      "Project Status": "/ProjectStatus",
      "Project Group": "/ProjectGroup",
      "Project Type": "/ProjectType",
      "Project Size Units": "/ProjectSizeUnits",
      "Cost Category": "/CostCategory",
      "Priority": "/Priority",
      "Business  Feasibility": "/BusinessFeasibility",
      "Business Feasibility": "/BusinessFeasibility", // Handle variant without double space
      "Business  Impact": "/BusinessImpact",
      "Business Impact": "/BusinessImpact", // Handle variant without double space
      "Business Value": "/BusinessValue",
      "Risk Categories": "/RiskCategories",
      "Business Strategy": "/BusinessStrategy",
      "Skills": "/Skills",
      "Audit Type Definition": "/AuditTypeDefinition",
      "Action Status": "/ActionStatus",
      "Action Priority": "/ActionPriority",
      "Department Master": "/DepartmentMaster",
      "Initiative Category": "/InitiativeCategory",
      "Resource Approach": "/ResourceApproach",
      "Country": "/Country",
      "Initiative Relation Type Master": "/InitiativeRelationTypeMaster",
      "Initiative Stage": "/InitiativeStage",
      "Checklist Item": "/ChecklistItem",
      "Checklist Section": "/ChecklistSection",
      "Practice": "/Practice",
      "Unlock User": "/UnlockUser",
      "Documents Category": "/DocumentsCategory",
      "Checklist Maker": "/ChecklistMaker",
      "User Groups": "/UserGroup",
      "Project Health Sheet Approval": "/ProjectHealthSheetApproval",
      "Initiative Access": "/InitiativeAccess",
      "Vendor": "/Vendor",
      "Delivery Team": "/DeliveryTeam",
      "User Login Maintenance": "/UserLoginMaintenance",
      "Vendor Login Maintenance": "/VendorLoginMaintenance",
      "Process Information": "/ProcessInformation",
      "Update Actual Cost": "/UpdateActualCost",
      "Risk Matrix Definition": "/RiskMatrixDefinition",
      "Initiative Corporate Fields": "/InitiativeCorporateFields",
      "Role Maintenance": "/Role_Maintenance",
      "User Maintenance": "/UserMaintenance",
      "Prioritization Checklist": "/PrioritizationChecklist",
      "System Email Messages Master": "/SystemEmailMessageMaster",
      "Framework Email Messages": "/FrameworkEmailMessage",
      "Email Messages": "/FrameworkEmailMessage", // Added By Madhuri.K - Map "Email Messages" to FrameworkEmailMessage route
      "Node Maintenance": "/NodeMaintenance",
      "Role Access": "/RoleAccess",
      "User Group Access": "/UserGroupAccess",
      "Password Management": "/PasswordManagement",
      "Nature of Initiative": "/NatureOfInitiative",
      "Initiative CRESS Ranges": "/InitiativeCressRanges",
      "Organization Structure": "/OrganizationStructure",
      "Business Group": "/BusinessGroup",
      "Organization Unit": "/OrganizationUnit",
      "Delivery Unit": "/DeliveryUnit",
      "Checklist Maker Inbox": "/ChecklistMakerInbox"
    };
    
    // Updated By Madhuri.K On 12-11-2025 - Check route mappings FIRST (before itemPaths)
    // This ensures items with route mappings are matched correctly and prevents false matches
    // Route mappings take priority because they're explicit mappings
    // Updated By Madhuri.K On 12-11-2025 - Check ALL possible fields (tagDescription, tagName, pageName) for route mappings
    // This prevents items from matching incorrectly when they have similar names
    // Updated By Madhuri.K On 12-11-2025 - Check if ANY of the item's fields match a route mapping key
    const itemFields = [item.tagDescription, item.tagName, item.pageName].filter(Boolean);
    const hasRouteMapping = itemFields.some(field => routeMapping[field]);
    
    if (hasRouteMapping) {
      // Check all fields that might have route mappings
      // Updated By Madhuri.K - Ensure exact route matching to prevent "Program" from matching when "Project" is selected
      for (const field of itemFields) {
        if (routeMapping[field]) {
          const mappedRoute = normalizePath(routeMapping[field]);
          // CRITICAL: Only return true if the mapped route EXACTLY matches the current path
          // This prevents "Program" (/programlist) from matching when "Project" (/projectlist) is selected
          // Updated By Madhuri.K On 20-11-2025 - Also check if "Projects" is under Configuration - if so, don't match "/projectlist"
          // Configuration > Projects is a Node (parent with children), not a direct page
          // Only Program > Projects (direct page) should match "/projectlist"
          if (mappedRoute === currentPath) {
            // Updated By Madhuri.K On 20-11-2025 - Special check: If this is "Projects" mapping to "/projectlist"
            // 1. If it's under Configuration, don't match (Configuration > Projects is a Node, not a page)
            // 2. If it's a parent node with children, don't match (parent nodes don't navigate directly)
            const isParentNode = item.isParent && item.children && item.children.length > 0;
            if ((field === "Projects" || field === "Project") && mappedRoute === "projectlist") {
              if (isUnderConfiguration || isParentNode) {
                return false; // Don't match Projects Node under Configuration or any parent Projects node
              }
            }
            return true; // Exact match found via route mapping (this is a direct page, not a parent node)
          }
        }
      }
      // If route mapping exists but doesn't match, don't check itemPaths (prevents false matches)
      // Updated By Madhuri.K On 12-11-2025 - CRITICAL: Return false immediately if route mapping doesn't match
      // This prevents items with route mappings (like "Program" and "Projects") from matching via itemPaths
      // This also prevents "Program" from being selected when "Project" is selected
      return false;
    }
    
    // Updated By Madhuri.K On 12-11-2025 - Only check itemPaths if NO route mapping exists
    // Items with route mappings can ONLY match via their route mapping, never via itemPaths
    // This ensures "Program" and "Projects" can't both match the same path
    // Updated By Madhuri.K On 12-11-2025 - CRITICAL: If currentPath is a route mapping route, only allow exact route mapping matches
    // This prevents "Projects" from matching "/programlist" when "Program" is selected
    const allRouteMappingRoutes = Object.values(routeMapping).map(route => normalizePath(route));
    
    // Updated By Madhuri.K On 18-11-2025 - If currentPath is a route mapping route, only allow exact route mapping matches
    // This prevents items without matching route mappings from being selected
    if (allRouteMappingRoutes.includes(currentPath)) {
      // Current path is a route mapping route - only route mappings should match, not itemPaths
      // This prevents "Program" from matching "/projectlist" via itemPaths when "Projects" is selected
      // Updated By Madhuri.K On 18-11-2025 - If item has a route mapping but it doesn't match, return false immediately
      // This ensures only the exact matching item is selected (e.g., only "Projects" when on /projectlist, not "Program")
      if (hasRouteMapping) {
        // Item has route mapping but it didn't match above, so return false
        return false;
      }
      // If item doesn't have route mapping and currentPath is a route mapping route, don't allow itemPaths matching
      return false;
    }
    
    // Only check itemPaths if currentPath is NOT a route mapping route
    const itemPaths = [
      item.pageName,
      item.tagDescription,
      item.tagName
    ].filter(Boolean); // Remove null/undefined values
    
    // Check if any of the item's paths match the current path
    // Updated By Madhuri.K - Ensure exact matching to prevent "Program" from matching "Project" routes
    // Updated By Madhuri.K On 18-11-2025 - Only match via itemPaths if item doesn't have a route mapping
    for (const itemPath of itemPaths) {
      const formattedPath = normalizePath(itemPath);
      // CRITICAL: Only match if paths are EXACTLY equal (case-insensitive after normalization)
      // This prevents "Program" from matching "/projectlist" or "Project" from matching "/programlist"
      if (currentPath === formattedPath) {
        // Additional safety check: If item has a route mapping, it should have been handled above
        // This prevents items with route mappings from matching via itemPaths (double-check)
        // Updated By Madhuri.K On 18-11-2025 - Only return true if item doesn't have route mapping
        if (!hasRouteMapping) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Updated By Madhuri.K On 12-11-2025 - Check if any child of a parent is active/selected
  // This allows parent items to show as active when any of their children are selected
  // Updated By Madhuri.K On 12-11-2025 - Search complete navigation tree (allNavigations) to find selected page
  // Updated By Madhuri.K On 12-11-2025 - Only check actual children, not parent nodes (to prevent false matches)
  // Updated By Madhuri.K On 12-11-2025 - Also check expandedParentItem children if it matches the current item
  // Updated By Madhuri.K On 20-11-2025 - Added parentPath and parentName parameters to pass context for isSelected calls
  // This ensures Configuration vs Program Projects are distinguished correctly when checking for active children
  const hasActiveChild = (item, parentPath = "", parentName = "") => {
    if (!item) {
      return false;
    }
    
    // Build parent path for children - Updated By Madhuri.K - Track parent context for Configuration check
    const currentParentPath = parentPath ? `${parentPath} > ${item.tagName || item.tagDescription || ""}` : (item.tagName || item.tagDescription || "");
    const currentParentName = item.tagName || item.tagDescription || parentName;
    
    // Recursive function to check if a child item is selected
    // Updated By Madhuri.K On 12-11-2025 - Use isSelected for consistency, with direct path check as fallback
    // Updated By Madhuri.K On 12-11-2025 - Exclude parent nodes from child checks (only check leaf nodes or actual children)
    // Updated By Madhuri.K - Pass parent context to isSelected to distinguish Configuration vs Program Projects
    const checkIfChildIsSelected = (childItem, childParentPath = currentParentPath, childParentName = currentParentName) => {
      if (!childItem) return false;
      
      // Updated By Madhuri.K On 12-11-2025 - Skip parent nodes - only check actual child pages
      // A parent node should not be considered a "child" of another parent
      // Only check items that are actual pages (not parent nodes with children)
      const isParentNode = childItem.isParent && childItem.children && childItem.children.length > 0;
      if (isParentNode) {
        // If it's a parent node, only check its children recursively, but don't consider the parent itself as a child
        // Updated By Madhuri.K - Build parent path for nested children
        const nestedParentPath = childParentPath ? `${childParentPath} > ${childItem.tagName || childItem.tagDescription || ""}` : (childItem.tagName || childItem.tagDescription || "");
        const nestedParentName = childItem.tagName || childItem.tagDescription || childParentName;
        if (childItem.children && Array.isArray(childItem.children)) {
          for (const nestedChild of childItem.children) {
            if (checkIfChildIsSelected(nestedChild, nestedParentPath, nestedParentName)) {
              return true;
            }
          }
        }
        return false; // Parent nodes themselves are not considered children
      }
      
      // First check using isSelected function (most reliable) - only for actual child pages
      // Updated By Madhuri.K - Pass parent context to distinguish Configuration vs Program Projects
      if (isSelected(childItem, childParentPath, childParentName)) {
        return true;
      }
      
      // Recursively check nested children (if any)
      // Updated By Madhuri.K - Build parent path for nested children
      const nestedParentPath = childParentPath ? `${childParentPath} > ${childItem.tagName || childItem.tagDescription || ""}` : (childItem.tagName || childItem.tagDescription || "");
      const nestedParentName = childItem.tagName || childItem.tagDescription || childParentName;
      if (childItem.children && Array.isArray(childItem.children)) {
        for (const nestedChild of childItem.children) {
          if (checkIfChildIsSelected(nestedChild, nestedParentPath, nestedParentName)) {
            return true;
          }
        }
      }
      
      return false;
    };
    
    // Updated By Madhuri.K On 12-11-2025 - First check item's direct children
    if (item.children && Array.isArray(item.children)) {
      for (const child of item.children) {
        if (checkIfChildIsSelected(child, currentParentPath, currentParentName)) {
          return true;
        }
      }
    }
    
    // Updated By Madhuri.K On 12-11-2025 - Also check expandedParentItem if it matches the current item
    // This ensures that when a child is selected from the children panel, the parent shows as active
    if (expandedParentItem && 
        (expandedParentItem.tagName === item.tagName || expandedParentItem.tagDescription === item.tagDescription)) {
      if (expandedParentItem.children && Array.isArray(expandedParentItem.children)) {
        for (const child of expandedParentItem.children) {
          if (checkIfChildIsSelected(child, currentParentPath, currentParentName)) {
            return true;
          }
        }
      }
    }
    
    // Updated By Madhuri.K On 12-11-2025 - Also search in allNavigations to find the complete tree structure
    // This ensures we find the selected page even if it's not in the filtered items
    // Updated By Madhuri.K On 12-11-2025 - Use strict matching to prevent false matches (e.g., "Program" matching "Project")
    // Updated By Madhuri.K - Fix: Increase depth limit and ensure we check all nested children
    // This ensures parent nodes like "Security" show as active when their child pages (like "User Maintenance") are selected
    if (allNavigations && allNavigations.length > 0) {
      // Find the parent item in allNavigations by matching tagName (strict match only)
      // Updated By Madhuri.K On 12-11-2025 - Use strict exact matching to prevent false matches
      // This prevents "Program" from matching "Project" or vice versa
      // Updated By Madhuri.K - Increase depth limit to 5 to handle deeply nested structures
      const findParentInTree = (itemsArray, parentTagName, parentTagDescription, level = 0) => {
        for (const treeItem of itemsArray || []) {
          // Strict matching: must match tagName exactly (case-sensitive)
          // Also check tagDescription if provided, but tagName is the primary identifier
          const tagNameMatch = treeItem.tagName === parentTagName;
          const tagDescriptionMatch = !parentTagDescription || !treeItem.tagDescription || treeItem.tagDescription === parentTagDescription;
          
          if (tagNameMatch && tagDescriptionMatch) {
            return treeItem;
          }
          
          // Only search recursively if we haven't found a match yet
          // Updated By Madhuri.K - Increase depth limit to 5 to handle deeply nested navigation structures
          // This ensures we can find parent nodes even if they're nested deeper in the tree
          if (level < 5 && treeItem.children && Array.isArray(treeItem.children)) {
            const found = findParentInTree(treeItem.children, parentTagName, parentTagDescription, level + 1);
            if (found) return found;
          }
        }
        return null;
      };
      
      // Find the parent item in the complete tree using strict matching
      const parentInTree = findParentInTree(allNavigations, item.tagName, item.tagDescription);
      if (parentInTree && parentInTree.children && Array.isArray(parentInTree.children)) {
        // Updated By Madhuri.K - Build parent path for children found in allNavigations
        // Use item's context as parent for its children
        const itemParentPath = parentPath ? `${parentPath} > ${item.tagName || item.tagDescription || ""}` : (item.tagName || item.tagDescription || "");
        const itemParentName = item.tagName || item.tagDescription || parentName;
        for (const child of parentInTree.children) {
          if (checkIfChildIsSelected(child, itemParentPath, itemParentName)) {
            return true;
          }
        }
      }
    }
    
    // Updated By Madhuri.K - Fallback: Check current location pathname directly against all possible child routes
    // This ensures parent nodes show as active even if the navigation tree structure is incomplete
    // For example, if "User Maintenance" (/UserMaintenance) is selected, "Security" should show as active
    // We do this by checking if the current pathname matches any route that could be a child of this item
    const currentPath = location.pathname || selectedItemRef.current || selectedItem;
    if (currentPath) {
      // Normalize path for comparison
      const normalizePath = (path) => {
        if (!path) return "";
        return path
          .replace(/\.aspx$/i, "")
          .replace(/^\/+|\/+$/g, "")
          .replace(/\s+/g, "")
          .replace(/&/g, "")
          .toLowerCase();
      };
      
      const normalizedCurrentPath = normalizePath(currentPath);
      
      // Check if any child route in the routeMapping could belong to this item
      // We check by seeing if the item's tagName/tagDescription matches any parent context
      // This is a fallback when the navigation tree structure doesn't have complete information
      // Updated By Madhuri.K - Also check route mappings for more reliable path matching
      if (allNavigations && allNavigations.length > 0) {
        // Route mappings from isSelected function for accurate path matching
        // This ensures we match paths correctly even when pageName/tagName differ from route
        const routeMapping = {
          "Dashboard": "/e-dashboard", // Added By Madhuri.K - Handle Dashboard route mapping
          "Initiative": "/InitiativeManagement",
          "Initiatives": "/InitiativeManagement", // Updated By Madhuri.K On 21-11-2025 - Added Initiatives route mapping
          "Program": "/programlist",
          "Projects": "/projectlist",
          "Project": "/projectlist",
          "Completed Initiatives": "/CompletedInitiativesList",
          "Withdrawn Initiatives": "/WithdrawnInitiatives",
          "Action Items": "/ActionItems",
          "Converted Initiatives": "/ConvertedInitiatives",
          "Initiative Reallocation": "/Reallocation",
          "External Audit": "/ExternalAudit",
          "Initiative Status Management": "/InitiativeStatusManagement",
          "Watch List Configuration": "/WatchListConfiguration",
          "Initiative Prioritization": "/InitiativePrioritization",
          "Initiative Progress": "/InitiativeProgress",
          "Initiative Conversion": "/InitiativeConversion",
          "Initiative Linking": "/InitiativeLinking",
          "Man-Com Prioritization": "/ManComPrioritization",
          "Currency": "/currency",
          "Initiative Report": "/InitiativeReport",
          "Action Items Report": "/ActionItemsReport",
          "Change Nature Of Initiative Report": "/ChangeNOIReport",
          "Delayed Initiatives": "/DelayedInitiativeReport",
          "Delivery Calendar": "/DeliveryCalendarReport",
          "Ageing Report": "/AgeingReport",
          "Program Tracking Report": "/ProgramTrackingReport",
          "Initiative Activate - Snooze": "/Snooze",
          "Initiative Dashboard": "/InitiativeDashboard",
          "Company Information": "/CompanyInformation",
          "Corporate Settings": "/CorporateSettings",
          "Project Status": "/ProjectStatus",
          "Project Group": "/ProjectGroup",
          "Project Type": "/ProjectType",
          "Project Size Units": "/ProjectSizeUnits",
          "Cost Category": "/CostCategory",
          "Priority": "/Priority",
          "Business  Feasibility": "/BusinessFeasibility",
          "Business Feasibility": "/BusinessFeasibility",
          "Business  Impact": "/BusinessImpact",
          "Business Impact": "/BusinessImpact",
          "Business Value": "/BusinessValue",
          "Risk Categories": "/RiskCategories",
          "Business Strategy": "/BusinessStrategy",
          "Skills": "/Skills",
          "Audit Type Definition": "/AuditTypeDefinition",
          "Action Status": "/ActionStatus",
          "Action Priority": "/ActionPriority",
          "Department Master": "/DepartmentMaster",
          "Initiative Category": "/InitiativeCategory",
          "Resource Approach": "/ResourceApproach",
          "Country": "/Country",
          "Initiative Relation Type Master": "/InitiativeRelationTypeMaster",
          "Initiative Stage": "/InitiativeStage",
          "Checklist Item": "/ChecklistItem",
          "Checklist Section": "/ChecklistSection",
          "Practice": "/Practice",
          "Unlock User": "/UnlockUser",
          "Documents Category": "/DocumentsCategory",
          "Checklist Maker": "/ChecklistMaker",
          "User Groups": "/UserGroup",
          "Project Health Sheet Approval": "/ProjectHealthSheetApproval",
          "Initiative Access": "/InitiativeAccess",
          "Vendor": "/Vendor",
          "Delivery Team": "/DeliveryTeam",
          "User Login Maintenance": "/UserLoginMaintenance",
          "Vendor Login Maintenance": "/VendorLoginMaintenance",
          "Process Information": "/ProcessInformation",
          "Update Actual Cost": "/UpdateActualCost",
          "Risk Matrix Definition": "/RiskMatrixDefinition",
          "Initiative Corporate Fields": "/InitiativeCorporateFields",
          "Role Maintenance": "/Role_Maintenance",
          "User Maintenance": "/UserMaintenance",
          "Prioritization Checklist": "/PrioritizationChecklist",
          "System Email Messages Master": "/SystemEmailMessageMaster",
          "Framework Email Messages": "/FrameworkEmailMessage",
          "Email Messages": "/FrameworkEmailMessage", // Added By Madhuri.K - Map "Email Messages" to FrameworkEmailMessage route
          // "Analytics Dashboard": "/AnalyticsDashboard", // Added by Madhuri.K on 24-11-2025
          "Node Maintenance": "/NodeMaintenance",
          "Role Access": "/RoleAccess",
          "User Group Access": "/UserGroupAccess",
          "Password Management": "/PasswordManagement",
          "Nature of Initiative": "/NatureOfInitiative",
          "Initiative CRESS Ranges": "/InitiativeCressRanges",
          "Organization Structure": "/OrganizationStructure",
          "Business Group": "/BusinessGroup",
          "Organization Unit": "/OrganizationUnit",
          "Delivery Unit": "/DeliveryUnit",
          "Checklist Maker Inbox": "/ChecklistMakerInbox"
        };
        
        // Recursively search for any child that matches the current path
        // Updated By Madhuri.K On 20-11-2025 - Added parentContext to track if we're under Configuration
        // This prevents Configuration module from showing as selected when Projects under Program is selected
        const findChildByPath = (itemsArray, targetPath, level = 0, parentContext = { isUnderConfiguration: false, parentName: "" }) => {
          if (level > 5) return false; // Limit depth
          
          for (const treeItem of itemsArray || []) {
            // Updated By Madhuri.K On 20-11-2025 - Determine if this item is under Configuration
            // This is used to exclude Projects Node under Configuration from matching /projectlist route
            const isItemUnderConfiguration = parentContext.isUnderConfiguration || 
                                            treeItem.tagName === "Configuration" ||
                                            (parentContext.parentName && parentContext.parentName === "Configuration");
            
            // Updated By Madhuri.K On 20-11-2025 - Build current context for children
            const currentContext = {
              isUnderConfiguration: isItemUnderConfiguration,
              parentName: treeItem.tagName || treeItem.tagDescription || parentContext.parentName
            };
            
            // Check if this item matches the current path
            if (treeItem.pageName || treeItem.tagName || treeItem.tagDescription) {
              const itemPaths = [
                treeItem.pageName,
                treeItem.tagName,
                treeItem.tagDescription
              ].filter(Boolean);
              
              // Check direct paths
              for (const itemPath of itemPaths) {
                const normalizedItemPath = normalizePath(itemPath);
                if (normalizedItemPath === normalizedCurrentPath) {
                  return true; // Found a match
                }
              }
              
              // Also check route mappings
              // Updated By Madhuri.K On 20-11-2025 - Exclude "Projects" Node under Configuration from matching "/projectlist"
              // Configuration > Projects is a Node (parent with children), not a direct page
              // Only Program > Projects (direct page) should match /projectlist route
              for (const itemPath of itemPaths) {
                if (routeMapping[itemPath]) {
                  const mappedRoute = normalizePath(routeMapping[itemPath]);
                  if (mappedRoute === normalizedCurrentPath) {
                    // Updated By Madhuri.K On 20-11-2025 - Special check: If this is "Projects" mapping to "/projectlist"
                    // 1. If it's under Configuration, don't match (Configuration > Projects is a Node)
                    // 2. If it's a parent node with children, don't match (parent nodes don't navigate directly)
                    const isParentNode = treeItem.isParent && treeItem.children && treeItem.children.length > 0;
                    if ((itemPath === "Projects" || itemPath === "Project") && mappedRoute === "projectlist") {
                      if (isItemUnderConfiguration || isParentNode) {
                        continue; // Skip this match - Projects Node should not match /projectlist
                      }
                    }
                    return true; // Found a match via route mapping (this is a direct page)
                  }
                }
              }
            }
            
            // Recursively check children
            if (treeItem.children && Array.isArray(treeItem.children)) {
              if (findChildByPath(treeItem.children, targetPath, level + 1, currentContext)) {
                return true;
              }
            }
          }
          return false;
        };
        
        // First, find the current item in allNavigations
        const findItemInTree = (itemsArray, targetTagName, targetTagDescription, level = 0) => {
          if (level > 5) return null;
          
          for (const treeItem of itemsArray || []) {
            const tagNameMatch = treeItem.tagName === targetTagName;
            const tagDescriptionMatch = !targetTagDescription || !treeItem.tagDescription || treeItem.tagDescription === targetTagDescription;
            
            if (tagNameMatch && tagDescriptionMatch) {
              return treeItem;
            }
            
            if (treeItem.children && Array.isArray(treeItem.children)) {
              const found = findItemInTree(treeItem.children, targetTagName, targetTagDescription, level + 1);
              if (found) return found;
            }
          }
          return null;
        };
        
        // Updated By Madhuri.K On 20-11-2025 - Determine if current item is Configuration or under Configuration
        // Define this outside the if block so it's accessible in all scopes (fixes eslint no-undef errors)
        // This is used to prevent Configuration module from showing as selected when Projects under Program is selected
        const isItemUnderConfiguration = item.tagName === "Configuration" || 
                                        item.tagDescription === "Configuration" ||
                                        (parentName && parentName === "Configuration") ||
                                        (parentPath && parentPath.toLowerCase().includes("configuration"));
        
        const itemInTree = findItemInTree(allNavigations, item.tagName, item.tagDescription);
        if (itemInTree) {
          // Check if any descendant of this item matches the current path
          if (itemInTree.children && Array.isArray(itemInTree.children)) {
            const initialContext = {
              isUnderConfiguration: isItemUnderConfiguration,
              parentName: item.tagName || item.tagDescription || parentName
            };
            if (findChildByPath(itemInTree.children, normalizedCurrentPath, 0, initialContext)) {
              return true;
            }
          }
        }
        
        // Updated By Madhuri.K - Also search for any item in the entire tree that matches current path
        // Then check if it's a descendant of the current item
        // This ensures we find the relationship even if the direct parent-child link isn't clear
        const findItemByPath = (itemsArray, targetPath, level = 0) => {
          if (level > 5) return null;
          
          for (const treeItem of itemsArray || []) {
            // Check if this item matches the current path
            if (treeItem.pageName || treeItem.tagName || treeItem.tagDescription) {
              const itemPaths = [
                treeItem.pageName,
                treeItem.tagName,
                treeItem.tagDescription
              ].filter(Boolean);
              
              // Check direct paths
              for (const itemPath of itemPaths) {
                const normalizedItemPath = normalizePath(itemPath);
                if (normalizedItemPath === normalizedCurrentPath) {
                  return treeItem; // Found matching item
                }
              }
              
              // Also check route mappings
              // Updated By Madhuri.K - Exclude "Projects" under Configuration from matching "/projectlist"
              for (const itemPath of itemPaths) {
                if (routeMapping[itemPath]) {
                  const mappedRoute = normalizePath(routeMapping[itemPath]);
                  if (mappedRoute === normalizedCurrentPath) {
                    // Special check: If this is "Projects" mapping to "/projectlist", check if it's under Configuration
                    // We'll check this later when we verify if it's a descendant of Configuration
                    // For now, return the item and let the descendant check handle the Configuration exclusion
                    return treeItem; // Found matching item via route mapping
                  }
                }
              }
            }
            
            // Recursively check children
            if (treeItem.children && Array.isArray(treeItem.children)) {
              const found = findItemByPath(treeItem.children, targetPath, level + 1);
              if (found) return found;
            }
          }
          return null;
        };
        
        // Find the item that matches the current path
        const matchingItem = findItemByPath(allNavigations, normalizedCurrentPath);
        if (matchingItem) {
          // Now check if this matching item is a descendant of the current item
          // Updated By Madhuri.K - Use more flexible matching to handle case variations and different field names
          const isDescendantOf = (parentItem, childItem, level = 0) => {
            if (level > 5) return false;
            
            if (!parentItem || !parentItem.children || !Array.isArray(parentItem.children)) {
              return false;
            }
            
            // Normalize strings for comparison (case-insensitive, trim whitespace)
            const normalize = (str) => str ? str.toString().toLowerCase().trim() : "";
            
            // Check direct children with flexible matching
            for (const child of parentItem.children) {
              // Check multiple fields for matching
              const childTagName = normalize(child.tagName);
              const childTagDescription = normalize(child.tagDescription);
              const childPageName = normalize(child.pageName);
              
              const matchingTagName = normalize(childItem.tagName);
              const matchingTagDescription = normalize(childItem.tagDescription);
              const matchingPageName = normalize(childItem.pageName);
              
              // Match if any field matches
              if ((childTagName && childTagName === matchingTagName) ||
                  (childTagDescription && childTagDescription === matchingTagDescription) ||
                  (childPageName && childPageName === matchingPageName) ||
                  (childTagName && childTagName === matchingTagDescription) ||
                  (childTagDescription && childTagDescription === matchingTagName)) {
                return true; // Found as direct child
              }
              
              // Recursively check nested children
              if (child.children && Array.isArray(child.children)) {
                if (isDescendantOf(child, childItem, level + 1)) {
                  return true;
                }
              }
            }
            
            return false;
          };
          
          // Check if matching item is a descendant of current item
          // Updated By Madhuri.K On 20-11-2025 - Exclude "Projects" Node under Configuration from matching "/projectlist"
          // This prevents Configuration module from showing as active when Projects under Program is selected
          if (itemInTree && isDescendantOf(itemInTree, matchingItem)) {
            // Updated By Madhuri.K On 20-11-2025 - Special check: If matching item is "Projects" mapping to "/projectlist"
            // 1. If current item is Configuration, don't match (Configuration > Projects is a Node)
            // 2. If matching item is a parent node, don't match (parent nodes don't navigate directly)
            const matchingItemFields = [
              matchingItem.tagName,
              matchingItem.tagDescription,
              matchingItem.pageName
            ].filter(Boolean);
            
            const isProjectsMatchingProjectlist = matchingItemFields.some(field => 
              (field === "Projects" || field === "Project") && 
              routeMapping[field] && 
              normalizePath(routeMapping[field]) === normalizedCurrentPath
            );
            
            const isMatchingItemParentNode = matchingItem.isParent && matchingItem.children && matchingItem.children.length > 0;
            
            if (isProjectsMatchingProjectlist && (isItemUnderConfiguration || isMatchingItemParentNode)) {
              return false; // Don't match Projects Node to /projectlist
            }
            
            return true;
          }
          
          // Updated By Madhuri.K On 20-11-2025 - Also check if matching item's route mapping matches current path
          // This handles cases where the item structure might not be clear but route mapping exists
          if (matchingItem) {
            const matchingItemFields = [
              matchingItem.tagName,
              matchingItem.tagDescription,
              matchingItem.pageName
            ].filter(Boolean);
            
            // Check if any field of matching item has a route mapping that matches current path
            for (const field of matchingItemFields) {
              if (routeMapping[field]) {
                const mappedRoute = normalizePath(routeMapping[field]);
                if (mappedRoute === normalizedCurrentPath) {
                  // Updated By Madhuri.K On 20-11-2025 - Exclude "Projects" Node under Configuration from matching "/projectlist"
                  // 1. If current item is Configuration, don't match (Configuration > Projects is a Node)
                  // 2. If matching item is a parent node, don't match (parent nodes don't navigate directly)
                  const isMatchingItemParentNode = matchingItem.isParent && matchingItem.children && matchingItem.children.length > 0;
                  if ((field === "Projects" || field === "Project") && mappedRoute === "projectlist") {
                    if (isItemUnderConfiguration || isMatchingItemParentNode) {
                      continue; // Skip this match - Projects Node should not match /projectlist
                    }
                  }
                  // Found matching route - now verify it's a child of current item
                  if (itemInTree && isDescendantOf(itemInTree, matchingItem)) {
                    return true;
                  }
                }
              }
            }
          }
        }
      }
    }
    
    return false;
  };

  /**
   * Recursively render navigation menu items using react-pro-sidebar SubMenu and MenuItem
   * 
   * @param {Array} data - Array of navigation items to render
   * @param {string} parentPath - Path of parent item for breadcrumb
   * @param {number} level - Nesting level (0 = root, 1 = first child, etc.)
   * @param {string} parentName - Name of parent item to check if it's Initiative Management
   * @param {boolean} forceShowText - Force show text labels (for children panel)
   * @returns {JSX.Element|null} - Rendered menu items or null if no data
   * 
   * Added By Madhuri.K On 04-11-2025 - Added parentName parameter to support page name based icons for Initiative Management
   * Updated By Madhuri.K On 06-11-2025 - Added forceShowText parameter to always show text in children panel
   * Updated By Madhuri.K On 06-11-2025 - Refactored to use SubMenu and MenuItem from react-pro-sidebar
   */
  const renderMenuItems = (data, parentPath = "", level = 0, parentName = "", forceShowText = false) => {
    // Return null if no valid data
    if (!data || !Array.isArray(data)) {
      console.log('⚠️ renderMenuItems - Invalid data:', data);
      return null;
    }

    console.log('🔍 renderMenuItems - data:', data, 'length:', data.length, 'parentName:', parentName, 'forceShowText:', forceShowText);
    
    const items = data.map((item, index) => {
      // Determine item state
      const itemId = `${item.tagName}-${index}-${level}`; // Unique ID for this item
      const hasChildren = item.isParent && item.children && item.children.length > 0; // Check if item has children
      const isExpanded = expandedItems[itemId] || false; // Whether this parent item is expanded
      // Updated By Madhuri.K On 12-11-2025 - Allow parent items to show as active when selected or when any child is active
      // Updated By Madhuri.K On 18-11-2025 - Highlight parent panel when parent is selected OR when any child page is selected
      // Parent items show active background (#3b82f6) and white icon (#fff) when:
      // 1. The parent itself is selected (e.g., navigating to /programlist highlights parent "Program")
      // 2. Any of its children (including nested children) are selected - highlight parent panel when child is selected
      // Updated By Madhuri.K On 18-11-2025 - Highlight parent panel when any child page is selected
      // This ensures parent "Program" is highlighted when:
      // - Navigating to /programlist (parent itself is selected)
      // - Navigating to /projectlist (child "Projects" is selected) - parent should also be highlighted
      // - Navigating to Project Health Sheet Approval page (child is selected) - parent should also be highlighted
      // Updated By Madhuri.K On 20-11-2025 - Pass parentPath and parentName to distinguish Configuration vs Program Projects
      const hasActiveChildItem = hasChildren && hasActiveChild(item, parentPath, parentName);
      const isActive = isSelected(item, parentPath, parentName) || hasActiveChildItem; // Highlight parent if it's selected OR if any child is selected
      
      // Check if this is a child of Initiative Management module
      // Added By Madhuri.K On 04-11-2025 - Use page name based icon for Initiative Management child pages
      const isInitiativeManagementChild = parentName === "Initiative Management" || 
                                         item.tagDescription === "Initiative" ||
                                         parentPath.toLowerCase().includes("initiative management");
      
      // Check if this is a child of Initiative Tracking module
      // Added By Madhuri.K On 04-11-2025 - Use page name based icon for Initiative Tracking child pages
      const isInitiativeTrackingChild = parentName === "Initiative Tracking" || 
                                       parentPath.toLowerCase().includes("initiative tracking");
      
      // Check if this is a child of Program module
      // Added By Madhuri.K On 04-11-2025 - Use page name based icon for Program module child pages
      const isProgramChild = parentName === "Program" || 
                            parentPath.toLowerCase().includes("program");
      
      // Check if this is a child of Project module (for Project Health Sheet Approval)
      // Added By Madhuri.K On 04-11-2025 - Use page name based icon for Project module child pages
      const isProjectChild = parentName === "Project" || 
                            parentName === "Projects" ||
                            parentPath.toLowerCase().includes("project");
      
      // Check if this is a child of Reports module
      // Added By Madhuri.K On 04-11-2025 - Use page name based icon for Reports module child pages
      const isReportsChild = parentName === "Reports" || 
                            parentPath.toLowerCase().includes("reports");
      
      // Check if this is a child of Dashboard module
      // Added By Madhuri.K - Use page name based icon for Dashboard child pages
      const isDashboardChild = parentName === "Dashboard" || 
                               parentPath.toLowerCase().includes("dashboard");
      
      // Check if this is a child of Configuration module or is a Configuration parent node
      // Added By Madhuri.K On 04-11-2025 - Use page name based icon for Configuration module parent nodes and child pages
      // Also check if parent is Projects under Configuration (Configuration > Projects)
      const isConfigurationChild = parentName === "Configuration" || 
                                  parentPath.toLowerCase().includes("configuration") ||
                                  (parentName === "Projects" && parentPath.toLowerCase().includes("configuration")) ||
                                  item.tagName === "Company Information" ||
                                  item.tagName === "Corporate Settings" ||
                                  item.tagName === "UI & Navigation" ||
                                  item.tagName === "Security" ||
                                  item.tagName === "Projects" ||
                                  item.tagName === "Initiative Masters" ||
                                  item.tagName === "Organization" ||
                                  item.tagName === "Business Masters" ||
                                  item.tagName === "Checklist" ||
                                  item.tagName === "General Information" ||
                                  item.tagName === "General" ||
                                  item.tagName === "Risk Master";
      
      // Get icon for this item
      let itemIcon = null;
      const iconTitle = item.tagName || item.tagDescription || "Menu Item";
      
      if (isInitiativeManagementChild && level > 0) {
        // For Initiative Management child pages, use page name based icon
        // Added By Madhuri.K On 04-11-2025 - Pass isActive to change icon color when selected
        const pageIcon = getInitiativeManagementPageIcon(item.tagName || item.tagDescription, isActive);
        itemIcon = pageIcon || (
          <PanoramaFishEyeIcon style={{ fontSize: 16, color: isActive ? "#ffffff" : undefined }} />
        );
      } else if (isInitiativeTrackingChild && level > 0) {
        // For Initiative Tracking child pages, use page name based icon
        // Updated By Madhuri.K - Removed icon color
        const pageIcon = getInitiativeTrackingPageIcon(item.tagName || item.tagDescription, isActive);
        itemIcon = pageIcon || (
          <PanoramaFishEyeIcon style={{ fontSize: 16, color: isActive ? "#ffffff" : undefined }} />
        );
      } else if (isConfigurationChild && level > 0) {
        // For Configuration module child pages (including pages under Projects node), use page name based icon
        // Updated By Madhuri.K - Removed icon color
        // Check if this is under Configuration > Projects to use Configuration icons
        const pageIcon = getConfigurationPageIcon(item.tagName || item.tagDescription, isActive);
        itemIcon = pageIcon || (
          <PanoramaFishEyeIcon style={{ fontSize: 16, color: isActive ? "#ffffff" : undefined }} />
        );
      } else if ((isProgramChild || isProjectChild) && level > 0) {
        // For Program/Project module child pages, use page name based icon
        // Updated By Madhuri.K - Removed icon color
        // Standard icon size to 16px for all module pages (same as other modules)
        const pageIcon = getProgramPageIcon(item.tagName || item.tagDescription, isActive);
        itemIcon = pageIcon || (
          <PanoramaFishEyeIcon style={{ fontSize: 16, color: isActive ? "#ffffff" : undefined }} />
        );
      } else if (isReportsChild && level > 0) {
        // For Reports module child pages, use page name based icon
        // Updated By Madhuri.K - Removed icon color
        const pageIcon = getReportsPageIcon(item.tagName || item.tagDescription, isActive);
        itemIcon = pageIcon || (
          <PanoramaFishEyeIcon style={{ fontSize: 16, color: isActive ? "#ffffff" : undefined }} />
        );
      } else if (isDashboardChild && level > 0) {
        // For Dashboard module child pages, use page name based icon
        // Added By Madhuri.K - Pass isActive to change icon color when selected
        const pageIcon = getDashboardPageIcon(item.tagName || item.tagDescription, isActive);
        itemIcon = pageIcon || (
          <PanoramaFishEyeIcon style={{ fontSize: 16, color: isActive ? "#ffffff" : undefined }} />
        );
      } else if (isConfigurationChild) {
        // Updated By Madhuri.K On 12-11-2025 - Always use Configuration icon for Configuration children
        // This ensures consistent icon rendering for items like "UI & Navigation" whether they're parents or children
        // For Configuration module parent nodes (direct children of Configuration), use page name based icon
        const pageIcon = getConfigurationPageIcon(item.tagName || item.tagDescription, isActive);
        itemIcon = pageIcon || (
          <PanoramaFishEyeIcon style={{ fontSize: 16, color: isActive ? "#ffffff" : undefined }} />
        );
      } else if (iconMappings[item.tagName]) {
        // Use mapped icon for parent items
        // Updated By Madhuri.K On 12-11-2025 - Add active class to image icon when parent is active
        // Updated By Madhuri.K On 12-11-2025 - Ensure icon turns white when parent is active (when child is selected)
        itemIcon = (
          <img 
            src={iconMappings[item.tagName]} 
            alt={item.tagName} 
            className={`sidebar-icon ${isActive ? 'active-icon' : ''}`}
            style={isActive ? { filter: 'brightness(0) invert(1)' } : {}}
          />
        );
      } else {
        // Default icon - Updated By Madhuri.K On 12-11-2025 - Use white color when active
        itemIcon = <PanoramaFishEyeIcon style={{ fontSize: 16, color: isActive ? "#ffffff" : undefined }} />;
      }

      // Wrap icon with Tooltip for parent panel icons
      // Updated By Madhuri.K On 12-11-2025 - Add blue background to icon wrapper when parent is active
      const iconWithTooltip = (
        <Tooltip title={iconTitle} arrow placement="right">
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              backgroundColor: isActive ? theme.itemActive : 'transparent',
              borderRadius: '4px',
              padding: '4px',
              minWidth: '32px',
              minHeight: '32px'
            }}
          >
            {itemIcon}
          </Box>
        </Tooltip>
      );

      // Render parent item with expandable children using MenuItem
      // Updated By Madhuri.K - Use MenuItem instead of SubMenu to handle click properly
      if (item.isParent && hasChildren) {
        const newPath = buildFullPath(parentPath, item.tagName);
        return (
          <MenuItem
            key={itemId}
            icon={iconWithTooltip}
            // Updated By Madhuri.K On 12-11-2025 - Only show parent as active if it's actually selected, not just expanded
            // This prevents parent items from showing active background when child pages are selected
            active={isActive}
            // Updated By Madhuri.K On 12-11-2025 - Show active background and white icon/text for parent items when selected
            // Parent items now show blue background (#3b82f6) and white icon/text when active
            rootStyles={{
              backgroundColor: isActive ? theme.itemActive : 'transparent',
              color: isActive ? '#ffffff' : theme.color,
              '&:hover': {
                backgroundColor: isActive ? theme.itemActive : 'transparent'
              },
              '&:focus': {
                backgroundColor: isActive ? theme.itemActive : 'transparent'
              },
              '&.ps-active': {
                backgroundColor: isActive ? theme.itemActive : 'transparent',
                color: isActive ? '#ffffff' : theme.color,
                '& .ps-menu-button-label': {
                  color: isActive ? '#ffffff' : theme.color
                },
                '& .ps-menu-icon': {
                  color: isActive ? '#ffffff' : 'inherit',
                  '& svg': {
                    color: isActive ? '#ffffff' : 'inherit',
                    fill: isActive ? '#ffffff' : 'inherit'
                  }
                }
              },
              '&.ps-menu-button-root': {
                backgroundColor: isActive ? theme.itemActive : 'transparent',
                color: isActive ? '#ffffff' : theme.color,
                '&:hover': {
                  backgroundColor: isActive ? theme.itemActive : 'transparent'
                },
                '& .ps-menu-button-label': {
                  color: isActive ? '#ffffff' : theme.color
                }
              },
              // Updated By Madhuri.K - Ensure non-active items maintain their default text color
              // This prevents all items from turning white after page selection
              ...(!isActive && {
                '& .ps-menu-button-label': {
                  color: theme.color + ' !important'
                }
              })
            }}
            onClick={(e) => {
              // Updated By Madhuri.K - Expand tree on click only, not on hover
              e.stopPropagation();
              e.preventDefault();
              
              // Updated By Madhuri.K - If item is already expanded and has visible children, keep it expanded and ensure hierarchy is set
              // This ensures back button shows when clicking on parent icon when a page is already selected
              const hasChildren = item.children && Array.isArray(item.children) && item.children.length > 0;
              
              // Updated By Madhuri.K - Check if children panel is visible and this item matches the expanded parent
              // Also check if item is expanded and has children, even if exact match fails
              const isCurrentExpandedParent = expandedParentItem?.tagName === item.tagName || 
                                            expandedParentItem?.tagDescription === item.tagDescription;
              
              // Updated By Madhuri.K - If parent is already expanded, ensure children panel is visible
              // This fixes the issue where clicking on an already expanded parent doesn't show the children panel
              // Handle both cases: when this is the current expanded parent OR when a different parent's children are showing
              if (isExpanded && hasChildren) {
                // Item is already expanded - ensure children panel is visible and hierarchy is set
                console.log('🔄 Parent icon clicked while already expanded - ensuring children panel is visible', {
                  isCurrentExpandedParent,
                  hasVisibleChildren,
                  itemTagName: item.tagName
                });
                
                // Always ensure children panel is visible when clicking on expanded parent
                // This works even if a different parent's children are currently showing
                setExpandedParentItem({ ...item });
                setHasVisibleChildren(true);
                setActiveExpandedParent(itemId);
                setIsCollapsed(false);
                
                // Check if this is a Configuration node and set hierarchy
                const itemName = item.tagName?.toLowerCase() || "";
                const isConfigurationNode = itemName === "security" || 
                                          itemName === "ui & navigation" || 
                                          itemName === "ui navigation" ||
                                          itemName === "company information" ||
                                          itemName === "corporate settings" ||
                                          itemName === "projects" ||
                                          itemName === "initiative masters" ||
                                          itemName === "organization" ||
                                          itemName === "business masters" ||
                                          itemName === "checklist" ||
                                          itemName === "general information" ||
                                          itemName === "general" ||
                                          itemName === "risk master";
                
                // Updated By Madhuri.K - Always set hierarchy for Configuration nodes when clicking parent icon
                // This ensures back button always shows, even if hierarchy was previously set
                if (isConfigurationNode) {
                  // Find Configuration in the navigation tree
                  const findItemInTree = (itemsArray, targetTagName, level = 0) => {
                    if (level > 5 || !itemsArray) return null;
                    const targetLower = targetTagName.toLowerCase();
                    for (const treeItem of itemsArray) {
                      const tagNameLower = treeItem.tagName?.toLowerCase() || "";
                      const tagDescLower = treeItem.tagDescription?.toLowerCase() || "";
                      if (tagNameLower === targetLower || tagDescLower === targetLower) {
                        return treeItem;
                      }
                      if (treeItem.children && Array.isArray(treeItem.children)) {
                        const found = findItemInTree(treeItem.children, targetTagName, level + 1);
                        if (found) return found;
                      }
                    }
                    return null;
                  };
                  
                  let configItem = findItemInTree(allNavigations || items, "Configuration");
                  if (!configItem && allNavigations && allNavigations.length > 0) {
                    configItem = findItemInTree(allNavigations, "Configuration");
                  }
                  if (!configItem && items && items.length > 0) {
                    configItem = findItemInTree(items, "Configuration");
                  }
                  
                  if (configItem) {
                    setParentHierarchy([configItem]);
                    console.log(`✅ Set hierarchy to [Configuration] for ${item.tagName} when clicking parent icon - back button will show`);
                  } else {
                    // Create minimal Configuration object if not found
                    setParentHierarchy([{
                      tagName: "Configuration",
                      tagDescription: "Configuration",
                      tagId: null,
                      children: [],
                      isParent: true
                    }]);
                    console.log(`✅ Created minimal Configuration object for hierarchy - back button will show`);
                  }
                }
                return; // Don't toggle expansion, just ensure panel is visible and hierarchy is set
              }
              
              // Updated By Madhuri.K - Additional check: If clicking on a Configuration node parent icon,
              // always ensure hierarchy is set for back button, especially when a page is already selected
              // This handles the case when a page is selected and user clicks parent icon again
              const itemName = item.tagName?.toLowerCase() || "";
              const isConfigurationNode = itemName === "security" || 
                                        itemName === "ui & navigation" || 
                                        itemName === "ui navigation" ||
                                        itemName === "company information" ||
                                        itemName === "corporate settings" ||
                                        itemName === "projects" ||
                                        itemName === "initiative masters" ||
                                        itemName === "organization" ||
                                        itemName === "business masters" ||
                                        itemName === "checklist" ||
                                        itemName === "general information" ||
                                        itemName === "general" ||
                                        itemName === "risk master";
              
              // Updated By Madhuri.K - If clicking on a Configuration node parent icon and (panel is visible OR item is expanded OR hierarchy is empty),
              // always ensure hierarchy is set for back button
              // This ensures back button shows when clicking parent icon after selecting a page
              if (isConfigurationNode && hasChildren && (hasVisibleChildren || isExpanded || parentHierarchy.length === 0)) {
                // Find Configuration in the navigation tree
                const findItemInTree = (itemsArray, targetTagName, level = 0) => {
                  if (level > 5 || !itemsArray) return null;
                  const targetLower = targetTagName.toLowerCase();
                  for (const treeItem of itemsArray) {
                    const tagNameLower = treeItem.tagName?.toLowerCase() || "";
                    const tagDescLower = treeItem.tagDescription?.toLowerCase() || "";
                    if (tagNameLower === targetLower || tagDescLower === targetLower) {
                      return treeItem;
                    }
                    if (treeItem.children && Array.isArray(treeItem.children)) {
                      const found = findItemInTree(treeItem.children, targetTagName, level + 1);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                
                let configItem = findItemInTree(allNavigations || items, "Configuration");
                if (!configItem && allNavigations && allNavigations.length > 0) {
                  configItem = findItemInTree(allNavigations, "Configuration");
                }
                if (!configItem && items && items.length > 0) {
                  configItem = findItemInTree(items, "Configuration");
                }
                
                if (configItem) {
                  setParentHierarchy([configItem]);
                  // Also ensure panel is visible and parent item is set
                  if (hasVisibleChildren) {
                    // Panel is already visible, just ensure hierarchy is set
                    console.log(`✅ Set hierarchy to [Configuration] for ${item.tagName} (fallback - panel visible) - back button will show`);
                  } else {
                    // Panel might not be visible, ensure it's set up
                    setExpandedParentItem({ ...item });
                    setHasVisibleChildren(true);
                    setActiveExpandedParent(itemId);
                    setIsCollapsed(false);
                    console.log(`✅ Set hierarchy to [Configuration] for ${item.tagName} (fallback - ensuring panel visible) - back button will show`);
                  }
                } else {
                  const minimalConfig = {
                    tagName: "Configuration",
                    tagDescription: "Configuration",
                    tagId: null,
                    children: [],
                    isParent: true
                  };
                  setParentHierarchy([minimalConfig]);
                  if (!hasVisibleChildren) {
                    setExpandedParentItem({ ...item });
                    setHasVisibleChildren(true);
                    setActiveExpandedParent(itemId);
                    setIsCollapsed(false);
                  }
                  console.log(`✅ Created minimal Configuration object for hierarchy (fallback) - back button will show`);
                }
              }
              
              const newExpandedState = !isExpanded;
              console.log('🖱️ MenuItem onClick - item:', item.tagName, 'newExpandedState:', newExpandedState, 'isExpanded:', isExpanded, 'hasChildren:', item.children?.length, 'item:', item);
              
              // Updated By Madhuri.K On 12-11-2025 - Ensure children panel opens on first click
              // Check if item has children before proceeding
              
              if (newExpandedState && hasChildren) {
                // Expanding - show children panel immediately
                console.log('✅ Expanding - hasChildren:', hasChildren, 'children count:', item.children?.length);
                
                // Updated By Madhuri.K On 20-11-2025 - Check if any nested child has a selected page (recursively)
                // If so, automatically expand that nested child and show its children panel
                // This works for multi-level nesting: Configuration > UI & Navigation > General > Pages
                // When user closes and re-opens Configuration module, it automatically expands to show the selected page
                // Returns: { parent: the deepest parent with selected child, hierarchy: array of parents from top to bottom }
                const findSelectedChildParent = (children, currentParentPath = "", currentParentName = "", depth = 0, hierarchy = []) => {
                  if (!children || !Array.isArray(children) || depth > 5) return null; // Limit depth to prevent infinite loops
                  
                  for (const child of children) {
                    // Check if this child is a parent with a selected child page
                    if (child.isParent && child.children && child.children.length > 0) {
                      // Build correct parent path for the nested child
                      const childParentPath = currentParentPath ? `${currentParentPath} > ${child.tagName || child.tagDescription}` : (child.tagName || child.tagDescription);
                      const childParentName = child.tagName || child.tagDescription || currentParentName;
                      const hasSelectedChild = hasActiveChild(child, childParentPath, childParentName);
                      
                      // Build current hierarchy including this child
                      const currentHierarchy = [...hierarchy, child];
                      
                      // Recursively check deeper nested children first (e.g., UI & Navigation > General > Pages)
                      const deeperResult = findSelectedChildParent(child.children, childParentPath, childParentName, depth + 1, currentHierarchy);
                      if (deeperResult) {
                        // Found a deeper nested parent with selected child - return it
                        return deeperResult;
                      }
                      
                      // If no deeper parent found, check if this child itself has a selected child
                      if (hasSelectedChild) {
                        // Found a nested parent with selected child - return it with hierarchy
                        return { parent: child, hierarchy: currentHierarchy };
                      }
                    }
                  }
                  return null;
                };
                
                // Build initial parent path for Configuration
                const initialParentPath = parentPath ? `${parentPath} > ${item.tagName}` : item.tagName;
                const initialParentName = item.tagName || item.tagDescription || parentName;
                
                const selectedResult = findSelectedChildParent(item.children, initialParentPath, initialParentName, 0, []);
                
                if (selectedResult && selectedResult.parent) {
                  // Found a nested parent with selected child - expand it automatically
                  const selectedChildParent = selectedResult.parent;
                  const parentHierarchyList = selectedResult.hierarchy || [];
                  
                  console.log('✅ Auto-expanding nested parent with selected child:', selectedChildParent.tagName, 'hierarchy:', parentHierarchyList.map(p => p.tagName));
                  
                  // Updated By Madhuri.K On 20-11-2025 - Set up the hierarchy: if we found General, hierarchy should be [UI & Navigation]
                  // But we want to show General's children, so we need to set parentHierarchy to [UI & Navigation]
                  // and expandedParentItem to General
                  // Updated By Madhuri.K On 22-11-2025 - For nested nodes like General, hierarchy should be [UI & Navigation], not [Configuration, UI & Navigation]
                  // This ensures header shows "UI & Navigation >> General" not "Configuration >> UI & Navigation >> General"
                  const hierarchyForPanel = parentHierarchyList.slice(0, -1); // All parents except the last one (which is the one we're showing)
                  
                  // Updated By Madhuri.K On 22-11-2025 - For nested nodes like General, hierarchy should be [UI & Navigation]
                  // Remove Configuration from hierarchy to show "UI & Navigation >> General" not "Configuration >> UI & Navigation >> General"
                  let finalHierarchy = hierarchyForPanel;
                  
                  // Filter out Configuration from hierarchy for nested nodes
                  const isNestedNode = selectedChildParent.tagName?.toLowerCase() === "general" || 
                                      selectedChildParent.tagName?.toLowerCase() === "general information";
                  
                  if (isNestedNode) {
                    // For nested nodes like General, find UI & Navigation in hierarchy and keep only that
                    const uiNavItem = finalHierarchy.find(h => {
                      const hName = h?.tagName?.toLowerCase() || "";
                      return hName === "ui & navigation" || hName === "ui navigation";
                    });
                    
                    if (uiNavItem) {
                      // Keep only UI & Navigation in hierarchy (remove Configuration)
                      finalHierarchy = [uiNavItem];
                      console.log(`✅ Set hierarchy to [UI & Navigation] for nested node ${selectedChildParent.tagName} - back button will show`);
                    } else {
                      // If UI & Navigation not found in hierarchy, try to find it in the tree
                      // Added By Madhuri.K On 22-11-2025 - Find UI & Navigation in navigation tree
                      const findItemInTree = (itemsArray, targetTagName, level = 0) => {
                        if (level > 5 || !itemsArray) return null;
                        const targetLower = targetTagName.toLowerCase();
                        
                        for (const treeItem of itemsArray) {
                          const tagNameLower = treeItem.tagName?.toLowerCase() || "";
                          const tagDescLower = treeItem.tagDescription?.toLowerCase() || "";
                          if (tagNameLower === targetLower || tagDescLower === targetLower) {
                            return treeItem;
                          }
                          
                          if (treeItem.children && Array.isArray(treeItem.children)) {
                            const found = findItemInTree(treeItem.children, targetTagName, level + 1);
                            if (found) return found;
                          }
                        }
                        return null;
                      };
                      
                      const uiNavInTree = findItemInTree(allNavigations || items, "UI & Navigation");
                      if (uiNavInTree) {
                        finalHierarchy = [uiNavInTree];
                        console.log(`✅ Found UI & Navigation in tree and set as hierarchy for nested node ${selectedChildParent.tagName} - back button will show`);
                      } else {
                        console.log(`⚠️ UI & Navigation not found in tree for nested node ${selectedChildParent.tagName}`);
                      }
                    }
                  } else if (finalHierarchy.length > 0) {
                    // For other cases, filter out Configuration if present but keep other parents
                    const lastItem = finalHierarchy[finalHierarchy.length - 1];
                    const lastItemName = lastItem?.tagName?.toLowerCase() || "";
                    const isUINavigation = lastItemName === "ui & navigation" || lastItemName === "ui navigation";
                    
                    // If the last item is UI & Navigation, remove Configuration from hierarchy if present
                    if (isUINavigation && finalHierarchy.length > 1) {
                      // Remove Configuration, keep only UI & Navigation
                      finalHierarchy = finalHierarchy.filter(h => {
                        const hName = h?.tagName?.toLowerCase() || "";
                        return hName !== "configuration";
                      });
                      console.log(`✅ Filtered hierarchy to show only UI & Navigation for nested node`);
                    }
                  }
                  
                  // Updated By Madhuri.K On 22-11-2025 - Ensure hierarchy is not empty for nested nodes so back button shows
                  // If hierarchy is still empty for a nested node, try to find UI & Navigation
                  if (finalHierarchy.length === 0 && isNestedNode) {
                    // Added By Madhuri.K On 22-11-2025 - Find UI & Navigation in navigation tree as fallback
                    const findItemInTree = (itemsArray, targetTagName, level = 0) => {
                      if (level > 5 || !itemsArray) return null;
                      const targetLower = targetTagName.toLowerCase();
                      
                      for (const treeItem of itemsArray) {
                        const tagNameLower = treeItem.tagName?.toLowerCase() || "";
                        const tagDescLower = treeItem.tagDescription?.toLowerCase() || "";
                        if (tagNameLower === targetLower || tagDescLower === targetLower) {
                          return treeItem;
                        }
                        
                        if (treeItem.children && Array.isArray(treeItem.children)) {
                          const found = findItemInTree(treeItem.children, targetTagName, level + 1);
                          if (found) return found;
                        }
                      }
                      return null;
                    };
                    
                    const uiNavInTree = findItemInTree(allNavigations || items, "UI & Navigation");
                    if (uiNavInTree) {
                      finalHierarchy = [uiNavInTree];
                      console.log(`✅ Set hierarchy to [UI & Navigation] as fallback for nested node ${selectedChildParent.tagName} - back button will show`);
                    } else {
                      console.log(`⚠️ UI & Navigation not found as fallback for nested node ${selectedChildParent.tagName}`);
                    }
                  }
                  
                  // Updated By Madhuri.K On 20-11-2025 - Ensure the top-level parent (current item) is included in hierarchy if not already present
                  // This ensures the back arrow shows when viewing nested children (fixes back arrow visibility issue)
                  // But don't add Configuration for nested nodes
                  if (!isNestedNode && (finalHierarchy.length === 0 || (finalHierarchy.length > 0 && finalHierarchy[0]?.tagName !== item.tagName))) {
                    // Only add current item if it's not Configuration (for nested nodes)
                    const itemName = item?.tagName?.toLowerCase() || "";
                    if (itemName !== "configuration") {
                      finalHierarchy = [item, ...finalHierarchy];
                    }
                  }
                  
                  // Updated By Madhuri.K On 22-11-2025 - Final check: Ensure hierarchy is set for nested nodes
                  // If still empty, try to find UI & Navigation from parentHierarchyList or search in tree
                  if (finalHierarchy.length === 0 && isNestedNode) {
                    // First, try to find UI & Navigation in the original parentHierarchyList
                    if (parentHierarchyList.length > 0) {
                      const uiNavInList = parentHierarchyList.find(h => {
                        const hName = h?.tagName?.toLowerCase() || "";
                        return hName === "ui & navigation" || hName === "ui navigation";
                      });
                      if (uiNavInList) {
                        finalHierarchy = [uiNavInList];
                        console.log(`✅ Found UI & Navigation in parentHierarchyList and set as hierarchy for nested node ${selectedChildParent.tagName} - back button will show`);
                      }
                    }
                    
                    // If still not found, search in the navigation tree
                    if (finalHierarchy.length === 0) {
                      const findItemInTree = (itemsArray, targetTagName, level = 0) => {
                        if (level > 5 || !itemsArray) return null;
                        const targetLower = targetTagName.toLowerCase();
                        
                        for (const treeItem of itemsArray) {
                          const tagNameLower = treeItem.tagName?.toLowerCase() || "";
                          const tagDescLower = treeItem.tagDescription?.toLowerCase() || "";
                          if (tagNameLower === targetLower || tagDescLower === targetLower) {
                            return treeItem;
                          }
                          
                          if (treeItem.children && Array.isArray(treeItem.children)) {
                            const found = findItemInTree(treeItem.children, targetTagName, level + 1);
                            if (found) return found;
                          }
                        }
                        return null;
                      };
                      
                      const uiNavInTree = findItemInTree(allNavigations || items, "UI & Navigation");
                      if (uiNavInTree) {
                        finalHierarchy = [uiNavInTree];
                        console.log(`✅ Found UI & Navigation in tree (final fallback) and set as hierarchy for nested node ${selectedChildParent.tagName} - back button will show`);
                      } else {
                        console.log(`⚠️ UI & Navigation not found anywhere for nested node ${selectedChildParent.tagName} - back button will NOT show`);
                      }
                    }
                  }
                  
                  // Updated By Madhuri.K On 22-11-2025 - Log final hierarchy before setting state
                  console.log(`🔍 Final hierarchy before setting state for ${selectedChildParent.tagName}:`, finalHierarchy.map(h => h?.tagName));
                  
                  setActiveExpandedParent(itemId);
                  setExpandedParentItem({ ...selectedChildParent }); // Set the deepest parent as expanded (e.g., General)
                  setHasVisibleChildren(true);
                  setIsCollapsed(false);
                  setExpandedItems(prev => ({ ...prev, [itemId]: true }));
                  setParentHierarchy(finalHierarchy); // Set hierarchy for back navigation (e.g., [UI & Navigation])
                  setSelectedModuleName(selectedChildParent.tagName || "Modules");
                  toggleExpand(itemId);
                  console.log('✅ Auto-expanded nested parent - expandedParentItem:', selectedChildParent.tagName, 'hierarchy:', finalHierarchy.map(p => p.tagName), 'length:', finalHierarchy.length);
                } else {
                  // No selected child found - expand normally
                  // Updated By Madhuri.K On 22-11-2025 - Check if this is a Configuration node and set hierarchy for back button
                  let hierarchyForNormalExpand = [];
                  
                  // Check if the item being expanded is a Configuration node (like UI & Navigation, Security, Projects, etc.)
                  const itemName = item.tagName?.toLowerCase() || "";
                  const isConfigurationNode = itemName === "security" || 
                                            itemName === "ui & navigation" || 
                                            itemName === "ui navigation" ||
                                            itemName === "company information" ||
                                            itemName === "corporate settings" ||
                                            itemName === "projects" ||
                                            itemName === "initiative masters" ||
                                            itemName === "organization" ||
                                            itemName === "business masters" ||
                                            itemName === "checklist" ||
                                            itemName === "general information" ||
                                            itemName === "general" ||
                                            itemName === "risk master";
                  
                  if (isConfigurationNode) {
                    // Find Configuration in the navigation tree
                    const findItemInTree = (itemsArray, targetTagName, level = 0) => {
                      if (level > 5 || !itemsArray) return null;
                      const targetLower = targetTagName.toLowerCase();
                      
                      for (const treeItem of itemsArray) {
                        const tagNameLower = treeItem.tagName?.toLowerCase() || "";
                        const tagDescLower = treeItem.tagDescription?.toLowerCase() || "";
                        if (tagNameLower === targetLower || tagDescLower === targetLower) {
                          return treeItem;
                        }
                        
                        if (treeItem.children && Array.isArray(treeItem.children)) {
                          const found = findItemInTree(treeItem.children, targetTagName, level + 1);
                          if (found) return found;
                        }
                      }
                      return null;
                    };
                    
                    // Updated By Madhuri.K - Try multiple sources to find Configuration
                    let configItem = findItemInTree(allNavigations || items, "Configuration");
                    if (!configItem && allNavigations && allNavigations.length > 0) {
                      configItem = findItemInTree(allNavigations, "Configuration");
                    }
                    if (!configItem && items && items.length > 0) {
                      configItem = findItemInTree(items, "Configuration");
                    }
                    
                    if (configItem) {
                      // Verify that this item is actually under Configuration
                      if (configItem.children) {
                        // Updated By Madhuri.K On 22-11-2025 - Use case-insensitive comparison for better matching
                        const itemNameLower = item.tagName?.toLowerCase() || "";
                        const itemDescLower = item.tagDescription?.toLowerCase() || "";
                        
                        const itemInConfig = configItem.children.find(child => {
                          const childNameLower = child.tagName?.toLowerCase() || "";
                          const childDescLower = child.tagDescription?.toLowerCase() || "";
                          return (childNameLower === itemNameLower || childDescLower === itemNameLower) ||
                                 (child.tagName === item.tagName || child.tagDescription === item.tagName) ||
                                 (child.tagId === item.tagId);
                        });
                        
                        if (itemInConfig) {
                          hierarchyForNormalExpand = [configItem];
                          console.log(`✅ Set hierarchy to [Configuration] for ${item.tagName} (normal expand) - back button will show`);
                        } else {
                          // Updated By Madhuri.K On 22-11-2025 - Fallback: Check parentTagId if direct match fails
                          if (item.parentTagId && item.parentTagId === configItem.tagId) {
                            hierarchyForNormalExpand = [configItem];
                            console.log(`✅ Set hierarchy to [Configuration] for ${item.tagName} via parentTagId (normal expand) - back button will show`);
                          } else {
                            // Updated By Madhuri.K On 22-11-2025 - Final fallback: If it's a known Configuration node, set hierarchy anyway
                            // This ensures back button shows even if item structure doesn't match exactly
                            hierarchyForNormalExpand = [configItem];
                            console.log(`✅ Set hierarchy to [Configuration] for ${item.tagName} (fallback - known Configuration node) - back button will show`);
                          }
                        }
                      } else {
                        // Updated By Madhuri.K On 22-11-2025 - If Configuration has no children yet, still set hierarchy for known Configuration nodes
                        hierarchyForNormalExpand = [configItem];
                        console.log(`✅ Set hierarchy to [Configuration] for ${item.tagName} (Configuration found but no children yet) - back button will show`);
                      }
                    } else {
                      // Updated By Madhuri.K - If Configuration not found, create a minimal Configuration object for hierarchy
                      // This ensures back button shows even if Configuration is not in the tree yet
                      console.log(`⚠️ Configuration not found in navigation tree for ${item.tagName}, creating minimal Configuration object for hierarchy`);
                      hierarchyForNormalExpand = [{
                        tagName: "Configuration",
                        tagDescription: "Configuration",
                        tagId: null,
                        children: [],
                        isParent: true
                      }];
                      console.log(`✅ Created minimal Configuration object for hierarchy - back button will show`);
                    }
                  }
                  
                  setActiveExpandedParent(itemId);
                  setExpandedParentItem({ ...item }); // Create a new object to ensure React detects the change
                  setHasVisibleChildren(true);
                  // Updated By Madhuri.K - Keep tree expanded when child panel opens (don't collapse)
                  setIsCollapsed(false);
                  setExpandedItems(prev => ({ ...prev, [itemId]: true }));
                  setParentHierarchy(hierarchyForNormalExpand); // Updated By Madhuri.K On 22-11-2025 - Set hierarchy for Configuration nodes so back button shows
                  setSelectedModuleName(item.tagName || "Modules");
                  toggleExpand(itemId);
                  console.log('✅ State set - expandedParentItem:', item.tagName, 'hasVisibleChildren: true, isCollapsed: false, hierarchy:', hierarchyForNormalExpand.map(h => h?.tagName));
                }
              } else if (!newExpandedState) {
                // Collapsing - hide children panel
                console.log('❌ Collapsing - hiding panel');
                    setActiveExpandedParent(null);
                    setExpandedParentItem(null);
                    setHasVisibleChildren(false);
                    setParentHierarchy([]);
                setExpandedItems(prev => ({ ...prev, [itemId]: false }));
                toggleExpand(itemId);
                } else {
                // No children - hide panel
                console.log('❌ No children - hiding panel');
                  setActiveExpandedParent(null);
                  setExpandedParentItem(null);
                  setHasVisibleChildren(false);
                  setParentHierarchy([]);
                }
            }}
          >
            {item.tagName}
            {/* Chevron icon to indicate expandable */}
            <ChevronRightIcon 
              sx={{ 
                marginLeft: "auto",
                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 0.2s"
              }} 
            />
          </MenuItem>
        );
      }

      // Render leaf node (item without children) using MenuItem
      const newPath = buildFullPath(parentPath, item.tagName);
      return (
        <MenuItem
          key={itemId}
          icon={iconWithTooltip}
          active={isActive}
          // Updated By Madhuri.K On 12-11-2025 - Ensure selected page name text is white (#fff)
          // Updated By Madhuri.K - Add blue background for active items on mobile view
          rootStyles={{
            backgroundColor: isActive ? theme.itemActive : 'transparent',
            color: isActive ? '#ffffff' : theme.color,
            '&:hover': {
              backgroundColor: isActive ? theme.itemActive : 'transparent'
            },
            '&:focus': {
              backgroundColor: isActive ? theme.itemActive : 'transparent'
            },
            '&.ps-active': {
              backgroundColor: isActive ? theme.itemActive : 'transparent',
              color: isActive ? '#ffffff' : theme.color,
              '& .ps-menu-button-label': {
                color: isActive ? '#ffffff' : theme.color
              },
              '& .ps-menu-icon': {
                color: isActive ? '#ffffff' : 'inherit',
                '& svg': {
                  color: isActive ? '#ffffff' : 'inherit',
                  fill: isActive ? '#ffffff' : 'inherit'
                }
              }
            },
            '&.ps-menu-button-root': {
              backgroundColor: isActive ? theme.itemActive : 'transparent',
              color: isActive ? '#ffffff' : theme.color,
              '&:hover': {
                backgroundColor: isActive ? theme.itemActive : 'transparent'
              },
              '& .ps-menu-button-label': {
                color: isActive ? '#ffffff' : theme.color
              }
            },
            ...(isActive && {
              '& .pro-inner-item': {
                color: '#ffffff !important'
              },
              '& .pro-item-content': {
                color: '#ffffff !important'
              }
            }),
            // Updated By Madhuri.K - Ensure non-active items maintain their default text color
            // This prevents all items from turning white after page selection
            ...(!isActive && {
              '& .ps-menu-button-label': {
                color: theme.color + ' !important'
              }
            })
          }}
          onClick={() => {
            // Updated By Madhuri.K On 12-11-2025 - handleClick will collapse navigation tree automatically
            handleClick(item.pageName, item.tagDescription, parentPath, item.tagName);
          }}
        >
          {item.tagName}
          {item.badge && (
            <Chip
              label={item.badge.value}
              size="small"
              sx={{
                height: "20px",
                fontSize: "11px",
                backgroundColor: item.badge.color || "#ef4444",
                color: "#ffffff",
                fontWeight: 600,
                marginLeft: "auto"
              }}
            />
          )}
        </MenuItem>
      );
    });
    
    console.log('🔍 renderMenuItems - items array:', items, 'length:', items.length);
    return items;
  };

  /**
   * Render children items for the right panel
   * Updated By Madhuri.K - Render children as simple clickable items in right panel
   */
  const renderChildrenPanelItems = (children, parentPath, parentName) => {
    if (!children || !Array.isArray(children)) {
      return null;
    }

    return children.map((child, index) => {
      const childPath = buildFullPath(parentPath, child.tagName);
      // Updated By Madhuri.K On 12-11-2025 - Check if child is active OR has any active child
      // Updated By Madhuri.K - Fix: Also check hasActiveChild for parent nodes in children panel
      // This ensures parent nodes like "General" show as active when their children (like "Framework Email Messages") are selected
      const hasChildChildren = child.isParent && child.children && child.children.length > 0;
      // Updated By Madhuri.K On 20-11-2025 - Pass parentPath and parentName to distinguish Configuration vs Program Projects
      const hasActiveChildItem = hasChildChildren && hasActiveChild(child, parentPath, parentName);
      const isActive = isSelected(child, parentPath, parentName) || hasActiveChildItem; // Highlight if child is selected OR if it has any active child
      const childId = `${child.tagName}-${index}`;

      // Get icon for child item - Updated By Madhuri.K - Add color to icons
      let childIcon = null;
      // Updated By Madhuri.K On 12-11-2025 - Use white color for icons when item is active/selected
      if (parentName === "Initiative Management") {
        const pageIcon = getInitiativeManagementPageIcon(child.tagName || child.tagDescription, isActive);
        const iconColor = isActive ? "#ffffff" : "#3b82f6";
        childIcon = pageIcon || <PanoramaFishEyeIcon sx={{ fontSize: 16, color: iconColor }} style={{ color: iconColor, fill: iconColor }} />;
      } else if (parentName === "Initiative Tracking") {
        const pageIcon = getInitiativeTrackingPageIcon(child.tagName || child.tagDescription, isActive);
        const iconColor = isActive ? "#ffffff" : "#3b82f6";
        childIcon = pageIcon || <PanoramaFishEyeIcon sx={{ fontSize: 16, color: iconColor }} style={{ color: iconColor, fill: iconColor }} />;
      } else if (parentName === "Configuration" || parentName === "Initiative Masters" || parentName === "General Information" || parentName === "General" || parentName === "Organization" || parentName === "Business Masters" || parentName === "Checklist" || parentName === "Projects" || parentName === "Security" || parentName === "Risk Master" || parentName === "UI & Navigation" || parentPath.toLowerCase().includes("configuration") || parentPath.toLowerCase().includes("initiative masters") || parentPath.toLowerCase().includes("general information") || parentPath.toLowerCase().includes("general") || parentPath.toLowerCase().includes("organization") || parentPath.toLowerCase().includes("business masters") || parentPath.toLowerCase().includes("checklist") || (parentPath.toLowerCase().includes("configuration") && parentPath.toLowerCase().includes("projects")) || parentPath.toLowerCase().includes("security") || parentPath.toLowerCase().includes("risk master") || parentPath.toLowerCase().includes("ui & navigation") || parentPath.toLowerCase().includes("ui navigation")) {
        // Use Configuration icon function for all Configuration module pages (including Projects under Configuration)
        const pageIcon = getConfigurationPageIcon(child.tagName || child.tagDescription, isActive);
        const iconColor = isActive ? "#ffffff" : "#3b82f6";
        childIcon = pageIcon || <PanoramaFishEyeIcon sx={{ fontSize: 16, color: iconColor }} style={{ color: iconColor, fill: iconColor }} />;
      } else if (parentName === "Program" || (parentName === "Projects" && !parentPath.toLowerCase().includes("configuration"))) {
        // Use Program icon function only for top-level Program/Projects modules (not under Configuration)
        const pageIcon = getProgramPageIcon(child.tagName || child.tagDescription, isActive);
        const iconColor = isActive ? "#ffffff" : "#3b82f6";
        childIcon = pageIcon || <PanoramaFishEyeIcon sx={{ fontSize: 16, color: iconColor }} style={{ color: iconColor, fill: iconColor }} />;
      } else if (parentName === "Reports") {
        const pageIcon = getReportsPageIcon(child.tagName || child.tagDescription, isActive);
        const iconColor = isActive ? "#ffffff" : "#3b82f6";
        childIcon = pageIcon || <PanoramaFishEyeIcon sx={{ fontSize: 16, color: iconColor }} style={{ color: iconColor, fill: iconColor }} />;
      } else if (parentName === "Dashboard") {
        // Added By Madhuri.K - Use Dashboard icon function for Dashboard child pages
        const pageIcon = getDashboardPageIcon(child.tagName || child.tagDescription, isActive);
        const iconColor = isActive ? "#ffffff" : "#3b82f6";
        childIcon = pageIcon || <PanoramaFishEyeIcon sx={{ fontSize: 16, color: iconColor }} style={{ color: iconColor, fill: iconColor }} />;
      } else {
        const iconColor = isActive ? "#ffffff" : "#3b82f6";
        childIcon = <PanoramaFishEyeIcon sx={{ fontSize: 16, color: iconColor }} style={{ color: iconColor, fill: iconColor }} />;
      }

      // Check if this child is a sub-parent (has its own children)
      const isSubParent = hasChildChildren;
      const isSubParentExpanded = expandedParentItem?.tagName === child.tagName && hasVisibleChildren;

      return (
        <Box
          key={childId}
          data-active={isActive ? "true" : "false"}
          onClick={(e) => {
            e.stopPropagation();
            if (isSubParent) {
              // If it's a sub-parent, expand to show its children
              console.log('🖱️ Sub-parent clicked - child:', child.tagName, 'hasChildren:', hasChildChildren);
              setTimeout(() => {
                // Add current parent to hierarchy for back navigation
                setParentHierarchy(prev => [...prev, expandedParentItem]);
                setExpandedParentItem({ ...child }); // Set this child as the expanded parent
                setHasVisibleChildren(true);
                setActiveExpandedParent(`${child.tagName}-sub-${index}`);
                setChildrenPanelSearchTerm(""); // Reset search when expanding sub-parent
                // Updated By Madhuri.K - Keep tree expanded when navigating to sub-parent in child panel
                setIsCollapsed(false);
                console.log('✅ Sub-parent expanded - expandedParentItem:', child.tagName, 'isCollapsed: false');
              }, 0);
            } else {
              // If it's a leaf node, navigate to the page
              // Updated By Madhuri.K On 12-11-2025 - handleClick will hide children panel automatically
              handleClick(child.pageName, child.tagDescription, parentPath, child.tagName);
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "10px 16px",
            margin: "2px 8px",
            borderRadius: "4px",
            cursor: "pointer",
            backgroundColor: isActive ? theme.itemActive : (isSubParentExpanded ? theme.itemHover : "transparent"),
            color: isActive ? "#ffffff" : theme.color,
            "&:hover": {
              backgroundColor: isActive ? theme.itemActive : theme.itemHover
            },
            transition: "background-color 0.2s"
          }}
        >
          {/* Icon */}
          {/* Updated By Madhuri.K - Ensure icons are aligned consistently with fixed size container (20px to match Material-UI fontSize 16) */}
          <Box 
            sx={{ 
              marginRight: "12px", 
              display: "flex", 
              alignItems: "center",
              justifyContent: "center",
              width: "27px",
              height: "27px",
              minWidth: "27px",
              minHeight: "27px",
              flexShrink: 0
            }}
          >
            {childIcon}
          </Box>
          
          {/* Text */}
          {/* Updated By Madhuri.K On 12-11-2025 - Ensure text color is white when active */}
          {/* Updated By Madhuri.K On 21-11-2025 - Reduced font size from 14px to 12px */}
          <Typography
            className={isActive ? "active-menu-item-text" : ""}
            sx={{
              fontSize: "12px", // Updated By Madhuri.K On 21-11-2025 - Reduced from 14px to 12px
              fontWeight: isActive ? 600 : 400,
              flex: 1,
              color: isActive ? "#ffffff !important" : "inherit"
            }}
          >
            {child.tagName}
          </Typography>

          {/* Chevron for sub-parent */}
          {/* Updated By Madhuri.K On 12-11-2025 - Make chevron white when item is active */}
          {isSubParent && (
            <ChevronRightIcon 
              sx={{ 
                marginLeft: "8px",
                fontSize: "16px",
                color: isActive ? "#ffffff" : theme.color,
                transform: isSubParentExpanded ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 0.2s"
              }} 
            />
          )}

          {/* Badge */}
          {child.badge && (
            <Chip
              label={child.badge.value}
              size="small"
              sx={{
                height: "20px",
                fontSize: "11px",
                backgroundColor: child.badge.color || "#ef4444",
                color: "#ffffff",
                fontWeight: 600,
                marginLeft: isSubParent ? "4px" : "auto"
              }}
            />
          )}
        </Box>
      );
    });
  };

  /**
   * Render children panel on the right side
   * Updated By Madhuri.K - Render children in a separate panel on the right side
   */
  const renderChildrenPanel = () => {
    // Debug logging
    console.log('🔍 renderChildrenPanel called - hasVisibleChildren:', hasVisibleChildren, 'expandedParentItem:', expandedParentItem, 'parentHierarchy:', parentHierarchy);
    
    // TEMPORARY: Always show panel for testing - remove this after verification
    // if (!hasVisibleChildren || !expandedParentItem) {
    //   console.log('❌ renderChildrenPanel - Not showing (hasVisibleChildren:', hasVisibleChildren, 'expandedParentItem:', expandedParentItem, ')');
    //   return null;
    // }
    
    // Check if we should render
    if (!hasVisibleChildren) {
      console.log('❌ renderChildrenPanel - hasVisibleChildren is false');
      return null;
    }
    
    if (!expandedParentItem) {
      console.log('❌ renderChildrenPanel - expandedParentItem is null');
      return null;
    }

    if (!expandedParentItem.children || !Array.isArray(expandedParentItem.children) || expandedParentItem.children.length === 0) {
      console.log('❌ renderChildrenPanel - No children array or empty children. Children:', expandedParentItem.children);
      return null;
    }

    // Updated By Madhuri.K On 12-11-2025 - Flatten search results to show only matching pages directly
    // Search through nested children and return a flat list of matching pages (not parent hierarchy)
    // Updated By Madhuri.K On 18-11-2025 - Trim search term to prevent searching with spaces
    const filterChildrenRecursively = (children, searchTerm) => {
      // Updated By Madhuri.K On 18-11-2025 - Trim search term to prevent searching with spaces
      const trimmedSearchTerm = searchTerm ? searchTerm.trim() : "";
      if (!trimmedSearchTerm || !children || children.length === 0) {
        return children;
      }

      const searchTermLower = trimmedSearchTerm.toLowerCase();
      const matchingPages = [];

      // Helper function to check if an item matches the search term
      const itemMatchesSearch = (item) => {
        const nameMatch = item.tagName && item.tagName.toLowerCase().includes(searchTermLower);
        const descMatch = item.tagDescription && item.tagDescription.toLowerCase().includes(searchTermLower);
        return nameMatch || descMatch;
      };

      // Recursively collect all matching pages into a flat list
      const collectMatchingPages = (items) => {
        items.forEach((item) => {
          // If this item matches, add it to the results (without children to flatten)
          if (itemMatchesSearch(item)) {
            matchingPages.push({ ...item, children: [] }); // Remove children to show as flat list
          }
          
          // Recursively search nested children
          if (item.children && item.children.length > 0) {
            collectMatchingPages(item.children);
          }
        });
      };

      collectMatchingPages(children);
      return matchingPages;
    };

    // Updated By Madhuri.K On 18-11-2025 - Trim search term to prevent searching with spaces
    const trimmedChildrenSearchTerm = childrenPanelSearchTerm ? childrenPanelSearchTerm.trim() : "";
    const filteredChildren = trimmedChildrenSearchTerm
      ? filterChildrenRecursively(expandedParentItem.children, trimmedChildrenSearchTerm)
      : expandedParentItem.children;

    // If no children match the search, still show the panel with empty state
    if (filteredChildren.length === 0 && trimmedChildrenSearchTerm) {
      console.log('⚠️ renderChildrenPanel - No children match search term:', childrenPanelSearchTerm);
      // Don't return null - show the panel with empty state message
    }

    // Added By Madhuri.K On 22-11-2025 - Build parentPath including full hierarchy for breadcrumb
    // Updated By Madhuri.K On 22-11-2025 - For Configuration nodes, parentPath should be "Configuration > [NodeName]" for breadcrumb
    // But header should show just the node name (e.g., "Projects") not "Configuration >> Projects"
    let parentPathForChildren = "";
    const parentName = expandedParentItem.tagName || "";
    const parentNameLower = parentName.toLowerCase();
    
    // Check if this is a Configuration node (Projects, Security, etc.)
    const isConfigurationNode = parentNameLower === "security" || 
                              parentNameLower === "ui & navigation" || 
                              parentNameLower === "ui navigation" ||
                              parentNameLower === "company information" ||
                              parentNameLower === "corporate settings" ||
                              parentNameLower === "projects" ||
                              parentNameLower === "initiative masters" ||
                              parentNameLower === "organization" ||
                              parentNameLower === "business masters" ||
                              parentNameLower === "checklist" ||
                              parentNameLower === "general information" ||
                              parentNameLower === "general" ||
                              parentNameLower === "risk master";
    
    if (isConfigurationNode) {
      // Updated By Madhuri.K On 22-11-2025 - For Configuration nodes, parentPath should be "Configuration > [NodeName]" for breadcrumb
      // This ensures child pages show correct breadcrumb "Configuration > Projects > [Child]"
      parentPathForChildren = buildFullPath("Configuration", parentName);
    } else if (parentHierarchy.length > 0) {
      // Build path from hierarchy for non-Configuration nodes
      parentPathForChildren = parentHierarchy.map(h => h.tagName).join(" > ");
      if (expandedParentItem.tagName) {
        parentPathForChildren = buildFullPath(parentPathForChildren, expandedParentItem.tagName);
      }
    } else {
      // Not a Configuration node and no hierarchy, just use the parent name
      parentPathForChildren = parentName;
    }
    
    const newPath = parentPathForChildren;
    const childrenItems = renderChildrenPanelItems(
      filteredChildren, 
      newPath, 
      expandedParentItem.tagName
    );

    console.log('✅ renderChildrenPanel - Rendering panel with', expandedParentItem.children.length, 'children');

    // Calculate panel position - should be right next to the sidebar (flush, no margin)
    // Updated By Madhuri.K - Reduced sidebar width (70px collapsed, 260px expanded)
    const sidebarWidth = isCollapsed ? 70 : 220;
    const panelLeft = `${sidebarWidth}px`; // Panel starts right after sidebar
    console.log('📍 Panel position - left:', panelLeft, 'isCollapsed:', isCollapsed, 'sidebarWidth:', sidebarWidth);

    return (
      <Box
        ref={childrenPanelRef}
        className="children-panel"
        id="children-panel-container"
        sx={{
          position: "fixed !important",
          left: panelLeft, // Panel starts right after sidebar (flush, no gap)
          top: "0px !important", // Always at top of screen - use px instead of 0
          right: "auto !important",
          bottom: "auto !important",
          width: "260px !important", // Updated By Madhuri.K - Reduced child panel width to match sidebar
          height: "100vh !important",
          maxHeight: "100vh !important",
          backgroundColor: "#ffffff !important",
          borderLeft: `1px solid ${theme.border} !important`,
          borderRight: `1px solid ${theme.border} !important`,
          zIndex: "10000 !important", // Very high z-index to appear above everything
          overflowY: "auto !important", // Only children panel scrolls
          overflowX: "hidden !important",
          boxShadow: "4px 0 12px rgba(0, 0, 0, 0.3) !important",
          display: "flex !important",
          flexDirection: "column !important",
          visibility: "visible !important",
          opacity: "1 !important",
          marginLeft: "0px !important", // No margin - panel flush against sidebar
          marginTop: "0px !important", // Ensure no margin - starts at top
          marginRight: "0px !important",
          marginBottom: "0px !important",
          padding: "0px !important",
          paddingTop: "0px !important",
          paddingLeft: "0px !important", // Padding will be on inner content
          paddingRight: "0px !important",
          pointerEvents: "auto !important", // Ensure it's interactive
          scrollBehavior: "auto !important", // Ensure scroll starts at top
          transform: "none !important", // Ensure no transforms
          alignSelf: "flex-start !important", // Align to top
          alignItems: "flex-start !important" // Align content to top
        }}
      >
        {/* Children Panel Header - Page Name at Top with Icon and Back Button */}
        <Box
          sx={{
            padding: "12px 16px",
            paddingLeft: "16px", // Ensure left padding to show page name
            backgroundColor: "#ffffff",
            borderBottom: `1px solid ${theme.border}`,
            position: "sticky",
            top: 0,
            zIndex: 10,
            flexShrink: 0, // Don't shrink
            display: "flex",
            alignItems: "center",
            gap: "4px", // Reduced from 8px to reduce spacing between icons
            minHeight: "48px", // Ensure minimum height
            width: "100%",
            boxSizing: "border-box"
          }}
        >
          {/* Back Button - Show only when there's a parent in hierarchy to go back to */}
          {parentHierarchy.length > 0 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                // Since button only shows when parentHierarchy.length > 0, we can safely access the last parent
                const previousParent = parentHierarchy[parentHierarchy.length - 1];
                setParentHierarchy(prev => prev.slice(0, -1)); // Remove last parent from hierarchy
                setExpandedParentItem(previousParent);
                setChildrenPanelSearchTerm(""); // Reset search when going back
                // Updated By Madhuri.K - Keep tree expanded when navigating back to parent
                setIsCollapsed(false);
                console.log('🔙 Back clicked - returning to:', previousParent?.tagName);
              }}
              size="small"
              sx={{
                padding: "2px",
                minWidth: "auto",
                color: theme.color,
                "&:hover": {
                  backgroundColor: theme.itemHover
                }
              }}
            >
              <ArrowBackIcon sx={{ fontSize: "18px" }} />
            </IconButton>
          )}
          
          {/* Updated By Madhuri.K On 22-11-2025 - Show breadcrumb format when viewing nested children (e.g., "UI & Navigation >> General Node") */}
          {/* Updated By Madhuri.K On 22-11-2025 - Don't show breadcrumb for Configuration nodes - show just the node name (e.g., "Projects" not "Configuration >> Projects") */}
          {/* Also check if expandedParentItem is a Configuration node to prevent showing breadcrumb format */}
          {(() => {
            const parentTagName = expandedParentItem?.tagName || "";
            const parentTagNameLower = parentTagName.toLowerCase();
            const isConfigurationNode = parentTagNameLower === "security" || 
                                      parentTagNameLower === "ui & navigation" || 
                                      parentTagNameLower === "ui navigation" ||
                                      parentTagNameLower === "company information" ||
                                      parentTagNameLower === "corporate settings" ||
                                      parentTagNameLower === "projects" ||
                                      parentTagNameLower === "initiative masters" ||
                                      parentTagNameLower === "organization" ||
                                      parentTagNameLower === "business masters" ||
                                      parentTagNameLower === "checklist" ||
                                      parentTagNameLower === "general information" ||
                                      parentTagNameLower === "general" ||
                                      parentTagNameLower === "risk master";
            
            // Updated By Madhuri.K On 22-11-2025 - Show breadcrumb format for nested nodes like "General" under "UI & Navigation"
            // Don't show breadcrumb format only if:
            // 1. Hierarchy has Configuration as last item AND expandedParentItem is a top-level Configuration node (like Projects, Security)
            // 2. But DO show it for nested nodes like "General" (even though it's a Configuration node, it's nested under UI & Navigation)
            const hierarchyLastItem = parentHierarchy.length > 0 ? parentHierarchy[parentHierarchy.length - 1]?.tagName?.toLowerCase() : "";
            const isTopLevelConfigurationNode = hierarchyLastItem === "configuration" && 
                                               (parentTagNameLower === "security" || 
                                                parentTagNameLower === "ui & navigation" || 
                                                parentTagNameLower === "ui navigation" ||
                                                parentTagNameLower === "company information" ||
                                                parentTagNameLower === "corporate settings" ||
                                                parentTagNameLower === "projects" ||
                                                parentTagNameLower === "initiative masters" ||
                                                parentTagNameLower === "organization" ||
                                                parentTagNameLower === "business masters" ||
                                                parentTagNameLower === "checklist" ||
                                                parentTagNameLower === "risk master");
            
            // Show breadcrumb format if there's a hierarchy AND it's not a top-level Configuration node directly under Configuration
            // This allows "UI & Navigation >> General" but prevents "Configuration >> Projects"
            const shouldShowBreadcrumb = parentHierarchy.length > 0 && !isTopLevelConfigurationNode;
            
            return shouldShowBreadcrumb;
          })() ? (
            // Show breadcrumb format: Parent >> Current
            <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: "8px" }}>
              {/* Parent name with icon - clickable link */}
              {(() => {
                const parentItem = parentHierarchy[parentHierarchy.length - 1];
                const parentTagName = parentItem?.tagName || "";
                const parentTagNameLower = parentTagName.toLowerCase();
                
                // Get icon for parent
                let parentIcon = null;
                const isConfigurationNode = parentTagNameLower === "security" || 
                                          parentTagNameLower === "ui & navigation" || 
                                          parentTagNameLower === "ui navigation" ||
                                          parentTagNameLower === "company information" ||
                                          parentTagNameLower === "corporate settings" ||
                                          parentTagNameLower === "projects" ||
                                          parentTagNameLower === "initiative masters" ||
                                          parentTagNameLower === "organization" ||
                                          parentTagNameLower === "business masters" ||
                                          parentTagNameLower === "checklist" ||
                                          parentTagNameLower === "general information" ||
                                          parentTagNameLower === "general" ||
                                          parentTagNameLower === "risk master";
                
                if (isConfigurationNode) {
                  const configIcon = getConfigurationPageIcon(parentTagName, false);
                  if (configIcon) {
                    parentIcon = <Box sx={{ display: "flex", alignItems: "center", color: "#3b82f6" }}>{configIcon}</Box>;
                  }
                } else if (iconMappings[parentTagName]) {
                  parentIcon = (
                    <img 
                      src={iconMappings[parentTagName]} 
                      alt={parentTagName} 
                      className="sidebar-icon"
                      style={{ width: "20px", height: "20px" }}
                    />
                  );
                } else {
                  parentIcon = <PanoramaFishEyeIcon sx={{ fontSize: 20, color: "#3b82f6" }} />;
                }
                
                return (
                  <Box 
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "4px",
                      cursor: "pointer",
                      "&:hover": {
                        "& .parent-link-text": {
                          textDecoration: "underline"
                        }
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Navigate back to parent
                      // Updated By Madhuri.K - When clicking parent icon, ensure hierarchy is properly set up for back button
                      const newHierarchy = parentHierarchy.slice(0, -1);
                      
                      // Check if the parentItem itself has a parent (like Configuration) and add it to hierarchy
                      const findItemInTree = (itemsArray, targetTagName, level = 0) => {
                        if (level > 5 || !itemsArray) return null;
                        const targetLower = targetTagName.toLowerCase();
                        for (const treeItem of itemsArray) {
                          const tagNameLower = treeItem.tagName?.toLowerCase() || "";
                          const tagDescLower = treeItem.tagDescription?.toLowerCase() || "";
                          if (tagNameLower === targetLower || tagDescLower === targetLower) {
                            return treeItem;
                          }
                          if (treeItem.children && Array.isArray(treeItem.children)) {
                            const found = findItemInTree(treeItem.children, targetTagName, level + 1);
                            if (found) return found;
                          }
                        }
                        return null;
                      };
                      
                      // Check if parentItem is a Configuration node and needs Configuration in hierarchy
                      const parentNameLower = parentTagName.toLowerCase();
                      const isConfigurationNode = parentNameLower === "security" || 
                                                parentNameLower === "ui & navigation" || 
                                                parentNameLower === "ui navigation" ||
                                                parentNameLower === "company information" ||
                                                parentNameLower === "corporate settings" ||
                                                parentNameLower === "projects" ||
                                                parentNameLower === "initiative masters" ||
                                                parentNameLower === "organization" ||
                                                parentNameLower === "business masters" ||
                                                parentNameLower === "checklist" ||
                                                parentNameLower === "general information" ||
                                                parentNameLower === "general" ||
                                                parentNameLower === "risk master";
                      
                      // If parentItem is a Configuration node and hierarchy doesn't already have Configuration, add it
                      if (isConfigurationNode && newHierarchy.length === 0) {
                        const sourceItems = (allNavigations && allNavigations.length > 0) ? allNavigations : items;
                        const configItem = findItemInTree(sourceItems, "Configuration");
                        if (configItem) {
                          newHierarchy.push(configItem);
                          console.log(`✅ Added Configuration to hierarchy for ${parentTagName} - back button will show`);
                        }
                      }
                      
                      setExpandedParentItem(parentItem);
                      setParentHierarchy(newHierarchy);
                      setChildrenPanelSearchTerm("");
                      setIsCollapsed(false);
                      console.log('🔗 Parent link clicked - navigating to:', parentTagName, 'hierarchy:', newHierarchy.map(h => h?.tagName));
                    }}
                  >
                    {parentIcon}
                    <Typography
                      className="parent-link-text"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#3b82f6",
                        textDecoration: "underline",
                        cursor: "pointer",
                        "&:hover": {
                          color: "#2563eb",
                          textDecoration: "underline"
                        }
                      }}
                    >
                      {parentTagName}
                    </Typography>
                  </Box>
                );
              })()}
              
              {/* Separator */}
              <Typography sx={{ fontSize: "14px", color: "#6b7280" }}>{" >> "}</Typography>
              
              {/* Current item with icon */}
              {(() => {
                const currentTagName = expandedParentItem.tagName || "";
                const currentTagNameLower = currentTagName.toLowerCase();
                
                // Get icon for current item
                let currentIcon = null;
                const isConfigurationNode = currentTagNameLower === "security" || 
                                          currentTagNameLower === "ui & navigation" || 
                                          currentTagNameLower === "ui navigation" ||
                                          currentTagNameLower === "company information" ||
                                          currentTagNameLower === "corporate settings" ||
                                          currentTagNameLower === "projects" ||
                                          currentTagNameLower === "initiative masters" ||
                                          currentTagNameLower === "organization" ||
                                          currentTagNameLower === "business masters" ||
                                          currentTagNameLower === "checklist" ||
                                          currentTagNameLower === "general information" ||
                                          currentTagNameLower === "general" ||
                                          currentTagNameLower === "risk master";
                
                if (isConfigurationNode) {
                  const configIcon = getConfigurationPageIcon(currentTagName, false);
                  if (configIcon) {
                    currentIcon = <Box sx={{ display: "flex", alignItems: "center", color: "#3b82f6" }}>{configIcon}</Box>;
                  }
                } else if (iconMappings[currentTagName]) {
                  currentIcon = (
                    <img 
                      src={iconMappings[currentTagName]} 
                      alt={currentTagName} 
                      className="sidebar-icon"
                      style={{ width: "20px", height: "20px" }}
                    />
                  );
                } else {
                  currentIcon = <PanoramaFishEyeIcon sx={{ fontSize: 20, color: "#3b82f6" }} />;
                }
                
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    {currentIcon}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#3b82f6"
                      }}
                    >
                      {currentTagName}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          ) : (
            // Show single item with icon (no breadcrumb)
            <>
              {/* Icon with color */}
              {/* Updated By Madhuri.K - Use getConfigurationPageIcon for Configuration nodes (Security, UI & Navigation, etc.) to show consistent icons */}
              {(() => {
                const parentTagName = expandedParentItem.tagName || "";
                const parentTagNameLower = parentTagName.toLowerCase();
                
                // Check if this is a Configuration node that should use getConfigurationPageIcon
                const isConfigurationNode = parentTagNameLower === "security" || 
                                          parentTagNameLower === "ui & navigation" || 
                                          parentTagNameLower === "ui navigation" ||
                                          parentTagNameLower === "company information" ||
                                          parentTagNameLower === "corporate settings" ||
                                          parentTagNameLower === "projects" ||
                                          parentTagNameLower === "initiative masters" ||
                                          parentTagNameLower === "organization" ||
                                          parentTagNameLower === "business masters" ||
                                          parentTagNameLower === "checklist" ||
                                          parentTagNameLower === "general information" ||
                                          parentTagNameLower === "general" ||
                                          parentTagNameLower === "risk master";
                
                if (isConfigurationNode) {
                  // Use getConfigurationPageIcon for Configuration nodes to get the same icon as in main navigation
                  const configIcon = getConfigurationPageIcon(parentTagName, false);
                  if (configIcon) {
                    return (
                      <Box sx={{ display: "flex", alignItems: "center", color: "#3b82f6" }}>
                        {configIcon}
                      </Box>
                    );
                  }
                }
                
                // Fall back to iconMappings for other nodes
                if (iconMappings[parentTagName]) {
                  return (
                    <img 
                      src={iconMappings[parentTagName]} 
                      alt={parentTagName} 
                      className="sidebar-icon"
                      style={{ width: "20px", height: "20px" }}
                    />
                  );
                }
                
                // Default icon
                return <PanoramaFishEyeIcon sx={{ fontSize: 20, color: "#3b82f6" }} />;
              })()}
              <Typography
                sx={{
                  fontSize: "14px", // Reduced from 16px
                  fontWeight: 400, // Normal weight instead of bold
                  color: "#3b82f6", // Blue color to match icon
                  display: "block",
                  flex: 1,
                  textAlign: "left"
                }}
              >
                {expandedParentItem.tagName || "Children"}
              </Typography>
            </>
          )}
        </Box>

        {/* Search Bar - After Module Name (Same UI as Parent Panel) */}
        {/* Updated By Madhuri.K On 12-11-2025 - Add animation and move refresh icon outside search input */}
        {/* Updated By Madhuri.K On 12-11-2025 - Ensure search and refresh icon are in one line/row */}
        <Box
          sx={{
            padding: "8px 8px 8px 16px", // Updated By Madhuri.K On 12-11-2025 - Reduce right padding (8px instead of 16px)
            paddingTop: "8px",
            backgroundColor: "#ffffff",
            borderBottom: `1px solid ${theme.border}`,
            position: "sticky",
            top: "48px", // Below the header
            zIndex: 9,
            flexShrink: 0,
            display: "flex",
            flexDirection: "row !important", // Force horizontal layout
            flexWrap: "nowrap !important", // Force no wrapping
            alignItems: "center", // Vertically center align
            justifyContent: "flex-start", // Align items to start
            gap: "4px", // Updated By Madhuri.K On 12-11-2025 - Reduced gap between search input and refresh button (4px instead of 8px)
            width: "100%",
            maxWidth: "100%", // Prevent overflow
            boxSizing: "border-box",
            overflow: "visible", // Allow content to be visible
            // Updated By Madhuri.K On 12-11-2025 - Add animation to search section
            animation: "fadeIn 0.3s ease-in",
            "@keyframes fadeIn": {
              "0%": {
                opacity: 0,
                transform: "translateY(-10px)"
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)"
              }
            }
          }}
        >
          <Paper
            component="form"
            // Updated By Madhuri.K On 18-11-2025 - Prevent form submission and page refresh on Enter key press
            onSubmit={(e) => {
              e.preventDefault(); // Prevent form submission and page refresh
            }}
            sx={{
              display: "flex",
              flexDirection: "row", // Ensure horizontal layout
              alignItems: "center", // Vertically center align
              flex: "1 1 0%", // Take remaining space, start from 0%
              minWidth: 0, // Allow flex to shrink below content size
              maxWidth: "calc(100% - 36px)", // Updated By Madhuri.K On 12-11-2025 - Account for refresh button width (32px) + reduced gap (4px)
              padding: "8px 12px",
              backgroundColor: "#ffffff", // Same as parent panel
              border: `1px solid ${theme.border}`,
              borderRadius: "8px", // Same as parent panel
              boxShadow: "none",
              transition: "all 0.3s ease",
              overflow: "hidden", // Prevent content overflow
              "&:focus-within": {
                borderColor: "#3b82f6",
                boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.1)"
              }
            }}
          >
            {/* Search icon */}
            <SearchIcon sx={{ color: theme.textSecondary, fontSize: "20px", mr: 1, flexShrink: 0 }} />
            {/* Search input */}
            <InputBase
              placeholder="Search..."
              value={childrenPanelSearchTerm}
              onChange={(e) => setChildrenPanelSearchTerm(e.target.value)}
              // Updated By Madhuri.K On 18-11-2025 - Prevent form submission and page refresh on Enter key press
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault(); // Prevent form submission and page refresh
                }
              }}
              sx={{
                flex: "1 1 0%",
                minWidth: 0, // Allow input to shrink
                maxWidth: "100%",
                fontSize: "14px",
                color: theme.color,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&::placeholder": {
                  color: theme.textSecondary
                }
              }}
            />
          </Paper>
          {/* Refresh button - Outside search input, aligned in same line/row */}
          {/* Updated By Madhuri.K On 12-11-2025 - Move refresh icon outside and ensure it's in one line with search */}
          {/* Updated By Madhuri.K On 12-11-2025 - Change tooltip to "Refresh Tree" */}
            {onRefresh && (
            <Tooltip title="Refresh Tree" arrow>
                <IconButton
                  onClick={onRefresh}
                  disabled={loading || isRefreshing}
                  size="small"
                  sx={{
                  color: loading || isRefreshing ? "#94a3b8" : "#3b82f6",
                  backgroundColor: loading || isRefreshing ? "transparent" : "rgba(59, 130, 246, 0.1)",
                  padding: "6px", // Reduced padding for smaller icon
                  width: "32px", // Reduced width
                  height: "32px", // Reduced height
                  minWidth: "32px", // Reduced minimum width
                  marginRight: 0, // Updated By Madhuri.K On 12-11-2025 - Remove right margin
                  flexShrink: 0, // Prevent button from shrinking
                  flexGrow: 0, // Prevent button from growing
                  transition: "all 0.3s ease",
                  // Updated By Madhuri.K On 12-11-2025 - Remove black color on click/focus
                  "&:focus": {
                    backgroundColor: loading || isRefreshing ? "transparent" : "rgba(59, 130, 246, 0.1)",
                    outline: "none"
                  },
                  "&:focus-visible": {
                    backgroundColor: loading || isRefreshing ? "transparent" : "rgba(59, 130, 246, 0.1)",
                    outline: "none"
                  },
                  "&.Mui-focusVisible": {
                    backgroundColor: loading || isRefreshing ? "transparent" : "rgba(59, 130, 246, 0.1)",
                    outline: "none"
                  },
                  "& .MuiTouchRipple-root": {
                    color: "rgba(59, 130, 246, 0.3)" // Light blue ripple instead of black
                  },
                    "&:hover": {
                    backgroundColor: isRefreshing ? "transparent" : "rgba(59, 130, 246, 0.2)",
                    transform: isRefreshing ? "none" : "scale(1.1)"
                  },
                  "&:active": {
                    backgroundColor: loading || isRefreshing ? "transparent" : "rgba(59, 130, 246, 0.2)", // Keep blue on click
                    transform: "scale(0.95)"
                    }
                  }}
                >
                <RefreshIcon sx={{ fontSize: "16px" }} /> {/* Reduced icon size */}
                </IconButton>
              </Tooltip>
            )}
        </Box>

        {/* Children Items - Scrollable content */}
        <Box
          sx={{
            padding: "8px 0",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0 // Allow flex to work properly
          }}
        >
          {filteredChildren.length === 0 && trimmedChildrenSearchTerm ? (
            <Box
              sx={{
                padding: "20px 16px",
                textAlign: "center",
                color: theme.color,
                fontSize: "14px"
              }}
            >
              No items found matching "{trimmedChildrenSearchTerm}"
            </Box>
          ) : (
            childrenItems
          )}
        </Box>
      </Box>
    );
  };

  /**
   * Main render function
   * Returns the ProSidebar component with modern UI elements
   */
  return (
    <>
      <ProSidebar
        collapsed={isCollapsed} // Updated By Madhuri.K On 06-11-2025 - Use isCollapsed state to control collapsed/expanded
        width={isCollapsed ? "70px" : "260px"} // Updated By Madhuri.K - Reduced sidebar width (70px collapsed, 260px expanded)
        style={{
          backgroundColor: "#ffffff", // Added By Madhuri.K On 04-11-2025 - White background (#fff) for entire sidebar
          color: theme.color,
          borderRight: `1px solid ${theme.border}`, // Right border separator
          overflow: "visible", // Updated By Madhuri.K On 06-11-2025 - Allow overflow to show children panel
          position: "relative",
          zIndex: 999
        }}
      >
        {/* ==================== MENU ITEMS SECTION ==================== */}
        {/* Updated By Madhuri.K On 06-11-2025 - Use react-pro-sidebar Menu component with SubMenu and MenuItem */}
        {/* Updated By Madhuri.K On 12-11-2025 - Add custom styles to ensure active MenuItems have white text */}
        <Menu 
          iconShape="square"
          rootStyles={{
            // Updated By Madhuri.K On 12-11-2025 - Ensure active MenuItem text and all child elements are white
            // Updated By Madhuri.K - Add blue background for active items on mobile view
            // Updated By Madhuri.K - Only apply white color to active items, not all items
            // Updated By Madhuri.K - Only apply white color to ACTIVE items, not all items
            // Use more specific selectors to prevent affecting non-active items
            '& .ps-menu-button-root.ps-active': {
              backgroundColor: theme.itemActive + ' !important', // Blue background for active items
              color: '#ffffff !important',
              '& .ps-menu-button-label': {
                color: '#ffffff !important'
              },
              '& .ps-menu-icon': {
                color: '#ffffff !important',
                '& svg': {
                  color: '#ffffff !important',
                  fill: '#ffffff !important'
                }
              },
              '& .pro-inner-item': {
                color: '#ffffff !important'
              },
              '& .pro-item-content': {
                color: '#ffffff !important'
              }
            },
            '& .pro-menu-item.ps-active': {
              backgroundColor: theme.itemActive + ' !important', // Blue background for active items
              color: '#ffffff !important',
              '& .ps-menu-button-label': {
                color: '#ffffff !important'
              },
              '& .ps-menu-icon': {
                color: '#ffffff !important',
                '& svg': {
                  color: '#ffffff !important',
                  fill: '#ffffff !important'
                }
              }
            },
            // Updated By Madhuri.K - Ensure non-active items maintain their default colors
            // This prevents all items from turning white after page selection
            '& .ps-menu-button-root:not(.ps-active)': {
              color: theme.color + ' !important',
              backgroundColor: 'transparent !important',
              '& .ps-menu-button-label': {
                color: theme.color + ' !important'
              },
              '& .ps-menu-icon': {
                color: 'inherit !important',
                '& svg': {
                  color: 'inherit !important',
                  fill: 'inherit !important'
                }
              }
            },
            '& .pro-menu-item:not(.ps-active)': {
              color: theme.color + ' !important',
              backgroundColor: 'transparent !important'
            }
          }}
        >
          {/* Render all navigation items using react-pro-sidebar SubMenu and MenuItem */}
          {renderMenuItems(searchQuery ? filteredItems : items, "", 0, "")}
        </Menu>

      </ProSidebar>

      {/* ==================== CHILDREN PANEL (RIGHT SIDE) ==================== */}
      {/* Updated By Madhuri.K - Render children in a separate panel on the right side using Portal */}
      {typeof document !== 'undefined' && (() => {
        const panel = renderChildrenPanel();
        console.log('🔍 Main render - panel:', panel, 'hasVisibleChildren:', hasVisibleChildren, 'expandedParentItem:', expandedParentItem);
        if (panel) {
          const portal = createPortal(panel, document.body);
          console.log('✅ Creating portal for panel');
          return portal;
        }
        console.log('❌ No panel to render');
        return null;
      })()}
    </>
  );
};

export default WhizVerticalNav;
